import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  CalendarDays, Video, Headphones, Activity, MailCheck, FileText, Users,
  LayoutDashboard, BookOpen, Folder, ChevronRight, ArrowLeft, ArrowRight,
  Lock, ShieldCheck, Search, Plus, Trash2, Edit3, Phone, Mail, MapPin,
  Clock, Stethoscope, Sparkles, ExternalLink, CheckCircle2, X, Share2,
  Settings, Bell, ChevronDown, User, LogOut, Send, Paperclip, Eye,
  Star, Mic, Camera, Wifi, AlertTriangle, Info, RefreshCw, UploadCloud,
  MessageSquare, Compass, Smartphone,
  Pill, ClipboardList, Sliders, ArrowUpRight, Syringe, ShieldAlert, Gauge, Receipt,
} from "lucide-react";
import SandboxTelesoin from "@/components/studio/SandboxTelesoin";
import SandboxFacturation from "@/components/studio/SandboxFacturation";
import { SandboxWelcome, SandboxTourCard, TOUR_STEPS } from "@/components/studio/SandboxTour";
import { setWizardStep } from "@/lib/timeTracker";
import StudioInlineTopBar from "../components/studio/StudioInlineTopBar";
import StudioInlineBanner from "../components/studio/StudioInlineBanner";
import StudioInlineDrawer from "../components/studio/StudioInlineDrawer";
import useStudioDraft from "../hooks/useStudioDraft";
import {
  SECTION_BANNERS, findQuestion, countAnsweredQuestions, deriveSandboxEffects,
} from "../data/studioConfigSchema";

// Refactored per-tab modules (see /app/frontend/src/pages/studio-preview/).
import { SEED_PATIENTS, DRUG_CATALOG } from "./studio-preview/data";
import { Card, PageHeader } from "./studio-preview/shared";
import { PatientAntecedentsTab } from "./studio-preview/PatientAntecedentsTab";
import { PmsSendModal } from "./studio-preview/PmsSendModal";
import { PageROSP } from "./studio-preview/PageROSP";
import { PageParametrage } from "./studio-preview/PageParametrage";
import { PmsConnectionPanel } from "./studio-preview/PmsConnectionPanel";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Fake PS dataset used for sub-pages
const SEED_USERS = [
  { id: "u1", first: "Camille", last: "Bernard", role: "Médecin généraliste", rpps: "10123456789", email: "c.bernard@cabinet.fr", phone: "06 12 34 56 78" },  { id: "u2", first: "Julien",  last: "Martin",  role: "Infirmier libéral",  rpps: "20198765432", email: "j.martin@cabinet.fr",  phone: "06 23 45 67 89" },
  { id: "u3", first: "Sophie",  last: "Dubois",  role: "Sage-femme",          rpps: "30155544433", email: "s.dubois@cabinet.fr",  phone: "06 34 56 78 90" },
];


// Helpers extracted at module level — avoids React re-mounting on each render
// (critical for the search input to keep focus while typing).
function DossierStatCard({ label, value, alert }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-3 flex flex-col" data-testid={`dossiers-stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="text-[10.5px] uppercase tracking-wider text-slate-500 font-semibold">{label}</div>
      <div className={`text-[22px] font-bold leading-tight mt-0.5 ${alert ? "text-amber-600" : "text-slate-900"}`}>{value}</div>
    </div>
  );
}

function DossierFilterPill({ id, label, count, active, onClick }) {
  return (
    <button
      onClick={onClick}
      data-testid={`dossiers-filter-${id}`}
      className={`px-3 py-1.5 rounded-full text-[12px] font-medium border transition-colors ${
        active
          ? "bg-[#0F2B5B] text-white border-[#0F2B5B]"
          : "bg-white text-slate-600 border-slate-300 hover:border-slate-400"
      }`}
    >
      {label}{typeof count === "number" && <span className={`ml-1.5 text-[10.5px] ${active ? "opacity-80" : "text-slate-400"}`}>· {count}</span>}
    </button>
  );
}

function DossierSortableTh({ k, children, className, sortKey, sortAsc, onClick }) {
  return (
    <th
      onClick={() => onClick(k)}
      className={`text-left px-4 py-3 cursor-pointer select-none hover:text-slate-700 ${className || ""}`}
      data-testid={`dossiers-sort-${k}`}
    >
      <span className="inline-flex items-center gap-1">
        {children}
        {sortKey === k && <span className="text-[9px]">{sortAsc ? "▲" : "▼"}</span>}
      </span>
    </th>
  );
}

/* ============================ Patient Detail Drawer ============================ */
const AI_DEMO_TRANSCRIPT = "Madame Lefèvre, 64 ans, vue ce jour pour suivi de son diabète de type 2. Elle rapporte des glycémies à jeun élevées autour de 1.4 à 1.6 g/L depuis une semaine. Pas de polyurie ni polydipsie. Asthénie modérée le matin. Examen clinique sans particularité. Tension à 138 sur 82. Pas d'œdème des membres inférieurs. Examen des pieds rassurant. HbA1c du laboratoire à 7.8%. J'ajuste la Metformine à 1000 milligrammes matin et soir. Je renforce les conseils hygiéno-diététiques. Bilan biologique de contrôle dans 3 mois. Je l'oriente vers une consultation de podologie préventive.";

const AI_DEMO_REPORT = {
  motif: "Suivi diabète type 2 — déséquilibre glycémique récent",
  subjectif: "Patiente de 64 ans, diabétique de type 2 connue depuis 2020 sous Metformine. Rapporte des glycémies capillaires à jeun élevées (1.4 à 1.6 g/L) depuis 7 jours. Pas de syndrome polyuro-polydipsique. Asthénie matinale modérée. Pas de douleur thoracique ni dyspnée d'effort.",
  objectif: "TA : 138/82 mmHg · FC : 76 bpm · Poids : 72 kg (IMC 27.4) · Pas d'œdème des MI · Auscultation cardio-pulmonaire normale · Examen des pieds : peau saine, sensibilité conservée, pouls périphériques perçus. Biologie reçue : HbA1c 7.8% (objectif < 7%), créatinine 78 µmol/L, ionogramme normal.",
  diagnostic: "Diabète de type 2 déséquilibré (HbA1c 7.8%) chez patiente observante. Pas de complication aiguë.",
  plan: "1. Majoration Metformine 1000 mg : 1 cp matin + 1 cp soir (au lieu de matin seul)\n2. Renforcement des règles hygiéno-diététiques : éviction sucres rapides, activité physique 30 min/j\n3. Bilan biologique de contrôle à 3 mois : HbA1c, créatinine, microalbuminurie\n4. Orientation podologie préventive (grade 0)\n5. Reconvocation à 3 mois (ou plus tôt si glycémies > 2.0 g/L)",
  cim: ["E11.9 — Diabète sucré de type 2, sans complications"],
  durée: 25,
};

function AIReportComposer({ patient, onClose, onSave }) {
  const [phase, setPhase] = React.useState("idle"); // idle | recording | transcribing | generating | report
  const [transcript, setTranscript] = React.useState("");
  const [elapsed, setElapsed] = React.useState(0);
  const timerRef = React.useRef(null);

  const startRecording = () => {
    setPhase("recording"); setTranscript(""); setElapsed(0);
    timerRef.current = setInterval(() => setElapsed((e) => e + 1), 1000);
  };
  const stopRecording = () => {
    clearInterval(timerRef.current);
    setPhase("transcribing");
    // Simulate live transcription typing
    let i = 0;
    const id = setInterval(() => {
      i += 8;
      setTranscript(AI_DEMO_TRANSCRIPT.slice(0, i));
      if (i >= AI_DEMO_TRANSCRIPT.length) {
        clearInterval(id);
        setTimeout(() => setPhase("generating"), 400);
        setTimeout(() => setPhase("report"), 2200);
      }
    }, 30);
  };

  React.useEffect(() => () => clearInterval(timerRef.current), []);
  const mm = String(Math.floor(elapsed / 60)).padStart(2, "0");
  const ss = String(elapsed % 60).padStart(2, "0");

  return (
    <div className="fixed inset-0 z-[110] flex items-center justify-center p-4" data-testid="ai-composer" onClick={onClose}>
      <div className="absolute inset-0 bg-slate-900/70 backdrop-blur-sm" />
      <div className="relative bg-white w-full max-w-3xl max-h-[90vh] overflow-y-auto rounded-2xl shadow-2xl" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between gap-3 rounded-t-2xl">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl grid place-items-center text-white" style={{ background: "linear-gradient(135deg, #4FC3F7, #00E5A0)" }}>
              <Sparkles className="w-5 h-5" />
            </div>
            <div>
              <h3 className="text-[16px] font-bold text-slate-900">Nouvelle consultation — assistant IA</h3>
              <p className="text-[12px] text-slate-500">{patient.first} {patient.last} · {patient.age} ans · {new Date().toLocaleDateString("fr-FR", { dateStyle: "long" })}</p>
            </div>
          </div>
          <button onClick={onClose} aria-label="Fermer" data-testid="ai-composer-close" className="p-2 rounded-lg hover:bg-slate-100 text-slate-500">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-6">
          {/* Mic / status */}
          <div className="rounded-xl border border-slate-200 bg-slate-50 p-5 flex flex-col items-center text-center">
            <button
              onClick={phase === "recording" ? stopRecording : (phase === "idle" ? startRecording : undefined)}
              disabled={phase === "transcribing" || phase === "generating" || phase === "report"}
              data-testid="ai-mic-btn"
              className={`relative w-20 h-20 rounded-full grid place-items-center text-white transition-all ${
                phase === "recording" ? "bg-red-500 shadow-[0_0_0_8px_rgba(239,68,68,0.20)]" : "bg-[#0F2B5B] hover:bg-[#1E4A8E] disabled:bg-slate-300"
              }`}
            >
              {phase === "recording" ? <span className="block w-5 h-5 bg-white rounded-sm" /> : <Mic className="w-7 h-7" />}
              {phase === "recording" && <span className="absolute inset-0 rounded-full animate-ping bg-red-500/40" />}
            </button>
            <div className="mt-4">
              {phase === "idle" && <span className="text-[14px] text-slate-700 font-medium">Cliquez pour démarrer la dictée vocale</span>}
              {phase === "recording" && <span className="text-[14px] text-red-600 font-semibold tabular-nums">● Enregistrement · {mm}:{ss}</span>}
              {phase === "transcribing" && <span className="text-[14px] text-[#4FC3F7] font-semibold">Transcription en cours…</span>}
              {phase === "generating" && <span className="text-[14px] text-[#0F2B5B] font-semibold inline-flex items-center gap-2"><RefreshCw className="w-3.5 h-3.5 animate-spin" />Génération du compte-rendu structuré…</span>}
              {phase === "report" && <span className="text-[14px] text-emerald-700 font-semibold inline-flex items-center gap-2"><CheckCircle2 className="w-4 h-4" />Compte-rendu généré · prêt à valider</span>}
            </div>
            <p className="mt-2 text-[11.5px] text-slate-400 max-w-md">L&apos;IA structure automatiquement votre dictée au format SOAP médical · 100% local · HDS</p>
          </div>

          {/* Transcript */}
          {(phase === "transcribing" || phase === "generating" || phase === "report") && (
            <div className="mt-5">
              <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-2">Transcription brute</div>
              <div className="rounded-lg border border-slate-200 bg-white p-4 text-[13px] text-slate-700 leading-relaxed min-h-[60px]" data-testid="ai-transcript">
                {transcript}{phase === "transcribing" && <span className="inline-block w-2 h-4 bg-[#4FC3F7] animate-pulse ml-0.5" />}
              </div>
            </div>
          )}

          {/* Generated report */}
          {phase === "report" && (
            <div className="mt-5 space-y-4" data-testid="ai-report">
              <div className="text-[11px] uppercase tracking-wider text-[#4FC3F7] font-bold inline-flex items-center gap-1.5">
                <Sparkles className="w-3 h-3" /> Compte-rendu structuré généré par IA
              </div>
              <div className="rounded-xl border-2 border-[#4FC3F7]/30 bg-gradient-to-br from-[#F0F9FF] to-white p-5 space-y-4">
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Motif</div>
                  <div className="text-[14px] font-semibold text-slate-900 mt-0.5">{AI_DEMO_REPORT.motif}</div>
                </div>
                {[
                  ["S — Subjectif (anamnèse)", AI_DEMO_REPORT.subjectif],
                  ["O — Objectif (examen + examens)", AI_DEMO_REPORT.objectif],
                  ["A — Analyse / Diagnostic", AI_DEMO_REPORT.diagnostic],
                  ["P — Plan thérapeutique", AI_DEMO_REPORT.plan],
                ].map(([k, v]) => (
                  <div key={k} className="border-l-2 border-[#4FC3F7] pl-3">
                    <div className="text-[10px] uppercase tracking-wider text-[#0F2B5B] font-bold">{k}</div>
                    <div className="text-[13px] text-slate-800 mt-1 leading-relaxed whitespace-pre-line">{v}</div>
                  </div>
                ))}
                <div className="pt-3 border-t border-[#4FC3F7]/20 flex flex-wrap gap-2">
                  {AI_DEMO_REPORT.cim.map((c, i) => (
                    <span key={i} className="text-[11px] px-2 py-1 rounded bg-[#0F2B5B] text-white font-mono font-semibold">{c}</span>
                  ))}
                  <span className="text-[11px] px-2 py-1 rounded bg-slate-100 text-slate-700">Durée : {AI_DEMO_REPORT.durée} min</span>
                </div>
              </div>

              <div className="flex items-center justify-end gap-2 pt-2">
                <Button variant="outline" className="border-slate-300" onClick={onClose}>Annuler</Button>
                <Button variant="outline" className="border-slate-300" onClick={() => { setPhase("idle"); setTranscript(""); }}>Reprendre la dictée</Button>
                <Button className="bg-[#0F2B5B] text-white hover:bg-[#1E4A8E]" data-testid="ai-validate-btn" onClick={() => { onSave && onSave(); toast.success("Compte-rendu signé électroniquement et archivé dans le DMP"); onClose(); }}>
                  <CheckCircle2 className="w-4 h-4 mr-1.5" /> Valider et signer
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function PatientMobileView({ patient, onClose }) {
  React.useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const time = new Date().toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });

  return (
    <div className="fixed inset-0 z-[110] flex items-center justify-center p-4" data-testid="patient-mobile" onClick={onClose}>
      <div className="absolute inset-0 bg-slate-900/70 backdrop-blur-sm" />
      <div className="relative" onClick={(e) => e.stopPropagation()}>
        {/* Phone frame */}
        <div className="relative w-[380px] h-[760px] bg-slate-900 rounded-[44px] p-3 shadow-[0_30px_80px_rgba(0,0,0,0.5)] border-4 border-slate-800">
          {/* Notch */}
          <div className="absolute top-3 left-1/2 -translate-x-1/2 w-32 h-6 bg-slate-900 rounded-full z-10" />
          {/* Screen */}
          <div className="w-full h-full rounded-[36px] bg-slate-50 overflow-hidden flex flex-col">
            {/* iOS Status bar */}
            <div className="flex items-center justify-between px-6 pt-3 pb-2 text-[12px] font-semibold text-slate-900">
              <span>{time}</span>
              <span className="text-[11px]">●●●● MetaConnect 5G ▮</span>
            </div>

            {/* App header */}
            <div className="px-5 pt-2 pb-4 bg-white border-b border-slate-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg bg-[#0F2B5B] text-white grid place-items-center text-[12px] font-bold">M</div>
                  <div>
                    <div className="text-[10px] text-slate-500 leading-none">Mon Espace Santé</div>
                    <div className="text-[14px] font-bold text-slate-900 leading-tight">Bonjour {patient.first} 👋</div>
                  </div>
                </div>
                <Bell className="w-5 h-5 text-slate-500" />
              </div>

              {/* Next RDV card */}
              {patient.nextRdv && (
                <div className="mt-4 rounded-2xl p-3 text-white" style={{ background: "linear-gradient(135deg, #0F2B5B, #1E4A8E)" }}>
                  <div className="text-[10px] uppercase tracking-wider opacity-80">Prochain rendez-vous</div>
                  <div className="text-[15px] font-bold mt-0.5">{new Date(patient.nextRdv).toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" })}</div>
                  <div className="text-[13px] opacity-90">{new Date(patient.nextRdv).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })} · Dr. Martin · Téléconsultation</div>
                  <div className="mt-2 flex gap-2">
                    <span className="text-[10.5px] px-2 py-0.5 rounded-full bg-white/20">📍 À domicile</span>
                    <span className="text-[10.5px] px-2 py-0.5 rounded-full bg-[#4FC3F7] text-[#0F2B5B] font-bold">Rejoindre</span>
                  </div>
                </div>
              )}
            </div>

            {/* Scrollable body */}
            <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5">
              {/* Allergies card */}
              {(patient.allergies?.length > 0 || patient.ald?.length > 0) && (
                <div className="rounded-xl border border-amber-300 bg-amber-50 p-3">
                  <div className="flex items-center gap-2 text-amber-800 mb-1">
                    <AlertTriangle className="w-4 h-4" />
                    <span className="text-[12px] font-bold uppercase tracking-wider">Mes alertes médicales</span>
                  </div>
                  <div className="space-y-0.5 text-[12.5px] text-amber-900">
                    {patient.allergies?.map((a, i) => <div key={i}><b>Allergie :</b> {a}</div>)}
                    {patient.ald?.map((a, i) => <div key={i}><b>ALD :</b> {a}</div>)}
                  </div>
                </div>
              )}

              {/* Documents récents */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[12px] font-bold text-slate-900">Mes documents</span>
                  <span className="text-[11px] text-[#4FC3F7] font-semibold">Tout voir →</span>
                </div>
                <div className="space-y-2">
                  {patient.documents.slice(0, 3).map((d, i) => (
                    <div key={i} className="rounded-xl bg-white border border-slate-200 p-3 flex items-center gap-3 active:scale-[0.98] transition-transform">
                      <div className="w-9 h-9 rounded-lg bg-[#F0F9FF] grid place-items-center text-[#0F2B5B] shrink-0">
                        <FileText className="w-4 h-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-[12.5px] font-semibold text-slate-900 truncate">{d.title}</div>
                        <div className="text-[10.5px] text-slate-500 truncate">{d.type} · {d.date}</div>
                      </div>
                      <span className="text-[10.5px] text-[#4FC3F7] font-bold">↓</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Ordonnances */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[12px] font-bold text-slate-900">Mes ordonnances</span>
                  <span className="text-[11px] text-[#4FC3F7] font-semibold">Tout voir →</span>
                </div>
                {patient.prescriptions.slice(0, 1).map((rx, i) => (
                  <div key={i} className="rounded-xl bg-white border border-slate-200 p-3">
                    <div className="flex items-center justify-between">
                      <span className="text-[11px] text-slate-500">Prescrit le {rx.date}</span>
                      {rx.renewable && <span className="text-[9.5px] uppercase font-bold text-emerald-700 bg-emerald-100 px-1.5 py-0.5 rounded-full">Renouvelable</span>}
                    </div>
                    <div className="mt-2 space-y-1">
                      {rx.items.slice(0, 2).map((it, j) => (
                        <div key={j} className="text-[12px] text-slate-800 flex items-start gap-1.5">
                          <span className="w-1 h-1 rounded-full bg-[#0F2B5B] mt-1.5 shrink-0" />{it}
                        </div>
                      ))}
                      {rx.items.length > 2 && <div className="text-[11px] text-slate-400">+ {rx.items.length - 2} autre(s)</div>}
                    </div>
                    <button className="mt-3 w-full py-2 rounded-lg text-[12px] font-bold text-white" style={{ background: "linear-gradient(135deg, #0F2B5B, #1E4A8E)" }}>
                      Envoyer à ma pharmacie
                    </button>
                  </div>
                ))}
              </div>

              {/* Messagerie sécurisée */}
              <div className="rounded-xl border-2 border-dashed border-[#4FC3F7]/40 p-3 text-center bg-[#F0F9FF]">
                <Mail className="w-5 h-5 text-[#4FC3F7] mx-auto" />
                <div className="text-[12.5px] font-bold text-slate-900 mt-1">Messagerie sécurisée</div>
                <div className="text-[10.5px] text-slate-500 mt-0.5">Échangez avec votre médecin via MSSanté</div>
              </div>
            </div>

            {/* Bottom tabbar iOS */}
            <div className="border-t border-slate-200 bg-white px-2 py-2 grid grid-cols-4 text-center">
              {[
                ["Accueil", true], ["Documents", false], ["Messages", false], ["Profil", false],
              ].map(([k, active], i) => (
                <div key={i} className={`text-[10px] ${active ? "text-[#0F2B5B] font-bold" : "text-slate-400"}`}>
                  <div className={`w-1.5 h-1.5 rounded-full mx-auto mb-1 ${active ? "bg-[#4FC3F7]" : "bg-transparent"}`} />
                  {k}
                </div>
              ))}
            </div>
          </div>
        </div>

        <button onClick={onClose} aria-label="Fermer" data-testid="patient-mobile-close" className="absolute -top-2 -right-2 w-9 h-9 bg-white rounded-full shadow-lg grid place-items-center text-slate-700 hover:bg-slate-100">
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

function PatientDetailDrawer({ patient, onClose }) {
  const [tab, setTab] = React.useState("synthese");
  const [aiOpen, setAiOpen] = React.useState(false);
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [pmsVisit, setPmsVisit] = React.useState(null);
  React.useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape" && !aiOpen && !mobileOpen && !pmsVisit) onClose(); };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => { window.removeEventListener("keydown", onKey); document.body.style.overflow = ""; };
  }, [onClose, aiOpen, mobileOpen, pmsVisit]);

  if (!patient) return null;
  const alerts = [...(patient.allergies || []).map((a) => ({ k: "Allergie", v: a })), ...(patient.ald || []).map((a) => ({ k: "ALD", v: a }))];

  const tabs = [
    { id: "synthese", label: "Synthèse", count: undefined },
    { id: "antecedents", label: "Antécédents", count: (patient.antecedentsCoded || patient.antecedents || []).length },
    { id: "consultations", label: "Consultations", count: patient.consultations?.length },
    { id: "prescriptions", label: "Prescriptions", count: patient.prescriptions?.length },
    { id: "documents", label: "Documents", count: patient.documents?.length },
  ];

  return (
    <div className="fixed inset-0 z-[100] flex justify-end" data-testid="patient-drawer" onClick={onClose}>
      <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" />
      <div
        className="relative bg-white w-full max-w-3xl h-full overflow-y-auto shadow-2xl flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Sticky header */}
        <div className="sticky top-0 z-10 bg-white border-b border-slate-200 px-6 py-4">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h2 className="text-[20px] font-bold text-slate-900">{patient.first} {patient.last}</h2>
                {patient.isMT && (
                  <span className="inline-flex items-center gap-1 text-[10.5px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 border border-emerald-300">MT</span>
                )}
                {alerts.length > 0 && (
                  <span className="inline-flex items-center gap-1 text-[10.5px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 border border-amber-300" title={alerts.map((a) => `${a.k} : ${a.v}`).join(" · ")}>
                    <AlertTriangle className="w-2.5 h-2.5" /> {alerts.length} alerte{alerts.length > 1 ? "s" : ""}
                  </span>
                )}
              </div>
              <div className="mt-1 text-[13px] text-slate-600 flex items-center gap-3 flex-wrap">
                <span>{patient.sex === "F" ? "Femme" : "Homme"} · {patient.age} ans · né{patient.sex === "F" ? "e" : ""} le {patient.birth}</span>
                <span className="font-mono text-[12px] text-slate-500">{patient.ins}</span>
              </div>
            </div>
            <div className="flex items-start gap-2 shrink-0">
              <button
                onClick={() => setAiOpen(true)}
                data-testid="open-ai-composer"
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-[12.5px] font-semibold text-white shadow-md hover:shadow-lg transition-all"
                style={{ background: "linear-gradient(135deg, #4FC3F7, #00E5A0)" }}
                title="Rédaction du compte-rendu avec assistance IA"
              >
                <Sparkles className="w-4 h-4" /> Compte-rendu IA
              </button>
              <button
                onClick={() => setMobileOpen(true)}
                data-testid="open-patient-mobile"
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-[12.5px] font-semibold border border-slate-300 bg-white text-slate-700 hover:bg-slate-50"
                title="Aperçu de l'application mobile du patient"
              >
                <Smartphone className="w-4 h-4" /> Vue patient
              </button>
              <button onClick={onClose} aria-label="Fermer" data-testid="patient-drawer-close" className="p-2 rounded-lg hover:bg-slate-100 text-slate-500">
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Alerts banner */}
          {alerts.length > 0 && (
            <div className="mt-3 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-[12.5px] text-amber-900 flex items-start gap-2" data-testid="patient-alerts-banner">
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0 text-amber-600" />
              <div className="flex flex-wrap gap-x-3 gap-y-1">
                {alerts.map((a, i) => (
                  <span key={i}><span className="font-bold uppercase tracking-wider text-[10.5px]">{a.k} :</span> {a.v}</span>
                ))}
              </div>
            </div>
          )}

          {/* Tabs */}
          <div className="mt-4 -mb-4 flex items-center gap-1 overflow-x-auto border-b border-transparent">
            {tabs.map((t) => (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                data-testid={`patient-tab-${t.id}`}
                className={`px-4 py-2.5 text-[13px] font-medium border-b-2 transition-colors whitespace-nowrap ${
                  tab === t.id ? "border-[#4FC3F7] text-[#0F2B5B]" : "border-transparent text-slate-500 hover:text-slate-700"
                }`}
              >
                {t.label}{typeof t.count === "number" && <span className="ml-1.5 text-[11px] text-slate-400">({t.count})</span>}
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 px-6 py-5">
          {tab === "synthese" && (
            <div className="space-y-5" data-testid="patient-tab-content-synthese">
              <section>
                <h3 className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-2">Constantes</h3>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                  {[
                    ["Poids", `${patient.weight} kg`],
                    ["Taille", `${patient.height} cm`],
                    ["IMC", patient.bmi],
                    ["Tension", patient.ta],
                    ["Pouls", `${patient.pulse} bpm`],
                  ].map(([k, v]) => (
                    <div key={k} className="rounded-lg border border-slate-200 p-2.5 bg-slate-50">
                      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{k}</div>
                      <div className="text-[16px] font-bold text-slate-900 leading-tight">{v}</div>
                    </div>
                  ))}
                </div>
              </section>

              <section>
                <h3 className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-2">Antécédents</h3>
                <ul className="space-y-1.5">
                  {patient.antecedents.map((a, i) => (
                    <li key={i} className="flex items-start gap-2 text-[13px] text-slate-700">
                      <span className="w-1.5 h-1.5 rounded-full bg-[#4FC3F7] mt-1.5 shrink-0" />{a}
                    </li>
                  ))}
                </ul>
              </section>

              <section className="grid sm:grid-cols-2 gap-3">
                <div className="rounded-lg border border-slate-200 p-3">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Dernière visite</div>
                  <div className="text-[14px] font-semibold text-slate-900 mt-0.5">{patient.lastVisit}</div>
                </div>
                <div className="rounded-lg border border-slate-200 p-3">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Prochain RDV</div>
                  <div className="text-[14px] font-semibold text-slate-900 mt-0.5">
                    {patient.nextRdv ? new Date(patient.nextRdv).toLocaleString("fr-FR", { dateStyle: "long", timeStyle: "short" }) : "Aucun"}
                  </div>
                </div>
              </section>
            </div>
          )}

          {tab === "antecedents" && (
            <PatientAntecedentsTab patient={patient} />
          )}

          {tab === "consultations" && (
            <div className="space-y-3" data-testid="patient-tab-content-consultations">
              {patient.consultations.map((c, i) => (
                <div key={i} className="rounded-lg border border-slate-200 p-4 hover:border-[#4FC3F7]/40 transition-colors">
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div>
                      <div className="text-[14px] font-bold text-slate-900">{c.motif}</div>
                      <div className="text-[11.5px] text-slate-500 mt-0.5">{c.date} · {c.ps} · {c.duration} min · {c.ngap || "G · 26,50 €"}</div>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      {c.pmsSent
                        ? <span data-testid={`pms-badge-sent-${i}`} className="inline-flex items-center gap-1 text-[10.5px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-semibold border border-emerald-200"><CheckCircle2 className="w-2.5 h-2.5" /> Transmis PMS</span>
                        : <span data-testid={`pms-badge-notsent-${i}`} className="inline-flex items-center gap-1 text-[10.5px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-slate-100 text-slate-500 font-semibold border border-slate-200">Non transmis</span>
                      }
                      <span className="text-[10.5px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#0F2B5B] text-white font-semibold">Diagnostic</span>
                      {!c.pmsSent && (
                        <button
                          onClick={() => setPmsVisit(c)}
                          data-testid={`pms-send-btn-${i}`}
                          className="inline-flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1 rounded-md text-white shadow-sm hover:shadow-md transition-all"
                          style={{ backgroundColor: "#4FC3F7" }}
                          title="Envoyer les données au logiciel métier (PMS)"
                        >
                          <ArrowUpRight className="w-3 h-3" /> Envoyer au PMS
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="mt-2 text-[13px] text-slate-800 font-medium">{c.diag}</div>
                  <p className="mt-2 text-[13px] text-slate-600 leading-relaxed">{c.notes}</p>
                </div>
              ))}
            </div>
          )}

          {tab === "prescriptions" && (
            <div className="space-y-3" data-testid="patient-tab-content-prescriptions">
              {patient.prescriptions.map((rx, i) => (
                <div key={i} className="rounded-lg border border-slate-200 p-4">
                  <div className="flex items-center justify-between gap-3 mb-2 flex-wrap">
                    <div className="text-[12px] text-slate-500">Prescrit le <span className="font-semibold text-slate-800">{rx.date}</span></div>
                    {rx.renewable && <span className="text-[10.5px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-semibold">Renouvelable</span>}
                  </div>
                  <ul className="space-y-1.5">
                    {rx.items.map((it, j) => (
                      <li key={j} className="flex items-start gap-2 text-[13px] text-slate-800">
                        <span className="w-1.5 h-1.5 rounded-full bg-[#0F2B5B] mt-1.5 shrink-0" />{it}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          )}

          {tab === "documents" && (
            <div className="space-y-2" data-testid="patient-tab-content-documents">
              {patient.documents.map((doc, i) => (
                <div key={i} className="rounded-lg border border-slate-200 p-3 flex items-start gap-3 hover:bg-slate-50 transition-colors cursor-pointer">
                  <div className="w-10 h-10 rounded-lg bg-[#F0F9FF] border border-[#BAE6FD] grid place-items-center text-[#0F2B5B] shrink-0">
                    <FileText className="w-5 h-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[13.5px] font-semibold text-slate-900">{doc.title}</div>
                    <div className="text-[11.5px] text-slate-500 mt-0.5">
                      <span className="font-medium text-[#4FC3F7]">{doc.type}</span> · {doc.date} · {doc.from} · {doc.size}
                    </div>
                  </div>
                  <Button variant="outline" size="sm" className="border-slate-300 text-slate-600 shrink-0">Ouvrir</Button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {aiOpen && <AIReportComposer patient={patient} onClose={() => setAiOpen(false)} />}
      {mobileOpen && <PatientMobileView patient={patient} onClose={() => setMobileOpen(false)} />}
      {pmsVisit && <PmsSendModal patient={patient} visit={pmsVisit} onClose={() => setPmsVisit(null)} />}
    </div>
  );
}

const SEED_INBOX = [
  { id: "m1", from: "secretariat.chu-orleans@chu-orleans.mssante.fr", subject: "Compte-rendu d'hospitalisation — M. Garcia", date: "Aujourd'hui · 09:42", unread: true, preview: "Bonjour Docteur, veuillez trouver ci-joint le CR de l'hospitalisation…" },
  { id: "m2", from: "dr.morel@medecin.mssante.fr", subject: "Demande d'avis téléexpertise — Mme Lefèvre", date: "Hier · 17:18", unread: true, preview: "Bonjour, dans le cadre du suivi de votre patiente, je sollicite votre avis…" },
  { id: "m3", from: "labo.sud@labosud.mssante.fr", subject: "Résultats biologiques — A. Benali", date: "26 mars · 11:05", unread: false, preview: "Résultats à votre disposition pour la patiente Aïcha Benali…" },
];

const slugify = (s) => (s || "votre-structure").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 60) || "votre-structure";

// SEC-002 defense-in-depth: render-time URL sanitizer.
// Only allow http(s) URLs; everything else (javascript:, data:, vbscript:, …) is
// neutralised to "#" so a poisoned preview cannot run code on the metaconnect.fr origin.
const safeHttpUrl = (raw) => {
  if (!raw || typeof raw !== "string") return "#";
  const trimmed = raw.trim();
  if (!trimmed || trimmed.length > 2048) return "#";
  try {
    const u = new URL(trimmed);
    return (u.protocol === "http:" || u.protocol === "https:") ? u.toString() : "#";
  } catch {
    return "#";
  }
};

// Logo URLs accept the http(s) whitelist above PLUS image data-URLs uploaded via the
// Wizard's <input type="file">. SVG is OK because browsers neutralise scripts inside
// <img src="data:image/svg+xml;…">. Any other scheme is rejected, mirroring the backend.
const LOGO_DATA_URL_RE = /^data:image\/(png|jpe?g|svg\+xml|webp|gif);base64,[A-Za-z0-9+/=\s]+$/i;
const safeLogoSrc = (raw) => {
  if (!raw || typeof raw !== "string") return "";
  const trimmed = raw.trim();
  if (!trimmed) return "";
  if (safeHttpUrl(trimmed) !== "#") return trimmed;
  // Cap at ~1.4 MB to stay aligned with the backend ceiling.
  if (trimmed.length <= 1_400_000 && LOGO_DATA_URL_RE.test(trimmed)) return trimmed;
  return "";
};

export default function StudioPreview() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const isEditor = searchParams.get("mode") === "editor";

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  // App-like internal state machine: 'landing' | 'login' | 'dashboard' | ...
  const [view, setView] = useState("landing");
  const [authed, setAuthed] = useState(false);
  const [showWelcome, setShowWelcome] = useState(false);
  const [tourIdx, setTourIdx] = useState(null);

  // Analytics: expose the sandbox module currently viewed by the prospect
  // as a wizard step so it surfaces in /admin/studio engagement breakdown.
  useEffect(() => {
    if (authed && !isEditor) setWizardStep(`sandbox:${view}`);
  }, [authed, view, isEditor]);

  // Local CRUD state (not persisted server-side)
  const [users, setUsers] = useState(SEED_USERS);

  // --- Editor-only state ---------------------------------------------------
  // The hook is always called (Rules of Hooks) but only consumed when in editor mode.
  const draft = useStudioDraft(isEditor ? id : null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  // Track banners already dismissed (answered or skipped) for the current session.
  // Stored as a Set of "blockId.qId". Banners stay hidden until the user refreshes,
  // so they're not re-shown after a single answer.
  const [dismissed, setDismissed] = useState(() => new Set());

  // Read-only fetch (used when NOT in editor mode)
  useEffect(() => {
    if (isEditor) return; // editor mode uses the draft hook instead
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API_BASE}/studio/preview/${id}`, { timeout: 12000 });
        if (!cancelled) setData(data);
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.detail || "Aperçu introuvable.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [id, isEditor]);

  // In editor mode, mirror the draft hook's state into `data` so the rest of the
  // sandbox (which reads `data?.brand`, `data?.services`) just works without changes.
  useEffect(() => {
    if (!isEditor) return;
    if (draft.status === "loading") return;
    if (draft.status === "error") {
      setError(draft.loadError || "Brouillon introuvable.");
      setLoading(false);
      return;
    }
    setData({ brand: draft.brand, services: draft.services, answers: draft.answers });
    setLoading(false);
  }, [isEditor, draft.status, draft.loadError, draft.brand, draft.services, draft.answers]);

  // Auto-jump to the dashboard view when the editor opens so the user lands
  // directly on the most question-rich page.
  useEffect(() => {
    if (isEditor && !authed && !loading) {
      setAuthed(true);
      setView("dashboard");
    }
  }, [isEditor, authed, loading]);

  const brand_raw = data?.brand || {};
  const services = data?.services || [];
  const hasService = (sid) => services.some((s) => s.id === sid);

  const slug = useMemo(() => slugify(brand_raw.name), [brand_raw.name]);
  const platformHost = brand_raw.domain
    ? (brand_raw.domainType === "subdomain" ? `${brand_raw.domain}.metaconnect.fr` : brand_raw.domain)
    : `${slug}.metaconnect.fr`;
  // SEC-002: sanitize all user-provided URLs at the source — only http(s) URLs reach the rendered DOM.
  const sanitizedRdv = safeHttpUrl(brand_raw.rdvUrl);
  const hasExternalRdv = sanitizedRdv !== "#";
  const rdvUrl = hasExternalRdv ? sanitizedRdv : "#internal-rdv";
  const safeLogoUrl = safeLogoSrc(brand_raw.logoUrl);
  // brand is the sanitized view propagated everywhere; the raw value is never passed down.
  const brand = useMemo(() => ({ ...brand_raw, rdvUrl: hasExternalRdv ? sanitizedRdv : "", logoUrl: safeLogoUrl }), [brand_raw, hasExternalRdv, sanitizedRdv, safeLogoUrl]);

  // Open the internal booking flow when no external RDV URL provided
  const openInternalRdv = () => setView("public-booking");

  // Inject brand CSS variables on the preview shell
  const themeStyle = useMemo(() => ({
    "--bp": brand.color || "#2D7BFF",
    "--ba": brand.accent || "#0F172A",
    fontFamily: brand.font ? `${brand.font}, system-ui, sans-serif` : undefined,
  }), [brand]);

  // ----- Editor-mode helpers (no-ops when not in editor) -----
  // Declared BEFORE any conditional return so React's hook order stays stable across renders.
  const { answered, total } = useMemo(
    () => (isEditor ? countAnsweredQuestions(draft.answers) : { answered: 0, total: 0 }),
    [isEditor, draft.answers]
  );
  const sandboxEffects = useMemo(
    () => (isEditor ? deriveSandboxEffects(draft.answers) : null),
    [isEditor, draft.answers]
  );
  const activeBanner = useMemo(() => {
    if (!isEditor) return null;
    const candidates = SECTION_BANNERS[view] || [];
    for (const c of candidates) {
      const dismKey = `${c.blockId}.${c.qId}`;
      if (dismissed.has(dismKey)) continue;
      const existing = (draft.answers?.[c.blockId] || {})[c.qId];
      const hasAnswer =
        existing !== undefined &&
        existing !== null &&
        existing !== "" &&
        !(Array.isArray(existing) && existing.length === 0);
      if (hasAnswer) continue;
      const q = findQuestion(c.blockId, c.qId);
      if (q) return { ...c, question: q };
    }
    return null;
  }, [isEditor, view, dismissed, draft.answers]);

  const onBannerAnswer = useCallback((payload) => {
    if (!activeBanner) return;
    const { blockId, qId, question } = activeBanner;
    if (payload.toggle && question.type === "checkbox") {
      const current = Array.isArray((draft.answers?.[blockId] || {})[qId])
        ? (draft.answers[blockId][qId])
        : [];
      const next = current.includes(payload.toggle)
        ? current.filter((x) => x !== payload.toggle)
        : [...current, payload.toggle];
      draft.setAnswer(blockId, qId, next);
      return;
    }
    draft.setAnswer(blockId, qId, payload.value);
    setDismissed((d) => new Set(d).add(`${blockId}.${qId}`));
  }, [activeBanner, draft]);

  const onBannerSkip = useCallback(() => {
    if (!activeBanner) return;
    setDismissed((d) => new Set(d).add(`${activeBanner.blockId}.${activeBanner.qId}`));
  }, [activeBanner]);

  const onPublishDraft = useCallback(async () => {
    try {
      const res = await draft.publish();
      if (res?.status === "published") {
        toast.success("Cahier des charges finalisé ! Vous allez recevoir le PDF par email.");
        setTimeout(() => navigate(`/studio/preview/${id}`, { replace: true }), 1200);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec de la finalisation.");
    }
  }, [draft, id, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen grid place-items-center bg-slate-50 text-slate-700">
        <div className="text-center">
          <div className="mx-auto w-12 h-12 rounded-full border-4 border-slate-200 border-t-[var(--mc-mint-400,#6EE7C5)] animate-spin" />
          <p className="mt-4 text-sm">Chargement de votre aperçu…</p>
        </div>
      </div>
    );
  }
  if (error) {
    return (
      <div className="min-h-screen grid place-items-center bg-slate-50 text-slate-700 p-6">
        <div className="text-center max-w-md">
          <X className="w-12 h-12 mx-auto text-red-500 mb-3" />
          <h1 className="text-2xl font-semibold text-slate-900">Aperçu introuvable</h1>
          <p className="mt-2 text-sm text-slate-600">{error}</p>
          <a href="/studio" className="mt-4 inline-flex items-center gap-2 text-[var(--mc-mint-400,#6EE7C5)] text-sm font-medium underline">
            <ArrowLeft className="w-4 h-4" /> Retour au configurateur
          </a>
        </div>
      </div>
    );
  }

  // ----- Editor-mode helpers (no-ops when not in editor) -----

  // Header on every preview view (sandbox banner)
  return (
    <div className="min-h-screen bg-slate-50" style={themeStyle} data-testid="studio-preview-root">
      {isEditor && (
        <>
          <StudioInlineTopBar
            brandName={brand.name}
            answered={answered}
            total={total}
            isDraft={draft.status === "draft"}
            isSaving={draft.isSaving}
            isPublishing={draft.isPublishing}
            onOpenDrawer={() => setDrawerOpen(true)}
            onPublish={onPublishDraft}
          />
          <StudioInlineDrawer
            open={drawerOpen}
            onClose={() => setDrawerOpen(false)}
            answers={draft.answers}
            onAnswer={(blockId, qId, value) => draft.setAnswer(blockId, qId, value)}
          />
        </>
      )}
      <SandboxBar brand={brand} />
      {isEditor && activeBanner && (
        <StudioInlineBanner
          bannerKey={`${activeBanner.blockId}.${activeBanner.qId}`}
          question={activeBanner.question}
          onAnswer={onBannerAnswer}
          onSkip={onBannerSkip}
        />
      )}
      {view === "landing" && (
        <Landing brand={brand} services={services} platformHost={platformHost} rdvUrl={rdvUrl} hasExternalRdv={hasExternalRdv}
          onLogin={() => setView("login")} onBookRdv={openInternalRdv}
        />
      )}
      {view === "login" && (
        <Login brand={brand} sandboxEffects={sandboxEffects}
          onBack={() => setView("landing")}
          onAuth={() => {
            setAuthed(true); setView("dashboard");
            if (!isEditor && !sessionStorage.getItem(`mc_sbx_welcome_${id}`)) {
              sessionStorage.setItem(`mc_sbx_welcome_${id}`, "1");
              setShowWelcome(true);
            }
          }}
        />
      )}
      {view === "public-booking" && (
        <PublicBookingShell brand={brand} services={services} platformHost={platformHost} onBack={() => setView("landing")} />
      )}
      {authed && view !== "landing" && view !== "login" && view !== "public-booking" && (
        <DashboardShell
          brand={brand} services={services} view={view} setView={setView}
          users={users} setUsers={setUsers}
          onLogout={() => { setAuthed(false); setView("landing"); }}
          platformHost={platformHost} rdvUrl={rdvUrl}
          sandboxEffects={sandboxEffects}
        />
      )}
      {showWelcome && authed && (
        <SandboxWelcome
          brand={brand}
          onStartTour={() => { setShowWelcome(false); setTourIdx(0); setView(TOUR_STEPS[0].view); }}
          onGoModule={(v) => { setShowWelcome(false); setView(v); }}
          onClose={() => setShowWelcome(false)}
        />
      )}
      {tourIdx !== null && authed && (
        <SandboxTourCard
          idx={tourIdx}
          onNext={() => { const n = tourIdx + 1; setTourIdx(n); setView(TOUR_STEPS[n].view); }}
          onPrev={() => { const p = Math.max(0, tourIdx - 1); setTourIdx(p); setView(TOUR_STEPS[p].view); }}
          onEnd={() => {
            setTourIdx(null);
            toast("✨ Tout ceci est personnalisable à 100 %", {
              description: "Configurez votre propre plateforme dans le Studio — un compte gratuit suffit pour sauvegarder votre interface.",
              duration: 9000,
            });
          }}
        />
      )}
    </div>
  );
}

function SandboxBar({ brand }) {
  return (
    <div className="w-full bg-amber-100 border-b border-amber-300 text-amber-900 text-[12.5px] py-1.5 px-4 flex items-center justify-between gap-3" data-testid="sandbox-banner">
      <div className="flex items-center gap-2 min-w-0">
        <Sparkles className="w-3.5 h-3.5 shrink-0" />
        <span className="truncate"><b>Aperçu sandbox</b> — Plateforme générée pour <b>{brand.name || "votre structure"}</b>. Aucune donnée réelle. Données effacées au rafraîchissement.</span>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <button
          onClick={() => { navigator.clipboard?.writeText(window.location.href); toast.success("Lien copié."); }}
          className="text-[11.5px] inline-flex items-center gap-1 px-2 py-0.5 rounded border border-amber-400 hover:bg-amber-200 transition-colors"
          data-testid="share-preview-btn"
        ><Share2 className="w-3 h-3" /> Partager</button>
        <a href="/studio" className="text-[11.5px] inline-flex items-center gap-1 px-2 py-0.5 rounded border border-amber-400 hover:bg-amber-200 transition-colors">
          <ArrowLeft className="w-3 h-3" /> Studio
        </a>
      </div>
    </div>
  );
}

/* ============================ PUBLIC BOOKING (no auth) ============================ */
function PublicBookingShell({ brand, services, platformHost, onBack }) {
  return (
    <div className="min-h-[calc(100vh-30px)] bg-slate-50">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-5 py-3 flex items-center justify-between">
          <button onClick={onBack} className="inline-flex items-center gap-2 text-slate-700 hover:text-slate-900 transition-colors" data-testid="public-booking-back">
            <ArrowLeft className="w-4 h-4" />
            <div className="flex items-center gap-2">
              {brand.logoUrl ? (
                <img src={brand.logoUrl} alt={brand.name || ""} className="h-9 max-w-[140px] object-contain" />
              ) : (
                <div className="w-9 h-9 rounded-lg grid place-items-center text-white font-bold text-[13px]" style={{ backgroundColor: "var(--bp)" }}>{(brand.name || "MC").split(" ").map((x) => x[0]).filter(Boolean).slice(0, 2).join("").toUpperCase()}</div>
              )}
              <div className="text-[15px] font-semibold text-slate-900">{brand.name || "Votre structure"}</div>
            </div>
          </button>
          <div className="text-[11.5px] text-slate-500 font-mono hidden sm:block">{platformHost}/rdv</div>
        </div>
      </header>
      <div className="py-8">
        <PagePatientBooking brand={brand} platformHost={platformHost} services={services} />
      </div>
    </div>
  );
}

/* ============================ LANDING ============================ */
function Landing({ brand, services, platformHost, rdvUrl, hasExternalRdv, onLogin, onBookRdv }) {
  // If external URL provided => open it in a new tab; otherwise open internal booking wizard
  const rdvProps = hasExternalRdv
    ? { href: rdvUrl, target: "_blank", rel: "noopener noreferrer" }
    : { href: "#", onClick: (e) => { e.preventDefault(); onBookRdv(); } };
  return (
    <div>
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-5 py-3 flex items-center justify-between">
          <BrandLogo brand={brand} />
          <div className="flex items-center gap-2">
            <a {...rdvProps}
              className="hidden sm:inline-flex items-center gap-1.5 text-[13px] text-slate-700 hover:text-slate-900 px-3 py-2 rounded-md hover:bg-slate-100 transition-colors"
              data-testid="landing-rdv-link"
            >
              <CalendarDays className="w-4 h-4" /> Prendre RDV
            </a>
            <button onClick={onLogin}
              className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-white px-4 py-2 rounded-md transition-colors"
              style={{ backgroundColor: "var(--bp)" }}
              data-testid="landing-login-btn"
            >
              <Lock className="w-4 h-4" /> Connexion
            </button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="px-5 py-14 sm:py-20 max-w-6xl mx-auto">
        <div className="grid lg:grid-cols-[1.1fr,1fr] gap-10 items-center">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-slate-100 text-slate-700 text-[12px] font-medium">
              <ShieldCheck className="w-3.5 h-3.5" /> Hébergement HDS · RGPD · ANS
            </div>
            <h1 className="mt-5 text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-slate-900 leading-tight">
              Bienvenue chez<br /><span style={{ color: "var(--bp)" }}>{brand.name || "votre structure"}</span>
            </h1>
            <p className="mt-5 text-[15.5px] text-slate-600 max-w-xl leading-relaxed">
              Notre plateforme sécurisée vous donne accès à la téléconsultation, la prise de rendez-vous en ligne et l&apos;ensemble des services de santé numérique conformes ANS.
            </p>
            <div className="mt-7 flex flex-wrap gap-3">
              <a {...rdvProps}
                className="inline-flex items-center gap-2 px-5 py-3 rounded-md text-white font-semibold text-[14.5px] shadow-sm hover:brightness-110 transition-all"
                style={{ backgroundColor: "var(--bp)" }}
                data-testid="hero-rdv-btn"
              >
                <CalendarDays className="w-4 h-4" /> Prendre RDV en ligne <ArrowRight className="w-4 h-4" />
              </a>
              <button onClick={onLogin}
                className="inline-flex items-center gap-2 px-5 py-3 rounded-md font-semibold text-[14.5px] border border-slate-300 bg-white text-slate-800 hover:bg-slate-50 transition-colors"
                data-testid="hero-login-btn"
              >
                <Lock className="w-4 h-4" /> Espace professionnel
              </button>
            </div>
            <p className="mt-3 text-[12.5px] text-slate-500">{platformHost}</p>
          </div>

          {/* Brand mockup card */}
          <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-[0_20px_60px_-20px_rgba(15,23,42,0.18)]">
            <div className="text-[11px] uppercase tracking-[0.16em] text-slate-400 font-semibold">Vos services en ligne</div>
            <div className="mt-3 grid grid-cols-2 gap-3">
              {services.length === 0 && (
                <div className="col-span-2 text-[13px] text-slate-500 italic">Aucun service configuré.</div>
              )}
              {services.map((s) => (
                <ServiceTile key={s.id} sid={s.id} title={s.title} />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Service detail blocks */}
      {services.length > 0 && (
        <section className="px-5 py-12 max-w-6xl mx-auto">
          <div className="text-[12px] uppercase tracking-[0.18em] font-semibold" style={{ color: "var(--bp)" }}>Nos services</div>
          <h2 className="mt-2 text-3xl font-bold text-slate-900">Une prise en charge de bout en bout</h2>
          <div className="mt-8 grid md:grid-cols-2 gap-5">
            {services.map((s) => <ServiceCard key={s.id} sid={s.id} title={s.title} />)}
          </div>
        </section>
      )}

      {/* Trust */}
      <section className="px-5 py-12 max-w-6xl mx-auto">
        <div className="rounded-3xl border border-slate-200 bg-white p-7 grid sm:grid-cols-3 gap-6">
          {[
            { i: ShieldCheck, t: "Hébergement HDS 🇫🇷", d: "Données hébergées en France chez un hébergeur certifié Santé." },
            { i: Lock, t: "Connexion sécurisée", d: "Authentification Pro Santé Connect (e-CPS) pour les professionnels." },
            { i: CheckCircle2, t: "Référentiels ANS", d: "INSi, MSSanté, DMP, FHIR France — conformité by design." },
            { i: Gauge, t: "Quota télésoin surveillé", d: "Plafond de 20 % d'activité à distance et 4 actes/an/patient suivis automatiquement." },
            { i: Receipt, t: "Facturation électronique 2026", d: "Factures Factur-X (EN 16931), TVA santé et transmission au comptable." },
            { i: MessageSquare, t: "Coordination d'équipe", d: "Messagerie instantanée entre professionnels + boîtes MSSanté réglementaires." },
          ].map((b, i) => (
            <div key={i}>
              <div className="w-10 h-10 rounded-lg grid place-items-center" style={{ backgroundColor: "rgba(15,23,42,0.06)" }}>
                <b.i className="w-5 h-5" style={{ color: "var(--bp)" }} />
              </div>
              <div className="mt-2.5 font-semibold text-slate-900">{b.t}</div>
              <p className="mt-1 text-[13px] text-slate-600">{b.d}</p>
            </div>
          ))}
        </div>
      </section>

      <footer className="mt-8 px-5 py-8 border-t border-slate-200 text-center text-[12px] text-slate-500">
        © {new Date().getFullYear()} {brand.name || "Votre structure"} — Plateforme propulsée par <a href="https://metaconnect.fr" className="underline">MetaConnect</a>.
      </footer>
    </div>
  );
}

function BrandLogo({ brand }) {
  const initials = (brand.name || "MC").split(" ").map((x) => x[0]).filter(Boolean).slice(0, 2).join("").toUpperCase();
  return (
    <div className="flex items-center gap-2.5">
      {brand.logoUrl ? (
        <img src={brand.logoUrl} alt={brand.name || ""} className="h-9 max-w-[180px] object-contain" onError={(e) => { e.currentTarget.style.display = "none"; }} />
      ) : (
        <div className="w-9 h-9 rounded-lg grid place-items-center text-white font-bold text-[14px]" style={{ backgroundColor: "var(--bp)" }}>
          {initials}
        </div>
      )}
      <div className="font-semibold text-slate-900 text-[15.5px]">{brand.name || "Votre structure"}</div>
    </div>
  );
}

function ServiceTile({ sid, title }) {
  const icon = SERVICE_ICONS[sid] || Stethoscope;
  const Icon = icon;
  return (
    <div className="rounded-xl border border-slate-200 p-3.5 hover:border-slate-300 transition-colors">
      <div className="w-9 h-9 rounded-lg grid place-items-center" style={{ backgroundColor: "rgba(15,23,42,0.06)" }}>
        <Icon className="w-4.5 h-4.5" style={{ color: "var(--bp)" }} />
      </div>
      <div className="mt-2 text-[13.5px] font-semibold text-slate-900">{title}</div>
    </div>
  );
}

const SERVICE_ICONS = { telecons: Video, teleexp: Headphones, telesurv: Activity, webmail: MailCheck };

function ServiceCard({ sid, title }) {
  const Icon = SERVICE_ICONS[sid] || Stethoscope;
  const desc = {
    telecons: "Consultation à distance en visio chiffrée bout-en-bout, conforme HAS. Salle d'attente intégrée, partage de documents, transmission au DMP.",
    teleexp: "Demande d'avis spécialisé asynchrone, dossiers partagés via MSSanté, sans présence patient.",
    telesurv: "Suivi continu des patients chroniques avec dispositifs connectés (BLE, BP, SpO₂) et alertes automatiques.",
    webmail: "Messagerie sécurisée MSSanté intégrée nativement, sans logiciel tiers à installer.",
  }[sid] || "";
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-6">
      <div className="w-11 h-11 rounded-xl grid place-items-center" style={{ backgroundColor: "rgba(15,23,42,0.06)" }}>
        <Icon className="w-5 h-5" style={{ color: "var(--bp)" }} />
      </div>
      <div className="mt-3 text-[16.5px] font-semibold text-slate-900">{title}</div>
      <p className="mt-1.5 text-[13.5px] text-slate-600 leading-relaxed">{desc}</p>
    </div>
  );
}

/* ============================ LOGIN ============================ */
function Login({ brand, onBack, onAuth, sandboxEffects }) {
  const cpxWarn = !!sandboxEffects?.cpxWarn;
  return (
    <div className="min-h-[calc(100vh-30px)] grid lg:grid-cols-2 bg-white">
      {/* Left brand panel */}
      <div className="hidden lg:flex flex-col justify-center px-12 py-12 text-white relative overflow-hidden" style={{ background: `linear-gradient(135deg, var(--bp) 0%, color-mix(in srgb, var(--bp) 60%, #0F172A) 100%)` }}>
        <button onClick={onBack} className="absolute top-6 left-6 inline-flex items-center gap-1 text-[12px] text-white/80 hover:text-white">
          <ArrowLeft className="w-4 h-4" /> Retour
        </button>
        <div className="mt-12">
          {brand.logoUrl ? (
            <img src={brand.logoUrl} alt={brand.name || ""} className="h-12 max-w-[280px] object-contain brightness-0 invert opacity-95" />
          ) : (
            <div className="text-[26px] font-bold tracking-tight text-white">{brand.name || "Votre structure"}</div>
          )}
          <div className="mt-8 text-[36px] leading-tight font-bold text-white">Bienvenue sur<br />{brand.name || "votre plateforme"}</div>
          <p className="mt-4 text-[14px] text-white/85 max-w-md leading-relaxed">
            Une plateforme dédiée aux professionnels de santé — conforme aux référentiels ANS, hébergée HDS en France.
          </p>

          {/* Features list — donne de la densité au panneau gauche */}
          <ul className="mt-8 space-y-2.5 max-w-md">
            {[
              { t: "Authentification Pro Santé Connect", d: "Carte CPx / e-CPS · sans mot de passe" },
              { t: "Dossier patient interopérable INS", d: "Qualification RNIV · INSi intégré" },
              { t: "Ordonnances CDA R2 · dépôt DMP", d: "e-Prescription unifiée PECAN · MSSanté" },
              { t: "Téléconsultation HDS chiffrée bout-en-bout", d: "WebRTC · LiveKit · PGSSI-S" },
            ].map((f, i) => (
              <li key={i} className="flex items-start gap-2.5">
                <div className="mt-0.5 w-5 h-5 rounded-full grid place-items-center shrink-0" style={{ background: "rgba(255,255,255,0.18)" }}>
                  <CheckCircle2 className="w-3 h-3 text-white" />
                </div>
                <div>
                  <div className="text-[13.5px] font-semibold text-white leading-snug">{f.t}</div>
                  <div className="text-[11.5px] text-white/75">{f.d}</div>
                </div>
              </li>
            ))}
          </ul>

          {/* ANS trust band */}
          <div className="mt-8 pt-6 border-t border-white/15">
            <div className="text-[10px] uppercase tracking-[0.18em] text-white/70 font-semibold mb-3">Conforme aux référentiels de l&apos;Agence du Numérique en Santé</div>
            <div className="flex flex-wrap items-center gap-x-2 gap-y-2">
              {[
                { src: "/logos/psc.png", alt: "Pro Santé Connect" },
                { src: "/logos/insi.png", alt: "INSi" },
                { src: "/logos/dmp.png", alt: "DMP" },
                { src: "/logos/mssante-official.png", alt: "MSSanté" },
                { src: "/logos/mon-espace-sante.png", alt: "Mon espace santé" },
                { src: "/logos/hds.png", alt: "HDS" },
              ].map((l) => (
                <div key={l.alt} className="h-11 px-3 rounded-md bg-white grid place-items-center shadow-sm" style={{ minWidth: 68 }}>
                  <img src={l.src} alt={l.alt} className="max-h-7 max-w-[80px] object-contain" onError={(e) => { e.currentTarget.replaceWith(Object.assign(document.createElement("span"), { className: "text-[10.5px] text-[#0F2B5B] font-bold", textContent: l.alt })); }} />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Right login panel */}
      <div className="flex items-center justify-center px-6 py-12 lg:py-0">
        <div className="w-full max-w-md">
          <button onClick={onBack} className="lg:hidden mb-6 inline-flex items-center gap-1 text-[12px] text-slate-600 hover:text-slate-900">
            <ArrowLeft className="w-4 h-4" /> Retour
          </button>

          {/* Brand identity — centered and prominent (premium look) */}
          <div className="text-center" data-testid="login-brand-block">
            <div className="inline-block relative">
              {brand.logoUrl ? (
                <img
                  src={brand.logoUrl}
                  alt={brand.name || ""}
                  className="h-24 sm:h-28 max-w-[300px] mx-auto object-contain drop-shadow-xl"
                  onError={(e) => { e.currentTarget.style.display = "none"; }}
                />
              ) : (
                <div
                  className="mx-auto w-24 h-24 sm:w-28 sm:h-28 rounded-3xl grid place-items-center text-white font-bold text-[38px] sm:text-[44px] shadow-[0_20px_50px_-14px_rgba(0,0,0,0.30)] ring-4 ring-white"
                  style={{ backgroundColor: "var(--bp)" }}
                >
                  {(brand.name || "MC").split(" ").map((x) => x[0]).filter(Boolean).slice(0, 2).join("").toUpperCase()}
                </div>
              )}
            </div>
            <div className="mt-5 font-display text-[26px] sm:text-[28px] font-bold text-slate-900 tracking-tight">{brand.name || "Votre structure"}</div>
            <div className="mt-1 inline-flex items-center gap-1.5 text-[11.5px] text-slate-500 font-medium">
              <ShieldCheck className="w-3 h-3" style={{ color: "var(--bp)" }} /> Plateforme de e-santé sécurisée · HDS · RGPD
            </div>
          </div>

          <h1 className="mt-10 text-2xl font-semibold text-slate-900 text-center">Connectez-vous</h1>
          <p className="mt-1.5 text-[13.5px] text-slate-600 text-center">Sélectionnez votre mode d&apos;accès pour rejoindre la plateforme.</p>

          {/* PSC card */}
          <div className="mt-8 rounded-2xl border border-slate-200 p-6 bg-slate-50">
            <div className="text-center">
              <div className="text-[11px] uppercase tracking-[0.16em] font-semibold text-slate-500">Professionnels</div>
              <div className="mt-2 text-[18px] font-semibold text-slate-900">Je suis professionnel</div>
              <p className="mt-1.5 text-[12.5px] text-slate-600">Authentifiez-vous avec Pro Santé Connect pour accéder à vos services.</p>
              <button
                onClick={onAuth}
                className={`mt-5 inline-flex items-center justify-center bg-white border-2 rounded-lg px-4 py-2.5 hover:shadow-md transition-all relative ${
                  cpxWarn
                    ? "border-amber-400 hover:border-amber-500"
                    : "border-slate-300 hover:border-slate-400"
                }`}
                data-testid="psc-login-btn"
              >
                <img src="/logos/psc.png" alt="S'identifier avec Pro Santé Connect"
                  className="h-11 object-contain"
                  onError={(e) => {
                    e.currentTarget.replaceWith(Object.assign(document.createElement("div"), {
                      className: "px-3 py-1 text-sm font-bold",
                      innerHTML: '<span style="color:#0055A4">PRO SANTÉ</span> <span style="color:#E1000F">CONNECT</span>'
                    }));
                  }}
                />
                {cpxWarn && (
                  <span
                    className="absolute -top-2 -right-2 inline-flex items-center gap-1 text-[9.5px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-amber-500 text-white font-bold shadow-md"
                    data-testid="psc-cpx-warn-badge"
                    title="Vos PS n'ont pas encore de carte CPx — la connexion par Pro Santé Connect nécessite une carte ou e-CPS active."
                  >
                    <AlertTriangle className="w-2.5 h-2.5" /> CPx à acquérir
                  </span>
                )}
              </button>
              {cpxWarn && (
                <p className="mt-2 text-[11px] text-amber-800 leading-snug">
                  D&apos;après votre configuration, vos professionnels de santé doivent encore acquérir leur carte CPx
                  ou activer leur e-CPS pour utiliser Pro Santé Connect.
                </p>
              )}
              <p className="mt-3 text-[11px] text-amber-700 bg-amber-100 rounded px-2 py-1 inline-block">⚠️ Démo sandbox — cliquez pour simuler une connexion</p>
            </div>
          </div>

          {/* OR separator */}
          <div className="my-6 flex items-center gap-3 text-[11px] uppercase tracking-[0.16em] text-slate-400">
            <div className="flex-1 h-px bg-slate-200" />OU<div className="flex-1 h-px bg-slate-200" />
          </div>

          {/* Patient card */}
          <div className="rounded-2xl border border-slate-200 p-6 bg-white">
            <div className="text-center">
              <div className="text-[11px] uppercase tracking-[0.16em] font-semibold text-slate-500">Patients</div>
              <div className="mt-2 text-[18px] font-semibold text-slate-900">Je suis patient</div>
              <p className="mt-1.5 text-[12.5px] text-slate-600">Connectez-vous via Mon espace santé ou FranceConnect+ pour accéder à votre dossier.</p>
              <div className="mt-5 grid grid-cols-2 gap-2">
                <button
                  onClick={onAuth}
                  className="bg-white border border-slate-300 rounded-lg p-2 hover:border-slate-400 hover:shadow-sm transition-all"
                  data-testid="mes-login-btn"
                >
                  <img src="/logos/mon-espace-sante.png" alt="Mon espace santé" className="h-7 mx-auto object-contain" />
                  <div className="mt-1 text-[10.5px] text-slate-500">Mon espace santé</div>
                </button>
                <button
                  onClick={onAuth}
                  className="bg-[#0053B3] hover:bg-[#003F8A] text-white rounded-lg p-2 transition-colors"
                  data-testid="fc-login-btn"
                >
                  <div className="text-[10px] leading-tight">S&apos;identifier avec</div>
                  <div className="text-[14px] font-bold">FranceConnect <span className="bg-white text-[#0053B3] rounded-sm px-1">+</span></div>
                </button>
              </div>
              <div className="mt-4 text-[11px] text-slate-500">
                <button className="underline hover:text-slate-700">Qu&apos;est-ce que FranceConnect+ ?</button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ============================ DASHBOARD SHELL ============================ */
function DashboardShell({ brand, services, view, setView, users, setUsers, onLogout, platformHost, rdvUrl, sandboxEffects = null }) {
  const [proPatientView, setProPatientView] = useState("pro"); // 'pro' | 'patient'
  // ---------- Accordion-based sidebar navigation (5 groups) ----------
  const NAV_GROUPS = [
    {
      id: "patients", label: "Patients", icon: Users,
      items: [
        { id: "dossiers", label: "Dossiers patients", icon: Folder, always: true },
        { id: "consentements", label: "Consentements", icon: CheckCircle2, always: true },
        { id: "rosp", label: "Suivi ROSP", icon: ClipboardList, always: true },
        { id: "annuaire", label: "Annuaire santé", icon: BookOpen, always: true },
      ],
    },
    {
      id: "agenda", label: "Agenda & consultations", icon: CalendarDays,
      items: [
        { id: "agenda", label: "Calendrier", icon: CalendarDays, always: true },
        { id: "telecons", label: "Téléconsultation", icon: Video, when: hasS("telecons", services) },
        { id: "teleexp", label: "Téléexpertise", icon: Headphones, when: hasS("teleexp", services) },
        { id: "telesurv", label: "Télésurveillance", icon: Activity, when: hasS("telesurv", services) },
        { id: "telesoin-quota", label: "Quota télésoin & contrats", icon: Gauge, always: true, isNew: true },
      ],
    },
    {
      id: "documents", label: "Documents & échanges", icon: FileText,
      items: [
        { id: "comptes-rendus", label: "Comptes-rendus", icon: FileText, always: true },
        { id: "ordonnances", label: "Ordonnances", icon: FileText, always: true },
        { id: "dmp", label: "Consulter un DMP", icon: Folder, always: true },
        { id: "webmail", label: "Messagerie MSS", icon: MailCheck, when: hasS("webmail", services) },
      ],
    },
    {
      id: "reseau-group", label: "Réseau", icon: Compass,
      items: [
        { id: "messagerie-instant", label: "Messagerie instantanée", icon: MessageSquare, always: true, unread: 3 },
        { id: "reseau", label: "Réseau de proximité", icon: Compass, always: true },
        { id: "materiel", label: "Mon matériel", icon: Wifi, when: !!sandboxEffects?.assistedEnabled, conditional: true },
      ],
    },
    {
      id: "admin", label: "Administration", icon: Settings,
      items: [
        { id: "structure", label: "Établissement", icon: BookOpen, always: true },
        { id: "parametrage", label: "Paramétrage", icon: Sliders, always: true },
        { id: "users", label: "Utilisateurs", icon: Users, always: true },
        { id: "facturation-pro", label: "Facturation & comptable", icon: Receipt, always: true, isNew: true },
        { id: "assistance", label: "Assistance", icon: Phone, always: true },
      ],
    },
  ];

  // Filter items by visibility — keep only groups with at least 1 visible item.
  const visibleGroups = NAV_GROUPS.map((g) => ({
    ...g,
    items: g.items.filter((it) => it.always || it.when),
  })).filter((g) => g.items.length > 0);

  // Sum unread badges per group (shown next to the group label when collapsed).
  const groupUnread = (g) => g.items.reduce((s, it) => s + (it.unread || 0), 0);

  // Active item / group detection.
  const isActiveItem = (id) => view === id && proPatientView === "pro";
  const activeGroupId = visibleGroups.find((g) => g.items.some((it) => isActiveItem(it.id)))?.id || null;

  // Open-state per group — initially open the group that contains the active page.
  const [openGroup, setOpenGroup] = useState(activeGroupId || visibleGroups[0]?.id || null);
  useEffect(() => { if (activeGroupId) setOpenGroup(activeGroupId); }, [activeGroupId]);

  // Force patient booking view if toggled
  const effectiveView = proPatientView === "patient" ? "patient-booking" : view;

  return (
    <div className="flex min-h-[calc(100vh-30px)] bg-slate-50">
      {/* Sidebar */}
      <aside className="w-64 shrink-0 text-white flex flex-col" style={{ background: `color-mix(in srgb, var(--bp) 15%, #0F172A)` }}>
        <div className="px-5 py-5 border-b border-white/10">
          <BrandLogoLight brand={brand} />
        </div>
        <nav className="flex-1 px-3 py-3 space-y-1 overflow-y-auto">
          {/* Dashboard — standalone (not in any group) */}
          <button
            onClick={() => { setProPatientView("pro"); setView("dashboard"); }}
            data-testid="nav-dashboard"
            className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-[13px] text-left transition-colors ${isActiveItem("dashboard") ? "bg-white/12 text-white font-semibold" : "text-white/85 hover:text-white hover:bg-white/6"}`}
          >
            <LayoutDashboard className="w-4 h-4" />
            <span className="flex-1">Tableau de bord</span>
          </button>

          {/* Accordion groups */}
          {visibleGroups.map((g) => {
            const isOpen = openGroup === g.id;
            const hasActive = g.items.some((it) => isActiveItem(it.id));
            const unreadSum = groupUnread(g);
            const GI = g.icon;
            return (
              <div key={g.id} className="mt-1.5">
                <button
                  onClick={() => setOpenGroup(isOpen ? null : g.id)}
                  data-testid={`nav-group-${g.id}`}
                  aria-expanded={isOpen}
                  className={`w-full flex items-center gap-2.5 px-3 py-1.5 rounded-md text-[10.5px] uppercase tracking-[0.14em] font-bold text-left transition-colors ${
                    isOpen || hasActive ? "text-white" : "text-white/55 hover:text-white/80"
                  }`}
                >
                  <GI className={`w-3.5 h-3.5 ${isOpen || hasActive ? "text-[var(--bp)]" : ""}`} style={(isOpen || hasActive) ? { color: "#7FB3FF" } : {}} />
                  <span className="flex-1">{g.label}</span>
                  {!isOpen && unreadSum > 0 && (
                    <span
                      data-testid={`nav-group-${g.id}-unread`}
                      className="inline-flex items-center justify-center min-w-[16px] h-[16px] px-1 text-[9px] font-bold rounded-full bg-red-500 text-white"
                    >
                      {unreadSum}
                    </span>
                  )}
                  <ChevronRight
                    className={`w-3 h-3 transition-transform duration-150 ${isOpen ? "rotate-90" : ""}`}
                  />
                </button>
                <div
                  className="overflow-hidden transition-[max-height] duration-150 ease-out"
                  style={{ maxHeight: isOpen ? `${g.items.length * 38 + 4}px` : "0px" }}
                >
                  <div className="pl-2 mt-1 space-y-0.5 border-l border-white/10 ml-3">
                    {g.items.map((it) => {
                      const active = isActiveItem(it.id);
                      const II = it.icon;
                      return (
                        <button
                          key={it.id}
                          onClick={() => { setProPatientView("pro"); setView(it.id); }}
                          data-testid={`nav-${it.id}`}
                          className={`w-full flex items-center gap-2.5 px-3 py-2 ml-1 rounded-md text-[12.5px] text-left transition-colors ${
                            active ? "bg-white/15 text-white font-semibold" : "text-white/75 hover:text-white hover:bg-white/6"
                          }`}
                        >
                          <II className="w-3.5 h-3.5" />
                          <span className="flex-1">{it.label}</span>
                          {it.isNew && (
                            <span
                              data-testid={`nav-${it.id}-new`}
                              className="inline-flex items-center px-1.5 py-px text-[8.5px] font-bold uppercase tracking-wider rounded-full bg-amber-400 text-slate-900"
                            >
                              Nouveau
                            </span>
                          )}
                          {it.unread > 0 && (
                            <span
                              data-testid={`nav-${it.id}-unread`}
                              className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[10px] font-bold rounded-full bg-red-500 text-white"
                            >
                              {it.unread}
                            </span>
                          )}
                          {it.conditional && (
                            <span
                              className="inline-flex items-center text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-emerald-400/85 text-emerald-950 font-bold"
                              title="Activé via votre configuration téléconsultation assistée"
                            >
                              Nouveau
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            );
          })}
        </nav>

        {/* Conformity — single discreet line with hover tooltip */}
        <div className="px-3 py-3 border-t border-white/10">
          <div
            className="group relative flex items-center gap-2 px-2 py-1.5 rounded-md text-[11px] text-white/70 hover:text-white hover:bg-white/5 cursor-help"
            title="ANS · HAS · INS · HDS · PGSSI-S · CI-SIS · CNDA"
            data-testid="sidebar-conformity"
          >
            <Lock className="w-3.5 h-3.5" />
            <span className="flex-1">Conformité ANS</span>
            <Info className="w-3 h-3 text-white/45 group-hover:text-white/80" />
            <div className="hidden group-hover:block absolute bottom-full left-2 right-2 mb-1 px-2.5 py-1.5 rounded-md bg-slate-900 border border-white/15 text-white text-[10.5px] leading-snug shadow-lg z-10">
              ANS · HAS · INS · HDS · PGSSI-S · CI-SIS · CNDA
            </div>
          </div>
        </div>

        <div className="px-3 py-3 border-t border-white/10 space-y-1">
          <button onClick={() => setView("settings")} className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-[12px] text-white/75 hover:text-white hover:bg-white/6 transition-colors" data-testid="nav-settings">
            <Settings className="w-4 h-4" /> Paramètres
          </button>
          <button onClick={onLogout} className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-[12px] text-white/75 hover:text-white hover:bg-white/6 transition-colors" data-testid="logout-btn">
            <LogOut className="w-4 h-4" /> Se déconnecter
          </button>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 min-w-0 flex flex-col">
        {/* Top bar with Pro/Patient toggle */}
        <header className="h-14 bg-white border-b border-slate-200 px-6 flex items-center justify-between gap-4">
          <div className="text-[13px] text-slate-500 truncate">
            <span className="font-mono">{platformHost}</span>
            <span className="mx-2">·</span>
            <span className="capitalize">{proPatientView === "patient" ? "vue patient" : view.replace("-", " ")}</span>
          </div>
          <div className="flex items-center gap-3">
            {/* Pro/Patient toggle */}
            <div className="flex bg-slate-100 rounded-md p-0.5" data-testid="pro-patient-toggle">
              <button onClick={() => setProPatientView("pro")} className={`px-3 py-1 text-[12px] rounded transition-colors ${proPatientView === "pro" ? "bg-white text-slate-900 font-semibold shadow-sm" : "text-slate-500 hover:text-slate-700"}`} data-testid="view-pro-btn">
                Vue Pro
              </button>
              <button onClick={() => setProPatientView("patient")} className={`px-3 py-1 text-[12px] rounded transition-colors ${proPatientView === "patient" ? "bg-white text-slate-900 font-semibold shadow-sm" : "text-slate-500 hover:text-slate-700"}`} data-testid="view-patient-btn">
                Vue Patient
              </button>
            </div>
            <button className="relative w-9 h-9 grid place-items-center rounded-full hover:bg-slate-100 transition-colors">
              <Bell className="w-4 h-4 text-slate-600" />
              <span className="absolute top-2 right-2 w-2 h-2 rounded-full bg-red-500" />
            </button>
            <button
              onClick={() => { setProPatientView("pro"); setView("messagerie-instant"); }}
              data-testid="topbar-im-button"
              title="Messagerie instantanée Inter-PS"
              className="relative w-9 h-9 grid place-items-center rounded-full hover:bg-slate-100 transition-colors"
            >
              <MessageSquare className="w-4 h-4 text-slate-600" />
              <span
                data-testid="topbar-im-badge"
                className="absolute -top-0.5 -right-0.5 min-w-[16px] h-[16px] px-1 grid place-items-center text-[10px] font-bold rounded-full bg-red-500 text-white"
              >
                3
              </span>
            </button>
            <div className="flex items-center gap-2 px-2 py-1 rounded-md hover:bg-slate-100 transition-colors cursor-pointer">
              <div className="w-7 h-7 rounded-full grid place-items-center text-white font-semibold text-[12px]" style={{ backgroundColor: "var(--bp)" }}>MD</div>
              <div className="text-[12.5px] text-slate-700 hidden sm:block">Dr Praticien</div>
              <ChevronDown className="w-3.5 h-3.5 text-slate-400" />
            </div>
          </div>
        </header>

        {/* Page */}
        <main className="flex-1 p-6 lg:p-8 overflow-x-hidden">
          {effectiveView === "patient-booking" && <PagePatientBooking brand={brand} platformHost={platformHost} services={services} />}
          {effectiveView === "dashboard" && <PageDashboard brand={brand} services={services} users={users} setView={setView} rdvUrl={rdvUrl} sandboxEffects={sandboxEffects} />}
          {effectiveView === "agenda" && <PageAgenda brand={brand} sandboxEffects={sandboxEffects} />}
          {effectiveView === "webmail" && hasS("webmail", services) && <PageWebmail brand={brand} sandboxEffects={sandboxEffects} />}
          {effectiveView === "messagerie-instant" && <PageMessagerieInstantanee brand={brand} setView={setView} />}
          {effectiveView === "structure" && <PageStructure brand={brand} platformHost={platformHost} rdvUrl={rdvUrl} />}
          {effectiveView === "parametrage" && <PageParametrage />}
          {effectiveView === "users" && <PageUsers users={users} setUsers={setUsers} />}
          {effectiveView === "telecons" && hasS("telecons", services) && <PageTelecons brand={brand} sandboxEffects={sandboxEffects} />}
          {effectiveView === "teleexp" && hasS("teleexp", services) && <PageTeleexp brand={brand} />}
          {effectiveView === "telesurv" && hasS("telesurv", services) && <PageTelesurv brand={brand} />}
          {effectiveView === "telesoin-quota" && <SandboxTelesoin brand={brand} />}
          {effectiveView === "facturation-pro" && <SandboxFacturation brand={brand} />}
          {effectiveView === "dmp" && <PageDMP brand={brand} />}
          {effectiveView === "annuaire" && <PageAnnuaire brand={brand} />}
          {effectiveView === "comptes-rendus" && <PageDocs kind="cr" />}
          {effectiveView === "ordonnances" && <PageOrdonnances />}
          {effectiveView === "consentements" && <PageDocs kind="cons" />}
          {effectiveView === "dossiers" && <PageDossiers sandboxEffects={sandboxEffects} />}
          {effectiveView === "rosp" && <PageROSP setView={setView} />}
          {effectiveView === "materiel" && <PageMateriel sandboxEffects={sandboxEffects} />}
          {effectiveView === "reseau" && <PageReseau brand={brand} />}
          {effectiveView === "assistance" && <PageAssistance brand={brand} />}
          {effectiveView === "settings" && <PageSettings brand={brand} />}
        </main>
        {/* Footer conformité — référentiels ANS / HAS / HDS */}
        <footer className="border-t border-slate-200 bg-white px-6 lg:px-8 py-3 flex flex-wrap items-center justify-between gap-x-6 gap-y-1.5 text-[10.5px] text-slate-500">
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
            <span className="flex items-center gap-1 font-semibold"><ShieldCheck className="w-3 h-3 text-emerald-500" />Conformité</span>
            <span>ANS · Référentiel fonctionnel télesoin</span>
            <span>HAS · BPT 2021</span>
            <span>INS v2.1</span>
            <span>HDS · agréé</span>
            <span>PGSSI-S</span>
            <span>RGPD</span>
            <span>CI-SIS / CDA R2</span>
            <span>CNDA (Phase 1)</span>
          </div>
          <div className="text-slate-400">Plateforme propulsée par <span className="font-semibold text-slate-600">MetaConnect Studio</span></div>
        </footer>
      </div>
    </div>
  );
}
const hasS = (id, services) => services.some((s) => s.id === id);

function BrandLogoLight({ brand }) {
  const initials = (brand.name || "MC").split(" ").map((x) => x[0]).filter(Boolean).slice(0, 2).join("").toUpperCase();
  return (
    <div className="flex items-center gap-2.5">
      {brand.logoUrl ? (
        <img src={brand.logoUrl} alt={brand.name || ""} className="h-8 max-w-[160px] object-contain brightness-0 invert" onError={(e) => { e.currentTarget.style.display = "none"; }} />
      ) : (
        <div className="w-8 h-8 rounded grid place-items-center text-white font-bold text-[12.5px] border border-white/30" style={{ backgroundColor: "rgba(255,255,255,0.10)" }}>
          {initials}
        </div>
      )}
      <div className="font-semibold text-white text-[14px] truncate">{brand.name || "Votre structure"}</div>
    </div>
  );
}

/* ============================ SUB-PAGES ============================ */


function PageDashboard({ brand, services, users, setView, rdvUrl, sandboxEffects }) {
  const showTeam = !!sandboxEffects?.showTeamBlock;
  const primaryStructure = sandboxEffects?.primaryStructure || "";
  const welcomeSubtitle = primaryStructure
    ? `${primaryStructure} — voici un aperçu de votre activité aujourd'hui.`
    : "Voici un aperçu de votre activité aujourd'hui.";
  // Adapt KPIs based on the team size answer (showTeamBlock = 51+ PS)
  const kpis = showTeam
    ? [
        { label: "Patients suivis", value: "3 248", trend: "+182 ce mois" },
        { label: "RDV cette semaine", value: "412", trend: "+58 vs S-1" },
        { label: "Téléconsultations", value: "186", trend: "+24 ce mois" },
        { label: "PS actifs", value: users.length > 50 ? `${users.length}+` : "62", trend: "Équipe pleine capacité" },
      ]
    : [
        { label: "Patients suivis", value: "248", trend: "+12 ce mois" },
        { label: "RDV cette semaine", value: "37", trend: "+5 vs S-1" },
        { label: "Téléconsultations", value: "21", trend: "+3 ce mois" },
        { label: "Documents MSS", value: "94", trend: "Aujourd'hui : 6" },
      ];
  return (
    <div>
      <PageHeader
        title={`Bienvenue chez ${brand.name || "votre structure"}`}
        subtitle={welcomeSubtitle}
      />
      {showTeam && (
        <Card className="p-5 mb-5 border-l-4" style={{ borderLeftColor: "var(--bp)" }} data-testid="dashboard-team-block">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex-1 min-w-0">
              <div className="text-[11px] uppercase tracking-[0.16em] text-slate-500 font-semibold">Équipe étendue</div>
              <h3 className="text-[18px] font-semibold text-slate-900 mt-1">Gestion d&apos;équipe activée</h3>
              <p className="text-[12.5px] text-slate-600 mt-1 leading-relaxed">
                Votre structure compte plus de 50 PS &mdash; la console multi-praticiens, les agendas partagés et la gestion fine des droits sont automatiquement activés.
              </p>
            </div>
            <div className="flex -space-x-2">
              {["AB", "JM", "SD", "PL", "+58"].map((init, i) => (
                <div
                  key={i}
                  className={`h-9 w-9 rounded-full grid place-items-center text-[11px] font-semibold border-2 border-white ${
                    i === 4 ? "bg-slate-700 text-white" : "text-white"
                  }`}
                  style={i < 4 ? { backgroundColor: "var(--bp)" } : {}}
                >
                  {init}
                </div>
              ))}
            </div>
          </div>
        </Card>
      )}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {kpis.map((k) => (
          <Card key={k.label} className="p-5">
            <div className="text-[11px] uppercase tracking-[0.14em] text-slate-500 font-semibold">{k.label}</div>
            <div className="mt-2 text-[28px] font-semibold text-slate-900">{k.value}</div>
            <div className="mt-0.5 text-[12px]" style={{ color: "var(--bp)" }}>{k.trend}</div>
          </Card>
        ))}
      </div>

      {/* Messagerie instantanée — quick access block (Inter-PS, non HDS) */}
      <button
        type="button"
        onClick={() => setView("messagerie-instant")}
        data-testid="dashboard-im-card"
        className="w-full mt-4 group text-left"
      >
        <Card className="p-5 border-l-4 hover:shadow-md transition-shadow" style={{ borderLeftColor: "var(--bp)" }}>
          <div className="flex items-center gap-4 flex-wrap">
            <div className="grid place-items-center h-11 w-11 rounded-lg shrink-0" style={{ backgroundColor: "color-mix(in srgb, var(--bp) 12%, white)", color: "var(--bp)" }}>
              <MessageSquare className="w-5 h-5" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <div className="text-[14.5px] font-semibold text-slate-900">Messagerie instantanée</div>
                <span className="inline-flex items-center text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 font-bold">Inter-PS</span>
                <span className="inline-flex items-center text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">Temps réel</span>
              </div>
              <div className="text-[12.5px] text-slate-600 mt-0.5">
                <span className="font-semibold text-red-600">3 messages non lus</span>
                {" "}— Dr Petit, Dr Lefèvre, Dr Garcia · Discutez avec vos confrères en quelques secondes.
              </div>
            </div>
            <span className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold px-3.5 py-2 rounded-md text-white shrink-0" style={{ backgroundColor: "var(--bp)" }}>
              Ouvrir <ArrowRight className="w-3.5 h-3.5" />
            </span>
          </div>
        </Card>
      </button>

      <div className="mt-6 grid lg:grid-cols-3 gap-5">
        <Card className="p-5 lg:col-span-2">
          <div className="flex items-center justify-between mb-3">
            <div className="font-semibold text-slate-900">Prochains rendez-vous</div>
            <button onClick={() => setView("agenda")} className="text-[12px] hover:underline" style={{ color: "var(--bp)" }}>Voir tout</button>
          </div>
          <div className="divide-y divide-slate-100">
            {[
              { t: "09:00", who: "Marie Lefèvre · 64 ans", motif: "Suivi diabète · Téléconsultation" },
              { t: "10:30", who: "Paul Garcia · 47 ans", motif: "Consultation présentielle" },
              { t: "14:00", who: "Aïcha Benali · 38 ans", motif: "Téléconsultation · Renouvellement ordo" },
              { t: "16:15", who: "Léon Petit · 71 ans", motif: "Bilan annuel" },
            ].map((r, i) => (
              <div key={i} className="flex items-center gap-4 py-3">
                <div className="w-14 text-[14px] font-semibold" style={{ color: "var(--bp)" }}>{r.t}</div>
                <div className="flex-1">
                  <div className="text-[13.5px] font-medium text-slate-900">{r.who}</div>
                  <div className="text-[12px] text-slate-500">{r.motif}</div>
                </div>
                <button className="text-[12px] px-3 py-1.5 rounded border border-slate-200 hover:bg-slate-50">Ouvrir</button>
              </div>
            ))}
          </div>
        </Card>

        <Card className="p-5">
          <div className="font-semibold text-slate-900 mb-3">Actions rapides</div>
          <div className="space-y-2">
            <button onClick={() => setView("dmp")} className="w-full flex items-center justify-between px-3 py-2.5 rounded-lg border border-slate-200 hover:bg-slate-50 text-left">
              <span className="text-[13px] font-medium text-slate-800">Consulter un DMP</span><ChevronRight className="w-4 h-4 text-slate-400" />
            </button>
            {hasS("telecons", services) && (
              <button onClick={() => setView("telecons")} className="w-full flex items-center justify-between px-3 py-2.5 rounded-lg border border-slate-200 hover:bg-slate-50 text-left">
                <span className="text-[13px] font-medium text-slate-800">Démarrer une téléconsultation</span><ChevronRight className="w-4 h-4 text-slate-400" />
              </button>
            )}
            {hasS("webmail", services) && (
              <button onClick={() => setView("webmail")} className="w-full flex items-center justify-between px-3 py-2.5 rounded-lg border border-slate-200 hover:bg-slate-50 text-left">
                <span className="text-[13px] font-medium text-slate-800">Ouvrir la messagerie MSS</span><ChevronRight className="w-4 h-4 text-slate-400" />
              </button>
            )}
            <a href={rdvUrl} target="_blank" rel="noopener noreferrer" className="w-full flex items-center justify-between px-3 py-2.5 rounded-lg border border-slate-200 hover:bg-slate-50 text-left">
              <span className="text-[13px] font-medium text-slate-800">Lien public RDV patient</span><ExternalLink className="w-4 h-4 text-slate-400" />
            </a>
          </div>

          <div className="mt-5 pt-4 border-t border-slate-100">
            <div className="text-[11px] uppercase tracking-[0.14em] text-slate-500 font-semibold mb-2">Équipe</div>
            <div className="space-y-1.5">
              {users.slice(0, 3).map((u) => (
                <div key={u.id} className="flex items-center gap-2.5">
                  <div className="w-7 h-7 rounded-full grid place-items-center text-white text-[11px] font-semibold" style={{ backgroundColor: "var(--bp)" }}>
                    {u.first[0]}{u.last[0]}
                  </div>
                  <div className="text-[12.5px] text-slate-700">{u.first} {u.last} · <span className="text-slate-400">{u.role}</span></div>
                </div>
              ))}
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}

/* ============================ Multi-Agendas (PS + Ressources) ============================ */
const SEED_AGENDAS = [
  { id: "ag-dr1", name: "Dr Praticien", kind: "ps", rpps: "10100123456", color: "#5B7CFF", visible: true, role: "Médecin généraliste" },
  { id: "ag-dr2", name: "Dr A. Muela-Garcia", kind: "ps", rpps: "10100456789", color: "#10B981", visible: true, role: "Médecin généraliste" },
  { id: "ag-dr3", name: "Dr H. Dupont", kind: "ps", rpps: "10100789012", color: "#F59E0B", visible: true, role: "Cardiologue" },
  { id: "ag-inf1", name: "Mme S. Bernard (IDEL)", kind: "ps", rpps: "10003456789", color: "#A855F7", visible: false, role: "Infirmier libéral" },
  { id: "ag-box1", name: "Box télémédecine n°1", kind: "room", color: "#0EA5E9", visible: true, role: "Équipement partagé" },
  { id: "ag-box2", name: "Salle d'examen B", kind: "room", color: "#EC4899", visible: false, role: "Salle d'examen" },
];

const SEED_AGENDA_EVENTS = [
  { agId: "ag-dr1", d: 0, h: 1, dur: 1, t: "M. Lefèvre — Suivi" },
  { agId: "ag-dr1", d: 3, h: 4, dur: 1, t: "P. Garcia" },
  { agId: "ag-dr2", d: 1, h: 2, dur: 2, t: "Téléconsult. A. Benali" },
  { agId: "ag-dr2", d: 2, h: 0, dur: 1, t: "Bilan annuel L. Petit" },
  { agId: "ag-dr3", d: 4, h: 3, dur: 1, t: "ECG repos — M. Dubois" },
  { agId: "ag-dr3", d: 0, h: 5, dur: 2, t: "Échographie cardiaque" },
  { agId: "ag-box1", d: 1, h: 4, dur: 1, t: "Téléexpertise dermato" },
  { agId: "ag-box1", d: 3, h: 1, dur: 2, t: "Session télésurveillance" },
];

function PageAgenda({ brand, sandboxEffects }) {
  void brand;
  const agendaSyncable = !!sandboxEffects?.agendaSyncable;
  const hours = Array.from({ length: 9 }, (_, i) => `${8 + i}:00`);
  const days = ["Lun 30", "Mar 31", "Mer 01", "Jeu 02", "Ven 03"];
  const [agendas, setAgendas] = useState(SEED_AGENDAS);
  const [view, setView] = useState("super"); // super | side
  const visibleAg = agendas.filter((a) => a.visible);
  const eventsToShow = SEED_AGENDA_EVENTS.filter((e) => visibleAg.some((a) => a.id === e.agId));
  const toggle = (id) => setAgendas(agendas.map((a) => a.id === id ? { ...a, visible: !a.visible } : a));

  return (
    <div>
      <PageHeader
        title="Agenda"
        subtitle={`Semaine du 30 juin — ${visibleAg.length} agenda(s) affiché(s) sur ${agendas.length}`}
        action={
          <div className="flex items-center gap-2">
            <div className="inline-flex rounded-lg border border-slate-200 bg-white p-0.5 shadow-sm">
              <button onClick={() => setView("super")} className={`px-2.5 py-1 text-[11.5px] font-medium rounded-md ${view === "super" ? "text-white" : "text-slate-500"}`} style={view === "super" ? { backgroundColor: "var(--bp)" } : {}}>Superposé</button>
              <button onClick={() => setView("side")} className={`px-2.5 py-1 text-[11.5px] font-medium rounded-md ${view === "side" ? "text-white" : "text-slate-500"}`} style={view === "side" ? { backgroundColor: "var(--bp)" } : {}}>Côte à côte</button>
            </div>
            <Button className="text-white" style={{ backgroundColor: "var(--bp)" }} data-testid="agenda-new-event"><Plus className="w-4 h-4 mr-1" /> Nouveau RDV</Button>
          </div>
        }
      />

      {/* Chips agendas */}
      <div className="mb-4 flex flex-wrap gap-2 items-center">
        {agendaSyncable && (
          <span
            className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-wider px-2.5 py-1 rounded-full bg-emerald-100 text-emerald-800 border border-emerald-300 font-semibold mr-1"
            data-testid="agenda-syncable-badge"
            title="Votre agenda actuel est compatible avec une synchronisation bidirectionnelle MetaConnect ↔ Doctolib/Maiia"
          >
            <RefreshCw className="w-3 h-3" /> Synchronisation disponible
          </span>
        )}
        {agendas.map((a) => (
          <button
            key={a.id}
            onClick={() => toggle(a.id)}
            className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full border text-[12px] font-medium transition-all ${a.visible ? "border-slate-300 bg-white text-slate-900 shadow-sm" : "border-slate-200 bg-slate-50 text-slate-400 line-through"}`}
            data-testid={`agenda-chip-${a.id}`}
          >
            <span className={`w-2.5 h-2.5 rounded-full ${a.visible ? "" : "opacity-40"}`} style={{ backgroundColor: a.color }} />
            {a.kind === "ps" ? <User className="w-3 h-3" /> : <Folder className="w-3 h-3" />}
            {a.name}
          </button>
        ))}
      </div>

      {view === "super" ? (
        <Card className="p-5 overflow-x-auto">
          <div className="min-w-[820px]">
            <div className="grid grid-cols-[80px_repeat(5,1fr)] gap-px bg-slate-200">
              <div className="bg-white" />
              {days.map((d) => <div key={d} className="bg-white p-2 text-[12px] font-semibold text-slate-700 text-center">{d}</div>)}
              {hours.map((h, hi) => (
                <React.Fragment key={h}>
                  <div className="bg-white p-2 text-[11px] text-slate-400 text-right pr-3 border-r border-slate-100">{h}</div>
                  {days.map((d, di) => {
                    const ev = eventsToShow.find((e) => e.d === di && e.h === hi);
                    const ag = ev ? agendas.find((a) => a.id === ev.agId) : null;
                    return (
                      <div key={d} className="bg-white relative h-14 border-t border-slate-50">
                        {ev && ag && (
                          <div className="absolute inset-x-1 top-1 rounded-md px-2 py-1 text-[11px] text-white truncate shadow-sm" style={{ backgroundColor: ag.color, height: `${ev.dur * 3.5 - 0.5}rem` }}>
                            <div className="font-semibold truncate leading-tight">{ev.t}</div>
                            <div className="text-[10px] opacity-80 truncate">{ag.name}</div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </React.Fragment>
              ))}
            </div>
          </div>
        </Card>
      ) : (
        <div className="grid gap-4" style={{ gridTemplateColumns: `repeat(${Math.max(1, visibleAg.length)}, minmax(280px, 1fr))` }}>
          {visibleAg.map((a) => (
            <Card key={a.id} className="p-4">
              <div className="flex items-center gap-2 mb-3 pb-2 border-b border-slate-200">
                <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: a.color }} />
                <div className="text-[13px] font-semibold text-slate-900">{a.name}</div>
                <div className="text-[10.5px] text-slate-500 ml-auto">{a.role}</div>
              </div>
              <div className="space-y-1.5">
                {eventsToShow.filter((e) => e.agId === a.id).map((e, i) => (
                  <div key={i} className="px-2 py-1.5 rounded-md text-[11.5px] text-white" style={{ backgroundColor: a.color }}>
                    <span className="font-semibold">{days[e.d].slice(0, 3)} {hours[e.h]}</span> · {e.t}
                  </div>
                ))}
                {eventsToShow.filter((e) => e.agId === a.id).length === 0 && (
                  <div className="text-[12px] text-slate-400 italic">Aucun RDV cette semaine</div>
                )}
              </div>
            </Card>
          ))}
          {visibleAg.length === 0 && <div className="col-span-full text-center text-[13px] text-slate-400 py-8">Activez un agenda pour afficher ses créneaux.</div>}
        </div>
      )}
    </div>
  );
}

function AgendasPanel() {
  const KINDS = [
    { id: "ps", label: "Professionnel de santé", icon: User },
    { id: "room", label: "Salle / box / équipement", icon: Folder },
  ];
  const PALETTE = ["#5B7CFF", "#10B981", "#F59E0B", "#A855F7", "#0EA5E9", "#EC4899", "#EF4444", "#84CC16", "#F97316", "#6366F1"];

  const [agendas, setAgendas] = useState(SEED_AGENDAS);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", kind: "ps", rpps: "", role: "", color: PALETTE[0] });

  const create = () => {
    if (!form.name.trim()) return toast.error("Indiquez un nom d'agenda.");
    if (form.kind === "ps" && !/^\d{11}$/.test(form.rpps)) return toast.error("RPPS attendu : 11 chiffres (référentiel RPPS ANS).");
    const id = `ag-${Date.now()}`;
    setAgendas([...agendas, { id, ...form, visible: true }]);
    toast.success(`Agenda « ${form.name} » créé. ${form.kind === "ps" ? "Synchronisation annuaire santé ANS en cours…" : "Ressource disponible à la réservation."}`);
    setOpen(false);
    setForm({ name: "", kind: "ps", rpps: "", role: "", color: PALETTE[(agendas.length + 1) % PALETTE.length] });
  };
  const remove = (id) => { setAgendas(agendas.filter((a) => a.id !== id)); toast.success("Agenda supprimé."); };
  const toggleVis = (id) => setAgendas(agendas.map((a) => a.id === id ? { ...a, visible: !a.visible } : a));

  const ps = agendas.filter((a) => a.kind === "ps");
  const rooms = agendas.filter((a) => a.kind === "room");

  return (
    <Card className="p-6">
      <div className="flex items-start justify-between gap-4 mb-5">
        <div>
          <div className="text-[15px] font-semibold text-slate-900">Agendas multiples — Professionnels & Ressources</div>
          <p className="text-[12.5px] text-slate-500 mt-0.5">Un agenda dédié par PS rattaché à l&apos;établissement et par ressource matérielle (box télémédecine, salles d&apos;examen, équipements). Synchronisation annuaire santé ANS via le RPPS.</p>
        </div>
        <Button onClick={() => setOpen(true)} className="text-white shrink-0" style={{ backgroundColor: "var(--bp)" }} data-testid="agendas-add-btn">
          <Plus className="w-4 h-4 mr-1" /> Nouvel agenda
        </Button>
      </div>

      {/* Stats résumées */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        <StatTile label="Agendas PS" value={ps.length} icon={User} accent="var(--bp)" />
        <StatTile label="Ressources" value={rooms.length} icon={Folder} accent="#0EA5E9" />
        <StatTile label="Actifs cette semaine" value={agendas.filter((a) => a.visible).length} icon={CalendarDays} accent="#10B981" />
        <StatTile label="Taux occupation moyen" value="68 %" icon={Activity} accent="#F59E0B" />
      </div>

      {/* PS Section */}
      <div className="mb-6">
        <div className="flex items-center gap-2 mb-2">
          <User className="w-4 h-4 text-slate-500" />
          <div className="text-[12px] uppercase tracking-[0.14em] font-semibold text-slate-500">Agendas des professionnels de santé</div>
          <Badge className="bg-slate-100 text-slate-600 border-0 ml-auto">{ps.length}</Badge>
        </div>
        <div className="space-y-2">
          {ps.map((a) => (
            <AgendaRow key={a.id} a={a} onToggle={() => toggleVis(a.id)} onRemove={() => remove(a.id)} />
          ))}
          {ps.length === 0 && <div className="text-center text-[12.5px] text-slate-400 py-6">Aucun PS rattaché. Ajoutez votre premier agenda.</div>}
        </div>
      </div>

      {/* Ressources Section */}
      <div>
        <div className="flex items-center gap-2 mb-2">
          <Folder className="w-4 h-4 text-slate-500" />
          <div className="text-[12px] uppercase tracking-[0.14em] font-semibold text-slate-500">Ressources matérielles partagées</div>
          <Badge className="bg-slate-100 text-slate-600 border-0 ml-auto">{rooms.length}</Badge>
        </div>
        <div className="space-y-2">
          {rooms.map((a) => (
            <AgendaRow key={a.id} a={a} onToggle={() => toggleVis(a.id)} onRemove={() => remove(a.id)} />
          ))}
          {rooms.length === 0 && <div className="text-center text-[12.5px] text-slate-400 py-6">Aucune ressource. Box télémédecine, salles, équipements…</div>}
        </div>
      </div>

      {/* Modal nouvel agenda */}
      {open && (
        <div className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-sm grid place-items-center p-4" onClick={() => setOpen(false)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="text-[16px] font-bold text-slate-900">Nouvel agenda</div>
                <p className="text-[12px] text-slate-500 mt-0.5">PS rattaché ou ressource matérielle</p>
              </div>
              <button onClick={() => setOpen(false)} className="text-slate-400 hover:text-slate-700"><X className="w-5 h-5" /></button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold block mb-1.5">Type</label>
                <div className="grid grid-cols-2 gap-2">
                  {KINDS.map((k) => {
                    const I = k.icon;
                    return (
                      <button key={k.id} onClick={() => setForm({ ...form, kind: k.id })} className={`p-3 rounded-lg border text-left transition-all ${form.kind === k.id ? "border-slate-900 bg-slate-50" : "border-slate-200 hover:border-slate-400"}`} data-testid={`agendas-kind-${k.id}`}>
                        <I className="w-4 h-4 text-slate-600 mb-1" />
                        <div className="text-[12px] font-semibold text-slate-900">{k.label}</div>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div>
                <label className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold block mb-1.5">{form.kind === "ps" ? "Nom du praticien" : "Nom de la ressource"}</label>
                <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full border border-slate-300 rounded-md px-3 py-2 text-[13px] text-slate-900 bg-white" placeholder={form.kind === "ps" ? "Dr Henri Dupont" : "Box télémédecine n°2"} data-testid="agendas-name" />
              </div>

              {form.kind === "ps" && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold block mb-1.5">RPPS</label>
                    <input value={form.rpps} onChange={(e) => setForm({ ...form, rpps: e.target.value.replace(/\D/g, "").slice(0, 11) })} className="w-full border border-slate-300 rounded-md px-3 py-2 text-[13px] text-slate-900 bg-white font-mono" placeholder="11 chiffres" data-testid="agendas-rpps" />
                  </div>
                  <div>
                    <label className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold block mb-1.5">Spécialité</label>
                    <input value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="w-full border border-slate-300 rounded-md px-3 py-2 text-[13px] text-slate-900 bg-white" placeholder="Ex : Médecin généraliste" />
                  </div>
                </div>
              )}
              {form.kind === "room" && (
                <div>
                  <label className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold block mb-1.5">Catégorie</label>
                  <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="w-full border border-slate-300 rounded-md px-3 py-2 text-[13px] text-slate-900 bg-white font-medium">
                    <option value="">— Choisir —</option>
                    <option value="Box de téléconsultation">Box de téléconsultation</option>
                    <option value="Salle d'examen">Salle d&apos;examen</option>
                    <option value="Salle de soins">Salle de soins</option>
                    <option value="Équipement partagé">Équipement partagé (ECG, écho…)</option>
                  </select>
                </div>
              )}

              <div>
                <label className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold block mb-1.5">Couleur de l&apos;agenda</label>
                <div className="flex flex-wrap gap-2">
                  {PALETTE.map((c) => (
                    <button key={c} onClick={() => setForm({ ...form, color: c })} className={`w-8 h-8 rounded-full border-2 transition-all ${form.color === c ? "border-slate-900 scale-110" : "border-white"}`} style={{ backgroundColor: c, boxShadow: "0 0 0 1px rgba(0,0,0,0.08)" }} data-testid={`agendas-color-${c}`} />
                  ))}
                </div>
              </div>

              {form.kind === "ps" && (
                <div className="rounded-lg bg-blue-50 border border-blue-200 p-3 text-[11.5px] text-blue-900 flex gap-2">
                  <Info className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                  <div>À la création, l&apos;agenda sera synchronisé avec l&apos;<strong>annuaire santé ANS</strong> via le RPPS. Le praticien recevra une invitation par email pour activer son accès via Pro Santé Connect.</div>
                </div>
              )}
            </div>

            <div className="mt-5 flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setOpen(false)} className="border-slate-300" data-testid="agendas-cancel">Annuler</Button>
              <Button onClick={create} className="text-white" style={{ backgroundColor: "var(--bp)" }} data-testid="agendas-create">Créer l&apos;agenda</Button>
            </div>
          </div>
        </div>
      )}
    </Card>
  );
}

function AgendaRow({ a, onToggle, onRemove }) {
  return (
    <div className="flex items-center gap-3 p-3 rounded-lg border border-slate-200 hover:bg-slate-50 transition-colors">
      <span className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: a.color }} />
      <div className="flex-1 min-w-0">
        <div className="text-[13.5px] font-semibold text-slate-900 truncate">{a.name}</div>
        <div className="text-[11px] text-slate-500 truncate">
          {a.role}
          {a.rpps && <span className="font-mono"> · RPPS {a.rpps}</span>}
        </div>
      </div>
      <button onClick={onToggle} className={`text-[11px] font-medium px-2.5 py-1 rounded ${a.visible ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`} data-testid={`agenda-row-toggle-${a.id}`}>
        {a.visible ? <span className="flex items-center gap-1"><Eye className="w-3 h-3" /> Affiché</span> : <span className="flex items-center gap-1"><X className="w-3 h-3" /> Masqué</span>}
      </button>
      <button onClick={onRemove} className="text-slate-400 hover:text-red-500" data-testid={`agenda-row-remove-${a.id}`}><Trash2 className="w-4 h-4" /></button>
    </div>
  );
}

function StatTile({ label, value, icon: Icon, accent }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-3 flex items-center gap-3">
      <div className="w-8 h-8 rounded-md grid place-items-center" style={{ backgroundColor: `${accent}20` }}>
        <Icon className="w-4 h-4" style={{ color: accent }} />
      </div>
      <div className="min-w-0">
        <div className="text-[10.5px] uppercase tracking-[0.12em] text-slate-500 font-semibold truncate">{label}</div>
        <div className="text-[16px] font-bold text-slate-900">{value}</div>
      </div>
    </div>
  );
}

function PageWebmail({ brand, sandboxEffects }) {
  const mssMissing = !!sandboxEffects?.mssMissing;
  const [folder, setFolder] = useState("inbox");
  const [selected, setSelected] = useState(SEED_INBOX[0]);
  const [scope, setScope] = useState("objet");
  const [showFilters, setShowFilters] = useState(false);
  const FOLDERS_SYS = [
    { id: "inbox", label: "Boîte de réception", icon: MailCheck, n: SEED_INBOX.length, unread: SEED_INBOX.filter((m) => m.unread).length },
    { id: "drafts", label: "Brouillons", icon: FileText, n: 2 },
    { id: "spam", label: "Courrier indésirable", icon: X, n: 0 },
    { id: "sent", label: "Envoyés", icon: Send, n: 12 },
    { id: "trash", label: "Corbeille", icon: Trash2, n: 1 },
  ];
  return (
    <div className="min-h-[640px]">
      {mssMissing && (
        <div
          className="mb-4 rounded-lg border-l-4 border-amber-400 bg-amber-50 px-4 py-3 flex items-start gap-3"
          data-testid="webmail-mss-missing-warning"
        >
          <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
          <div className="flex-1">
            <div className="text-[13.5px] font-semibold text-amber-900">
              ⚠️ Aucune adresse MSSanté détectée
            </div>
            <p className="text-[12.5px] text-amber-800 mt-0.5 leading-relaxed">
              {`D'après votre configuration, vos professionnels de santé n'ont pas encore d'adresse MSSanté active.`}
              {` MetaConnect peut vous accompagner dans la création de votre annuaire et l'ouverture des boîtes par PS.`}
            </p>
            <button
              className="mt-2 inline-flex items-center gap-1.5 text-[12px] font-semibold text-amber-900 underline hover:text-amber-700"
            >
              Démarrer la création des adresses MSSanté
            </button>
          </div>
        </div>
      )}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2 text-[14px] font-semibold text-slate-700">
          <MailCheck className="w-5 h-5" style={{ color: "var(--bp)" }} /> Messagerie sécurisée de santé (MSSanté)
        </div>
        <div className="text-[11.5px] text-amber-700 bg-amber-50 border border-amber-200 px-3 py-1 rounded">Profil incomplet : 80 % complété. <button className="underline ml-1">Compléter mon profil</button></div>
      </div>
      <div className="mb-3 rounded-lg border border-blue-200 bg-blue-50 px-3.5 py-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-blue-900">
        <span className="flex items-center gap-1.5 font-semibold"><ShieldCheck className="w-3 h-3" /> Espace de confiance MSSanté · opérateur agréé ANS</span>
        <span>Auth. forte Pro Santé Connect (CIBA)</span>
        <span>Chiffrement TLS 1.3 + S/MIME au repos</span>
        <span>Compatible LRO MSSanté</span>
        <span>Annuaire santé ANS (RPPS / FINESS)</span>
      </div>
      <Card className="overflow-hidden grid lg:grid-cols-[240px_320px_1fr] divide-x divide-slate-200">
        {/* Folder sidebar */}
        <div className="bg-slate-50 p-3">
          <div className="relative mb-3">
            <Search className="absolute left-2.5 top-2.5 w-3.5 h-3.5 text-slate-400" />
            <input placeholder="Rechercher un dossier…" className="w-full text-[12.5px] pl-7 pr-2 py-2 rounded-md border border-slate-300 bg-white text-slate-900 placeholder:text-slate-400" />
          </div>
          <div className="text-[10.5px] uppercase tracking-[0.16em] text-slate-500 font-semibold mb-1.5 px-1">Dossiers système</div>
          <div className="space-y-0.5 mb-4">
            {FOLDERS_SYS.map((f) => {
              const I = f.icon;
              const active = folder === f.id;
              return (
                <button key={f.id} onClick={() => setFolder(f.id)} className={`w-full flex items-center gap-2 px-2 py-1.5 rounded text-[12.5px] transition-colors ${active ? "bg-white border-l-2 font-semibold text-slate-900" : "text-slate-700 hover:bg-white"}`} style={active ? { borderLeftColor: "var(--bp)" } : {}}>
                  <I className="w-3.5 h-3.5 shrink-0" />
                  <span className="flex-1 text-left">{f.label}</span>
                  {f.unread > 0 && <span className="text-[10px] font-bold px-1.5 rounded-full text-white" style={{ backgroundColor: "var(--bp)" }}>{f.unread}</span>}
                </button>
              );
            })}
          </div>
          <div className="text-[10.5px] uppercase tracking-[0.16em] text-slate-500 font-semibold mb-1.5 px-1">Dossiers utilisateur</div>
          <button className="w-full flex items-center gap-2 px-2 py-1.5 rounded text-[12.5px] text-slate-700 hover:bg-white">
            <Folder className="w-3.5 h-3.5" /> Suivi diabète
          </button>
          <button className="w-full flex items-center gap-2 px-2 py-1.5 rounded text-[12.5px] text-slate-500 hover:bg-white hover:text-slate-700">
            <Plus className="w-3.5 h-3.5" /> Nouveau dossier
          </button>
        </div>

        {/* Email list */}
        <div>
          <div className="px-4 pt-4 pb-3">
            <div className="text-[10px] uppercase tracking-[0.16em] text-slate-500 font-semibold">Messagerie MSS</div>
            <div className="text-[16px] font-semibold text-slate-900">Boîte de réception</div>
            <div className="text-[11px] text-slate-500 mt-1">Connecté en tant que <span className="font-mono text-slate-700">{`praticien@${(brand?.name || "votre-structure").toLowerCase().replace(/\s+/g, "")}.mssante.fr`}</span></div>
          </div>
          <div className="px-3 pb-2 flex items-center justify-between border-b border-slate-200">
            <div className="flex gap-1 text-[11.5px]">
              <button className="px-2 py-0.5 rounded bg-slate-900 text-white">Tous</button>
              <button className="px-2 py-0.5 rounded text-slate-600 hover:bg-slate-100">Non lus</button>
            </div>
            <div className="flex items-center gap-1">
              <button onClick={() => setShowFilters((v) => !v)} className="text-[11px] text-slate-600 underline">{showFilters ? "Masquer" : "Filtrer"}</button>
              <span className="text-[10.5px] text-slate-400">·</span>
              <span className="text-[10.5px] text-slate-400">64 messages</span>
            </div>
          </div>
          {showFilters && (
            <div className="px-3 py-3 bg-slate-50 border-b border-slate-200 space-y-2">
              <div className="flex items-center gap-1.5">
                <span className="text-[10.5px] uppercase tracking-wider text-slate-500 font-semibold w-14">Portée</span>
                <button onClick={() => setScope("objet")} className={`text-[11px] px-2 py-0.5 rounded ${scope === "objet" ? "bg-slate-900 text-white" : "bg-white border border-slate-300 text-slate-600"}`}>Objet</button>
                <button onClick={() => setScope("corps")} className={`text-[11px] px-2 py-0.5 rounded ${scope === "corps" ? "bg-slate-900 text-white" : "bg-white border border-slate-300 text-slate-600"}`}>Corps</button>
              </div>
              <input placeholder="ex : dr@example.fr" className="w-full text-[12px] px-2 py-1 rounded border border-slate-300 text-slate-900 placeholder:text-slate-400" />
              <input type="date" className="w-full text-[12px] px-2 py-1 rounded border border-slate-300 text-slate-900" />
            </div>
          )}
          <div className="divide-y divide-slate-100 overflow-y-auto" style={{ maxHeight: "520px" }}>
            {SEED_INBOX.map((m) => {
              const active = selected.id === m.id;
              const initials = m.from.split("@")[0].slice(0, 2).toUpperCase();
              return (
                <button key={m.id} onClick={() => setSelected(m)} className={`w-full text-left p-3 hover:bg-slate-50 flex gap-2 ${active ? "bg-slate-50 border-l-2" : ""}`} style={active ? { borderLeftColor: "var(--bp)" } : {}}>
                  <div className="w-8 h-8 rounded-full grid place-items-center text-[10.5px] font-semibold text-slate-700 bg-slate-200 shrink-0">{initials}</div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      {m.unread && <span className="w-2 h-2 rounded-full" style={{ backgroundColor: "var(--bp)" }} />}
                      <div className="text-[12.5px] font-semibold text-slate-900 truncate flex-1">{m.subject}</div>
                      <div className="text-[10.5px] text-slate-400 shrink-0">{m.date}</div>
                    </div>
                    <div className="text-[11px] text-slate-500 truncate font-mono">{m.from}</div>
                    <div className="text-[11.5px] text-slate-500 truncate mt-0.5">{m.preview}</div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Email body */}
        <div className="bg-white">
          <div className="px-6 pt-5 pb-3 border-b border-slate-200 flex items-start gap-2">
            <div className="flex-1">
              <div className="text-[17px] font-semibold text-slate-900 leading-snug">{selected.subject}</div>
              <div className="text-[11.5px] text-slate-500 mt-1">
                De : <span className="font-mono text-slate-700">{selected.from}</span>
                <span className="mx-1.5">·</span>
                À : <span className="font-mono text-slate-700">{`praticien@${(brand?.name || "votre-structure").toLowerCase().replace(/\s+/g, "")}.mssante.fr`}</span>
              </div>
              <div className="text-[10.5px] text-slate-400 font-mono mt-1 truncate">Message-ID: {selected.id}-cffcbbd-c50f-4499-ad54-5fc65d5b406b@diffusion.dgs-urgent.sante.gouv.fr</div>
            </div>
          </div>
          <div className="px-6 pt-3 pb-3 flex flex-wrap items-center gap-1.5 border-b border-slate-100">
            <Button className="text-white" style={{ backgroundColor: "var(--bp)" }} size="sm"><Send className="w-3 h-3 mr-1" />Répondre</Button>
            <Button variant="outline" size="sm">Répondre à tous</Button>
            <Button variant="outline" size="sm">Transférer</Button>
            <Button variant="outline" size="sm">Déplacer</Button>
            <Button variant="outline" size="sm" className="text-red-600 border-red-200 hover:bg-red-50">Supprimer</Button>
            <div className="ml-auto"><Button className="bg-blue-600 hover:bg-blue-700 text-white" size="sm">Télécharger .eml</Button></div>
          </div>
          <div className="px-6 py-6 overflow-y-auto" style={{ maxHeight: "440px" }}>
            <div className="bg-slate-50 border border-slate-200 rounded-md p-6">
              <div className="flex items-start gap-4 mb-4">
                <div className="text-[10px] font-bold leading-tight">
                  <div className="w-7 h-7 grid place-items-center mb-1">
                    <div className="grid grid-cols-3 w-7 h-5 gap-0">
                      <div className="bg-[#000091]" />
                      <div className="bg-white" />
                      <div className="bg-[#E1000F]" />
                    </div>
                  </div>
                  <div>RÉPUBLIQUE</div>
                  <div>FRANÇAISE</div>
                </div>
                <div className="text-[10px] uppercase font-bold leading-tight text-slate-900 mt-1">
                  <div>MINISTÈRE</div>
                  <div>DE LA SANTÉ, DES FAMILLES,</div>
                  <div>DE L&apos;AUTONOMIE</div>
                  <div>ET DES PERSONNES HANDICAPÉES</div>
                  <div className="mt-1.5 italic font-normal text-slate-500">Liberté · Égalité · Fraternité</div>
                </div>
              </div>
              <div className="bg-red-700 text-white px-4 py-3 text-center font-semibold tracking-wide">DGS-URGENT n°2026-08 · ANTICIPATION VAGUE DE CHALEUR</div>
              <div className="mt-4 text-[13px] text-slate-700 leading-relaxed">
                <p>Madame, Monsieur, Docteur,</p>
                <p className="mt-2">{selected.preview}</p>
                <p className="mt-2">Compte tenu du contexte météorologique annoncé pour les prochains jours, la Direction Générale de la Santé invite l&apos;ensemble des professionnels de santé à <b>renforcer la vigilance</b> auprès des populations vulnérables (personnes âgées, nourrissons, patients chroniques).</p>
                <p className="mt-2">Les recommandations détaillées sont disponibles en pièce jointe.</p>
                <p className="mt-3 italic text-slate-500">Cordialement,<br />Direction Générale de la Santé</p>
              </div>
              <div className="mt-4 inline-flex items-center gap-2 text-[12px] text-slate-600 bg-white border border-slate-200 rounded px-3 py-2">
                <Paperclip className="w-3.5 h-3.5" /> dgs-urgent-2026-08.pdf · 312 Ko
              </div>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
}

/* ----------- PageMessagerieInstantanee — sandbox mock (Inter-PS, non HDS) -- */
function PageMessagerieInstantanee({ brand, setView }) {
  const SEED_CONVS = [
    { id: "c1", name: "Dr Léon Petit", role: "Médecin généraliste", lastAt: "11:42", unread: 2, online: true, preview: "Bonjour, peux-tu jeter un œil sur le créneau de demain 14h ?" },
    { id: "c2", name: "Dr Marie Lefèvre", role: "Pédiatre", lastAt: "10:15", unread: 1, online: true, preview: "Le patient #2841 préfère une téléconsultation cette semaine." },
    { id: "c3", name: "Dr Paul Garcia", role: "Cardiologue", lastAt: "Hier", unread: 0, online: false, preview: "OK reçu, j'envoie le compte-rendu sur MSSanté." },
    { id: "c4", name: "Dr Aïcha Benali", role: "Endocrinologue", lastAt: "Lun.", unread: 0, online: true, preview: "Merci pour l'orientation 🙏" },
  ];
  const SEED_MSGS = {
    c1: [
      { id: "m1", mine: false, t: "11:38", body: "Salut, dispo pour échanger sur Mme Dupont ?" },
      { id: "m2", mine: true, t: "11:40", body: "Oui, vas-y." },
      { id: "m3", mine: false, t: "11:42", body: "Bonjour, peux-tu jeter un œil sur le créneau de demain 14h ?" },
    ],
    c2: [
      { id: "m4", mine: false, t: "10:15", body: "Le patient #2841 préfère une téléconsultation cette semaine." },
    ],
    c3: [
      { id: "m5", mine: true, t: "Hier", body: "Compte-rendu cardiologique prêt ?" },
      { id: "m6", mine: false, t: "Hier", body: "OK reçu, j'envoie le compte-rendu sur MSSanté." },
    ],
    c4: [
      { id: "m7", mine: false, t: "Lun.", body: "Merci pour l'orientation 🙏" },
    ],
  };
  const [activeId, setActiveId] = useState("c1");
  const [draft, setDraft] = useState("");
  const [hdsBlock, setHdsBlock] = useState(false);
  const [dnd, setDnd] = useState(false);
  const [convs, setConvs] = useState(SEED_CONVS);
  const [msgsByConv, setMsgsByConv] = useState(SEED_MSGS);

  const active = convs.find((c) => c.id === activeId) || convs[0];
  const messages = msgsByConv[active?.id] || [];

  const handleSend = (e) => {
    e?.preventDefault?.();
    const text = draft.trim();
    if (!text) return;
    const newMsg = { id: `mlocal-${Date.now()}`, mine: true, t: "à l'instant", body: text };
    setMsgsByConv((prev) => ({ ...prev, [active.id]: [...(prev[active.id] || []), newMsg] }));
    setConvs((prev) => prev.map((c) => c.id === active.id ? { ...c, preview: text, lastAt: "à l'instant", unread: 0 } : c));
    setDraft("");
  };

  const handleSelect = (cid) => {
    setActiveId(cid);
    setConvs((prev) => prev.map((c) => c.id === cid ? { ...c, unread: 0 } : c));
  };

  const handleAttachClick = (e) => {
    e.preventDefault();
    setHdsBlock(true);
    setTimeout(() => setHdsBlock(false), 6000);
  };

  return (
    <div>
      <PageHeader
        title="Messagerie instantanée"
        subtitle="Échangez en quelques secondes avec vos confrères — Inter-PS uniquement, sans données patient."
        action={
          <button
            onClick={() => setView && setView("webmail")}
            className="text-[12.5px] px-3 py-2 rounded-md border border-slate-200 hover:bg-slate-50 inline-flex items-center gap-1.5"
            data-testid="im-mock-open-mss"
          >
            <MailCheck className="w-3.5 h-3.5" /> Pour les documents : Messagerie MSSanté
          </button>
        }
      />

      <Card className="overflow-hidden">
        <div className="grid grid-cols-[280px_1fr] min-h-[540px] divide-x divide-slate-200">
          {/* Conversation list */}
          <aside className="flex flex-col bg-slate-50/50">
            <div className="p-3 border-b border-slate-200 flex items-center gap-2">
              <div className="text-[12px] font-semibold text-slate-900 flex-1 flex items-center gap-1.5">
                <MessageSquare className="w-3.5 h-3.5" style={{ color: "var(--bp)" }} /> Conversations
              </div>
              <button
                onClick={() => setDnd(!dnd)}
                aria-pressed={dnd}
                data-testid="im-mock-dnd"
                title={dnd ? "Ne pas déranger activé" : "Ne pas déranger"}
                className={`grid place-items-center h-7 w-7 rounded-md border text-[11px] transition-colors ${
                  dnd ? "border-amber-300 text-amber-700 bg-amber-50" : "border-slate-200 text-slate-500 hover:border-slate-300"
                }`}
              >
                <Bell className="w-3 h-3" />
              </button>
              <button
                data-testid="im-mock-compose"
                title="Nouvelle conversation"
                className="grid place-items-center h-7 w-7 rounded-md border border-emerald-300 bg-emerald-50 text-emerald-700"
              >
                <Plus className="w-3 h-3" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto">
              {convs.map((c) => {
                const isActive = c.id === active.id;
                return (
                  <button
                    key={c.id}
                    onClick={() => handleSelect(c.id)}
                    data-testid={`im-mock-conv-${c.id}`}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 text-left border-b border-slate-100 transition-colors ${
                      isActive ? "bg-white" : "hover:bg-white"
                    }`}
                  >
                    <div className="relative shrink-0">
                      <div className="h-9 w-9 rounded-full grid place-items-center text-white text-[11px] font-semibold" style={{ backgroundColor: "var(--bp)" }}>
                        {c.name.split(" ").map((p) => p[0]).slice(0, 2).join("")}
                      </div>
                      {c.online && (
                        <span className="absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full bg-emerald-500 border-2 border-white" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5">
                        <div className="text-[13px] font-semibold text-slate-900 truncate flex-1">{c.name}</div>
                        <div className="text-[10px] text-slate-400 shrink-0">{c.lastAt}</div>
                      </div>
                      <div className="flex items-center gap-1.5 mt-0.5">
                        <div className={`text-[11.5px] truncate flex-1 ${c.unread > 0 ? "text-slate-900 font-medium" : "text-slate-500"}`}>{c.preview}</div>
                        {c.unread > 0 && (
                          <span
                            data-testid={`im-mock-unread-${c.id}`}
                            className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[10px] font-bold rounded-full bg-red-500 text-white"
                          >
                            {c.unread}
                          </span>
                        )}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </aside>

          {/* Thread */}
          <section className="flex flex-col bg-white min-w-0">
            <div className="p-3 border-b border-slate-200 flex items-center gap-3">
              <div className="relative shrink-0">
                <div className="h-9 w-9 rounded-full grid place-items-center text-white text-[11px] font-semibold" style={{ backgroundColor: "var(--bp)" }}>
                  {active.name.split(" ").map((p) => p[0]).slice(0, 2).join("")}
                </div>
                {active.online && (
                  <span className="absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full bg-emerald-500 border-2 border-white" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[13.5px] font-semibold text-slate-900 truncate">{active.name}</div>
                <div className="text-[11px] text-slate-500 truncate">
                  {active.online ? "En ligne" : "Hors ligne"} · {active.role}
                </div>
              </div>
              <div className="text-[10.5px] uppercase tracking-wider px-2 py-1 rounded bg-amber-50 text-amber-700 font-semibold inline-flex items-center gap-1">
                <ShieldCheck className="w-3 h-3" /> Non HDS · Inter-PS
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-2 bg-slate-50/30" data-testid="im-mock-thread">
              {messages.map((m) => (
                <div key={m.id} className={`flex ${m.mine ? "justify-end" : "justify-start"}`}>
                  <div
                    className={`max-w-[78%] rounded-2xl px-3.5 py-2 text-[13px] leading-snug shadow-sm ${
                      m.mine ? "text-white rounded-br-sm" : "bg-white text-slate-900 rounded-bl-sm border border-slate-200"
                    }`}
                    style={m.mine ? { backgroundColor: "var(--bp)" } : {}}
                  >
                    <div className="whitespace-pre-wrap break-words">{m.body}</div>
                    <div className={`mt-0.5 text-[9.5px] flex items-center gap-1 ${m.mine ? "text-white/80 justify-end" : "text-slate-400"}`}>
                      <span>{m.t}</span>
                      {m.mine && <CheckCircle2 className="w-2.5 h-2.5 text-white/95" />}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {hdsBlock && (
              <div
                data-testid="im-mock-hds-banner"
                className="mx-4 mb-2 rounded-md border border-amber-300 bg-amber-50 text-amber-800 text-[12px] px-3 py-2"
              >
                Pour les documents médicaux, utilisez la Messagerie MSSanté
              </div>
            )}

            <form onSubmit={handleSend} className="border-t border-slate-200 p-3 flex items-end gap-2 bg-white">
              <button
                type="button"
                onClick={handleAttachClick}
                data-testid="im-mock-attach-blocked"
                title="Pour les documents médicaux, utilisez la Messagerie MSSanté"
                className="grid place-items-center h-9 w-9 rounded-md border border-slate-200 text-slate-500 hover:text-slate-700 hover:border-slate-300 shrink-0"
              >
                <Paperclip className="w-4 h-4" />
              </button>
              <textarea
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
                rows={1}
                placeholder="Écrivez un message…"
                data-testid="im-mock-input"
                className="flex-1 resize-none px-3 py-2 rounded-md bg-white border border-slate-300 text-slate-900 text-[13px] placeholder:text-slate-400 outline-none focus:border-slate-400 max-h-32"
              />
              <button
                type="submit"
                disabled={!draft.trim()}
                data-testid="im-mock-send"
                className="grid place-items-center h-9 w-9 rounded-md text-white disabled:opacity-40 disabled:cursor-not-allowed shrink-0"
                style={{ backgroundColor: "var(--bp)" }}
              >
                <Send className="w-4 h-4" />
              </button>
            </form>
          </section>
        </div>
      </Card>
    </div>
  );
}


function PageStructure({ brand, platformHost, rdvUrl }) {
  const [tab, setTab] = useState("edit");
  const [s, setS] = useState({
    name: brand.name || "",
    type: brand.structureType || "",
    address: "12 rue de l'Hôpital, 45000 Orléans",
    phone: "02 38 00 00 00",
    email: brand.contactEmail || `contact@${platformHost}`,
    description: `${brand.name || "Notre structure"} accueille les patients du lundi au samedi pour des consultations en présentiel et en téléconsultation.`,
    rdv: rdvUrl,
    seoKeywords: "médecin généraliste, téléconsultation, RDV en ligne, Orléans, médecine de famille",
    hours: "Lun-Ven : 8h-19h · Sam : 9h-12h · Téléconsultation 7j/7",
    specialties: "Médecine générale, suivi diabète, pédiatrie",
    showInDirectory: true,
    publicUrl: "https://annuaire.metaconnect.fr/totalav",
  });
  const SUB_TABS = [
    { id: "edit", label: "Éditer l'établissement" },
    { id: "pms", label: "PMS connecté" },
    { id: "services", label: "Gérer les services" },
    { id: "gallery", label: "Galerie" },
    { id: "faq", label: "Gestion de la FAQ" },
    { id: "reviews", label: "Avis clients" },
    { id: "employees", label: "Gérer les employés" },
    { id: "resources", label: "Agendas multiples (PS & ressources)" },
    { id: "schedule", label: "Gérer les horaires" },
    { id: "holidays", label: "Jours fériés" },
    { id: "stats", label: "Statistiques RDV" },
    { id: "invoices", label: "Mes factures" },
  ];
  return (
    <div>
      <PageHeader title="Fiche établissement" subtitle="Ces informations alimentent votre fiche vitrine publique et son référencement Google." />
      <div className="rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 mb-5 flex items-center justify-between gap-3">
        <div className="text-[12.5px] text-slate-700">
          Voir l&apos;affichage public : <a href={s.publicUrl} target="_blank" rel="noopener noreferrer" className="underline font-semibold text-blue-700">accéder à la page</a>
        </div>
        <button onClick={() => { navigator.clipboard?.writeText(s.publicUrl); toast.success("URL copiée."); }} className="text-[12px] px-3 py-1 rounded bg-blue-100 hover:bg-blue-200 text-blue-800 font-medium">Copier l&apos;URL</button>
      </div>
      <div className="grid lg:grid-cols-[220px_1fr] gap-5">
        {/* Sub-nav */}
        <aside className="space-y-0.5">
          {SUB_TABS.map((t) => {
            const active = tab === t.id;
            return (
              <button key={t.id} onClick={() => setTab(t.id)} className={`w-full text-left px-3 py-2 rounded text-[12.5px] transition-colors ${active ? "bg-slate-100 font-semibold text-slate-900" : "text-slate-600 hover:bg-slate-50"}`} data-testid={`struct-tab-${t.id}`}>
                {t.label}
              </button>
            );
          })}
        </aside>
        {/* Content */}
        <div>
          {tab === "edit" && (
            <div className="grid lg:grid-cols-2 gap-5">
              <Card className="p-6">
                <div className="text-[11px] uppercase tracking-[0.14em] font-semibold text-slate-500 mb-3">Informations générales</div>
                <div className="space-y-4">
                  {[
                    ["Nom de la structure", "name"],
                    ["Type d'établissement", "type"],
                    ["Adresse", "address"],
                    ["Téléphone", "phone"],
                    ["Email contact", "email"],
                    ["Horaires d'ouverture", "hours"],
                    ["Spécialités", "specialties"],
                  ].map(([lbl, k]) => (
                    <div key={k}>
                      <label className="text-[11px] uppercase tracking-[0.14em] text-slate-500 font-semibold block mb-1">{lbl}</label>
                      <input value={s[k]} onChange={(e) => setS({ ...s, [k]: e.target.value })} className="w-full border border-slate-300 rounded-md px-3 py-2 text-[13.5px] text-slate-900 placeholder:text-slate-400 focus:outline-none focus:border-slate-500" data-testid={`struct-${k}`} />
                    </div>
                  ))}
                </div>
              </Card>
              <Card className="p-6">
                <div className="text-[11px] uppercase tracking-[0.14em] font-semibold text-slate-500 mb-3">Présentation publique</div>
                <div className="flex items-center justify-between gap-3 rounded-lg border border-slate-200 p-3 mb-4">
                  <div>
                    <div className="text-[13px] font-semibold text-slate-900">Afficher dans l&apos;annuaire public</div>
                    <p className="text-[11.5px] text-slate-500">Contrôlez la présence de votre établissement sur le site public. Désactivé par défaut.</p>
                  </div>
                  <button onClick={() => setS({ ...s, showInDirectory: !s.showInDirectory })} className={`relative w-10 h-5 rounded-full transition-colors ${s.showInDirectory ? "" : "bg-slate-300"}`} style={s.showInDirectory ? { backgroundColor: "var(--bp)" } : {}}>
                    <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-all ${s.showInDirectory ? "left-5" : "left-0.5"}`} />
                  </button>
                </div>
                <div className="space-y-4">
                  <div>
                    <label className="text-[11px] uppercase tracking-[0.14em] text-slate-500 font-semibold block mb-1">Description publique</label>
                    <textarea rows={5} value={s.description} onChange={(e) => setS({ ...s, description: e.target.value })} className="w-full border border-slate-300 rounded-md px-3 py-2 text-[13.5px] text-slate-900 placeholder:text-slate-400 focus:outline-none focus:border-slate-500" />
                  </div>
                  <div>
                    <label className="text-[11px] uppercase tracking-[0.14em] text-slate-500 font-semibold block mb-1">Site internet</label>
                    <input placeholder="https://" className="w-full border border-slate-300 rounded-md px-3 py-2 text-[13.5px] text-slate-900 placeholder:text-slate-400" />
                  </div>
                  <div>
                    <label className="text-[11px] uppercase tracking-[0.14em] text-slate-500 font-semibold block mb-1">Lien de prise de rendez-vous</label>
                    <input value={s.rdv} onChange={(e) => setS({ ...s, rdv: e.target.value })} className="w-full border border-slate-300 rounded-md px-3 py-2 text-[13.5px] text-slate-900" />
                  </div>
                  <div>
                    <label className="text-[11px] uppercase tracking-[0.14em] text-slate-500 font-semibold block mb-1">Mots-clés SEO</label>
                    <input value={s.seoKeywords} onChange={(e) => setS({ ...s, seoKeywords: e.target.value })} className="w-full border border-slate-300 rounded-md px-3 py-2 text-[13.5px] text-slate-900" />
                    <div className="mt-2 flex flex-wrap gap-1">
                      {s.seoKeywords.split(",").map((kw, i) => kw.trim() && (
                        <span key={i} className="text-[10.5px] px-2 py-0.5 rounded bg-slate-100 text-slate-700">{kw.trim()}</span>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label className="text-[11px] uppercase tracking-[0.14em] text-slate-500 font-semibold block mb-1">Vidéo de présentation</label>
                    <input placeholder="URL YouTube / Vimeo" className="w-full border border-slate-300 rounded-md px-3 py-2 text-[13.5px] text-slate-900 placeholder:text-slate-400" />
                  </div>
                </div>
                <Button className="text-white mt-5" style={{ backgroundColor: "var(--bp)" }} onClick={() => toast.success("Modifications enregistrées (sandbox).")}>Enregistrer</Button>
              </Card>
            </div>
          )}
          {tab === "services" && <ServicesPanel />}
          {tab === "pms" && <PmsConnectionPanel />}
          {tab === "gallery" && <GalleryPanel />}
          {tab === "faq" && <FaqPanel />}
          {tab === "reviews" && <ReviewsPanel />}
          {tab === "employees" && <SoonPanel title="Gérer les employés" what="Onglet équivalent à 'Utilisateurs' — voir le menu principal." />}
          {tab === "resources" && <AgendasPanel />}
          {tab === "schedule" && <SoonPanel title="Gérer les horaires" what="Plages d'ouverture, créneaux d'urgence, exceptions." />}
          {tab === "holidays" && <SoonPanel title="Jours fériés" what="Calendrier officiel français + jours de fermeture exceptionnels." />}
          {tab === "stats" && <SoonPanel title="Statistiques RDV" what="Taux de remplissage, no-show, conversion téléconsult/présentiel." />}
          {tab === "invoices" && <SoonPanel title="Mes factures" what="Téléchargement des factures MetaConnect, gestion du moyen de paiement." />}
        </div>
      </div>
    </div>
  );
}

function SoonPanel({ title, what }) {
  return (
    <Card className="p-10 text-center">
      <Sparkles className="w-8 h-8 mx-auto mb-3" style={{ color: "var(--bp)" }} />
      <div className="text-[16px] font-semibold text-slate-900">{title}</div>
      <p className="mt-2 text-[13px] text-slate-600 max-w-md mx-auto">{what}</p>
      <div className="mt-4 inline-block text-[11px] px-2 py-0.5 rounded bg-amber-100 text-amber-700">Module inclus dans votre offre · paramétrage à la mise en ligne</div>
    </Card>
  );
}

function GalleryPanel() {
  const [photos, setPhotos] = useState([
    { id: "g1", caption: "Façade du cabinet" },
    { id: "g2", caption: "Salle d'attente" },
    { id: "g3", caption: "Salle de consultation" },
  ]);
  const onPick = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    for (const f of files) {
      if (f.size > 1_500_000) { toast.error(`${f.name} trop lourd (max 1.5 Mo).`); continue; }
      const r = new FileReader();
      r.onload = () => setPhotos((p) => [{ id: `g${Date.now()}`, caption: f.name.split(".")[0], dataUrl: String(r.result || "") }, ...p]);
      r.readAsDataURL(f);
    }
    e.target.value = "";
  };
  return (
    <Card className="p-6">
      <div className="flex items-start justify-between gap-3 mb-5">
        <div>
          <div className="text-[15px] font-semibold text-slate-900">Galerie photos</div>
          <p className="text-[12.5px] text-slate-500 mt-1">Affichez vos locaux et votre matériel sur votre fiche vitrine publique pour rassurer les patients.</p>
        </div>
        <label className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md text-white text-[13px] font-medium cursor-pointer" style={{ backgroundColor: "var(--bp)" }} data-testid="gallery-upload-label">
          <Plus className="w-4 h-4" /> Ajouter des photos
          <input type="file" accept="image/*" multiple className="hidden" onChange={onPick} data-testid="gallery-upload-input" />
        </label>
      </div>
      <div className="grid sm:grid-cols-3 gap-3">
        {photos.map((p) => (
          <div key={p.id} className="group relative rounded-lg overflow-hidden border border-slate-200 bg-slate-100 aspect-[4/3]">
            {p.dataUrl ? (
              <img src={p.dataUrl} alt={p.caption} className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full grid place-items-center text-slate-400">
                <div className="text-center">
                  <FileText className="w-6 h-6 mx-auto mb-1.5" />
                  <div className="text-[10.5px] uppercase tracking-wide">Aperçu</div>
                </div>
              </div>
            )}
            <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent px-2.5 py-1.5">
              <div className="text-[11.5px] text-white font-medium truncate">{p.caption}</div>
            </div>
            <button onClick={() => setPhotos(photos.filter((x) => x.id !== p.id))} className="absolute top-2 right-2 w-7 h-7 rounded-full bg-white/95 text-red-600 grid place-items-center opacity-0 group-hover:opacity-100 transition-opacity">
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        ))}
        {photos.length === 0 && (
          <div className="col-span-3 text-center py-8 text-slate-400 text-[13px]">Aucune photo. Cliquez sur « Ajouter des photos ».</div>
        )}
      </div>
      <p className="mt-4 text-[11.5px] text-slate-500">Formats acceptés : JPG, PNG, WebP · 1.5 Mo max par photo · Compression automatique côté serveur.</p>
    </Card>
  );
}

function FaqPanel() {
  const [items, setItems] = useState([
    { id: "f1", q: "Faut-il un téléphone ou un ordinateur pour la téléconsultation ?", a: "Les deux fonctionnent. Sur smartphone, ouvrez le lien envoyé par SMS. Sur ordinateur, le lien arrive par email. Aucune application à installer." },
    { id: "f2", q: "Combien de temps avant le RDV dois-je arriver ?", a: "Pour une consultation présentielle, présentez-vous 5 minutes avant. Pour une téléconsultation, connectez-vous 2 à 3 minutes avant pour vérifier votre caméra et votre micro." },
    { id: "f3", q: "Comment annuler ou décaler mon rendez-vous ?", a: "Depuis le lien de confirmation que vous avez reçu par email, cliquez sur « Modifier » jusqu'à 24 h avant le créneau." },
  ]);
  const [form, setForm] = useState({ q: "", a: "" });
  const add = () => {
    if (!form.q || !form.a) return toast.error("Question et réponse requises.");
    setItems([{ id: `f${Date.now()}`, ...form }, ...items]);
    setForm({ q: "", a: "" });
    toast.success("Question ajoutée.");
  };
  return (
    <Card className="p-6">
      <div className="text-[15px] font-semibold text-slate-900">Questions / réponses publiques</div>
      <p className="text-[12.5px] text-slate-500 mt-1 mb-5">Les FAQ apparaissent sur votre fiche vitrine et sont indexées par Google (rich snippet FAQPage) pour booster votre SEO local.</p>
      <div className="rounded-lg border border-slate-200 p-4 mb-5 bg-slate-50">
        <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-3">Ajouter une question</div>
        <input placeholder="Question…" value={form.q} onChange={(e) => setForm({ ...form, q: e.target.value })} className="w-full border border-slate-300 rounded-md px-3 py-2 text-[13.5px] text-slate-900 placeholder:text-slate-400 mb-2" data-testid="faq-new-q" />
        <textarea rows={3} placeholder="Réponse…" value={form.a} onChange={(e) => setForm({ ...form, a: e.target.value })} className="w-full border border-slate-300 rounded-md px-3 py-2 text-[13.5px] text-slate-900 placeholder:text-slate-400 mb-2" data-testid="faq-new-a" />
        <Button onClick={add} className="text-white" style={{ backgroundColor: "var(--bp)" }} data-testid="faq-add-btn"><Plus className="w-4 h-4 mr-1" />Ajouter</Button>
      </div>
      <div className="space-y-2">
        {items.map((it) => (
          <details key={it.id} className="group rounded-lg border border-slate-200 p-3 open:border-slate-400">
            <summary className="cursor-pointer list-none flex items-start gap-2">
              <div className="shrink-0 w-5 h-5 rounded-full grid place-items-center text-[11px] font-bold text-white group-open:rotate-45 transition-transform" style={{ backgroundColor: "var(--bp)" }}>+</div>
              <span className="text-[13.5px] font-semibold text-slate-900 flex-1">{it.q}</span>
              <button onClick={(e) => { e.preventDefault(); setItems(items.filter((x) => x.id !== it.id)); }} className="text-slate-400 hover:text-red-500"><Trash2 className="w-3.5 h-3.5" /></button>
            </summary>
            <p className="mt-2 pl-7 text-[12.5px] text-slate-600 leading-relaxed">{it.a}</p>
          </details>
        ))}
        {items.length === 0 && <div className="text-center py-6 text-slate-400 text-[13px]">Aucune question. Ajoutez la première ci-dessus.</div>}
      </div>
    </Card>
  );
}

function PageUsers({ users, setUsers }) {
  const [form, setForm] = useState({ first: "", last: "", role: "Médecin généraliste", rpps: "", email: "", roles: ["Salarié"] });
  const ROLE_TAGS = ["Gérant", "Salarié", "Manager", "Analyst"];
  const add = () => {
    if (!form.first || !form.last) return toast.error("Prénom et nom requis.");
    const u = { id: `u${Date.now()}`, phone: "", ...form };
    setUsers([u, ...users]);
    setForm({ first: "", last: "", role: "Médecin généraliste", rpps: "", email: "", roles: ["Salarié"] });
    toast.success(`${u.first} ${u.last} ajouté.`);
  };
  const toggleRole = (uid, tag) => {
    setUsers(users.map((u) => u.id === uid ? { ...u, roles: (u.roles || []).includes(tag) ? u.roles.filter((x) => x !== tag) : [...(u.roles || []), tag] } : u));
  };
  return (
    <div>
      <PageHeader title="Salariés & rôles" subtitle="Liste des utilisateurs rattachés à votre établissement avec leurs rôles (Spatie Teams)." />
      <Card className="p-5 mb-5">
        <div className="text-[11px] uppercase tracking-[0.14em] text-slate-500 font-semibold mb-3">Ajouter un salarié</div>
        <div className="grid sm:grid-cols-5 gap-2">
          <input placeholder="Prénom" value={form.first} onChange={(e) => setForm({ ...form, first: e.target.value })} className="border border-slate-300 rounded-md px-3 py-2 text-[13px] text-slate-900 placeholder:text-slate-400" data-testid="new-user-first" />
          <input placeholder="Nom" value={form.last} onChange={(e) => setForm({ ...form, last: e.target.value })} className="border border-slate-300 rounded-md px-3 py-2 text-[13px] text-slate-900 placeholder:text-slate-400" data-testid="new-user-last" />
          <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="border border-slate-300 rounded-md px-3 py-2 text-[13px] text-slate-900">
            <option>Médecin généraliste</option><option>Infirmier libéral</option><option>Sage-femme</option><option>Pharmacien</option><option>Kinésithérapeute</option><option>Pédiatre</option>
          </select>
          <input placeholder="RPPS" value={form.rpps} onChange={(e) => setForm({ ...form, rpps: e.target.value })} className="border border-slate-300 rounded-md px-3 py-2 text-[13px] text-slate-900 placeholder:text-slate-400" />
          <Button onClick={add} className="text-white" style={{ backgroundColor: "var(--bp)" }} data-testid="add-user-btn"><Plus className="w-3.5 h-3.5 mr-1" />Ajouter</Button>
        </div>
      </Card>
      <Card className="overflow-hidden">
        <table className="w-full text-[13px]">
          <thead className="bg-slate-50 text-slate-600 text-[11px] uppercase tracking-wider">
            <tr>
              <th className="text-left px-4 py-3">Employé</th>
              <th className="text-left px-4 py-3">Email</th>
              <th className="text-left px-4 py-3">Rôles</th>
              <th className="text-left px-4 py-3">Disponibilités</th>
              <th className="px-4 py-3 w-24 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {users.map((u) => {
              const userRoles = u.roles && u.roles.length ? u.roles : ["Salarié"];
              return (
                <tr key={u.id} className="hover:bg-slate-50">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-full grid place-items-center text-white text-[11.5px] font-semibold" style={{ backgroundColor: "var(--bp)" }}>{u.first[0]}{u.last[0]}</div>
                      <div>
                        <div className="font-semibold text-slate-900">{u.first} {u.last.toUpperCase()}</div>
                        <div className="text-[11px] text-slate-500">#{u.id.slice(0, 6)} · {u.role} · RPPS <span className="font-mono">{u.rpps || "—"}</span></div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-slate-700 text-[12.5px]">{u.email || "—"}</td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1">
                      {ROLE_TAGS.map((tag) => {
                        const active = userRoles.includes(tag);
                        return (
                          <button
                            key={tag}
                            onClick={() => toggleRole(u.id, tag)}
                            className={`text-[10.5px] px-2 py-0.5 rounded border transition-colors ${
                              active ? "border-slate-800 bg-slate-800 text-white" : "border-slate-300 text-slate-500 hover:border-slate-500"
                            }`}
                          >{tag}</button>
                        );
                      })}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <button onClick={() => toast.success("Disponibilités à définir (sandbox).")} className="text-[12px] px-3 py-1 rounded border border-slate-300 hover:bg-white">Gérer</button>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button onClick={() => { setUsers(users.filter((x) => x.id !== u.id)); toast.success("Supprimé."); }} className="text-slate-400 hover:text-red-600 transition-colors" data-testid={`del-user-${u.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              );
            })}
            {users.length === 0 && <tr><td colSpan="5" className="text-center py-8 text-slate-400">Aucun utilisateur. Ajoutez le premier ci-dessus.</td></tr>}
          </tbody>
        </table>
      </Card>
      <p className="mt-3 text-[11.5px] text-slate-500">La modification des rôles envoie une requête au back (auth + policies) qui encapsule en DB::transaction() et revalide avec Spatie Permissions (teams).</p>
    </div>
  );
}

function PageTelecons({ brand, sandboxEffects }) {
  const assistedEnabled = !!sandboxEffects?.assistedEnabled;
  const connectedDevices = sandboxEffects?.connectedDevices || [];
  const [inCall, setInCall] = useState(false);
  const [mode, setMode] = useState("pro"); // pro | patient
  const [bpt, setBpt] = useState({ clinique: false, capacite: false, nature: false, alternatives: false });
  const [consentReceived, setConsentReceived] = useState(false);
  const bptOk = Object.values(bpt).every(Boolean);
  const canStart = bptOk && consentReceived;
  if (mode === "patient") {
    return (
      <div>
        <div className="mb-4 flex items-center justify-between gap-3 flex-wrap">
          <div>
            <div className="text-[11px] uppercase tracking-[0.14em] font-semibold text-slate-500">Téléconsultation</div>
            <h1 className="text-[22px] font-bold text-slate-900">Aperçu — Vue patient</h1>
          </div>
          <div className="inline-flex rounded-lg border border-slate-200 bg-white p-0.5 shadow-sm">
            <button onClick={() => setMode("pro")} className="px-3 py-1.5 text-[12px] font-medium rounded-md text-slate-500 hover:bg-slate-50" data-testid="tele-mode-pro">Vue praticien</button>
            <button onClick={() => setMode("patient")} className="px-3 py-1.5 text-[12px] font-medium rounded-md text-white" style={{ backgroundColor: "var(--bp)" }} data-testid="tele-mode-patient">Vue patient</button>
          </div>
        </div>
        <PatientTeleconsFlow brand={brand} onExit={() => setMode("pro")} />
      </div>
    );
  }
  return (
    <div>
      <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
        <PageHeader title="Téléconsultation" subtitle="Salle d'attente virtuelle MetaConnect · LiveKit E2E" />
        <div className="flex items-center gap-2 flex-wrap">
          {assistedEnabled && (
            <span
              className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-wider px-2 py-1 rounded-full bg-emerald-100 text-emerald-800 border border-emerald-300 font-semibold"
              data-testid="telecons-assisted-badge"
              title="Téléconsultation assistée activée : un PS délégué est présent auprès du patient avec matériel connecté."
            >
              <Stethoscope className="w-3 h-3" /> Assistée disponible
            </span>
          )}
          <div className="inline-flex rounded-lg border border-slate-200 bg-white p-0.5 shadow-sm shrink-0">
            <button onClick={() => setMode("pro")} className="px-3 py-1.5 text-[12px] font-medium rounded-md text-white" style={{ backgroundColor: "var(--bp)" }} data-testid="tele-mode-pro">Vue praticien</button>
            <button onClick={() => setMode("patient")} className="px-3 py-1.5 text-[12px] font-medium rounded-md text-slate-500 hover:bg-slate-50" data-testid="tele-mode-patient">Vue patient</button>
          </div>
        </div>
      </div>
      {assistedEnabled && connectedDevices.length > 0 && (
        <Card className="p-4 mb-4 border-l-4" style={{ borderLeftColor: "var(--bp)" }} data-testid="telecons-devices-card">
          <div className="flex items-start gap-3">
            <div className="grid place-items-center h-9 w-9 rounded-lg bg-slate-100 text-slate-700 shrink-0">
              <Wifi className="w-4 h-4" />
            </div>
            <div className="flex-1">
              <div className="text-[11px] uppercase tracking-[0.14em] text-slate-500 font-semibold">Mon matériel connecté</div>
              <div className="text-[14px] font-semibold text-slate-900 mt-0.5">
                {connectedDevices.length} dispositif{connectedDevices.length > 1 ? "s" : ""} configuré{connectedDevices.length > 1 ? "s" : ""}
              </div>
              <div className="flex flex-wrap gap-1.5 mt-2">
                {connectedDevices.map((d) => (
                  <span
                    key={d}
                    className="text-[11.5px] px-2.5 py-1 rounded-full bg-white border border-slate-200 text-slate-700 inline-flex items-center gap-1.5"
                  >
                    <CheckCircle2 className="w-3 h-3" style={{ color: "var(--bp)" }} />
                    {d}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </Card>
      )}
      {!inCall ? (
        <div className="grid lg:grid-cols-[1.3fr,1fr] gap-5">
          <Card className="p-6">
            <div className="text-[11px] uppercase tracking-[0.14em] text-slate-500 font-semibold mb-3">Patients en attente · validation pré-acte</div>
            <div className="space-y-3">
              {SEED_PATIENTS.map((p, i) => (
                <div key={p.id} className="flex items-center gap-4 p-3 rounded-lg border border-slate-200">
                  <div className="w-10 h-10 rounded-full grid place-items-center text-white font-semibold" style={{ backgroundColor: "var(--bp)" }}>{p.first[0]}{p.last[0]}</div>
                  <div className="flex-1">
                    <div className="text-[13.5px] font-semibold text-slate-900 flex items-center gap-2">
                      {p.first} {p.last}
                      <span className="inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700"><CheckCircle2 className="w-3 h-3" /> INS qualifiée v2.1</span>
                    </div>
                    <div className="text-[11.5px] text-slate-500">INS : <span className="font-mono">{p.ins}</span> · {p.age} ans · Connecté depuis {2 + i} min</div>
                  </div>
                  <Button
                    onClick={() => { if (!canStart) { toast.error("Validez la checklist HAS BPT et le double consentement patient avant de démarrer."); return; } setInCall(true); }}
                    disabled={!canStart}
                    className="text-white disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{ backgroundColor: "var(--bp)" }}
                    data-testid={`start-call-${p.id}`}
                  >
                    <Video className="w-3.5 h-3.5 mr-1.5" /> Démarrer
                  </Button>
                </div>
              ))}
            </div>
          </Card>
          <Card className="p-6">
            {/* Checklist HAS BPT 2021 */}
            <div className="flex items-start justify-between gap-3 mb-3">
              <div>
                <div className="text-[11px] uppercase tracking-[0.14em] font-semibold text-slate-500">Pertinence du télesoin</div>
                <div className="font-semibold text-slate-900 text-[14px] mt-0.5">Checklist HAS BPT 2021 <span className="text-[11px] text-slate-500 font-medium">(art. L.6316-2 CSP)</span></div>
              </div>
              {bptOk ? <Badge className="bg-emerald-100 text-emerald-700 border-0">Validée</Badge> : <Badge className="bg-amber-100 text-amber-700 border-0">À valider</Badge>}
            </div>
            <div className="space-y-2 mb-4">
              {[
                ["clinique", "Situation clinique adaptée au télesoin"],
                ["capacite", "Capacité du patient (compréhension, autonomie, équipement)"],
                ["nature", "Nature du soin compatible avec un acte à distance"],
                ["alternatives", "Alternatives présentielles présentées et écartées avec justification"],
              ].map(([k, lbl]) => (
                <label key={k} className="flex items-start gap-2.5 cursor-pointer p-2 rounded hover:bg-slate-50">
                  <input
                    type="checkbox"
                    checked={bpt[k]}
                    onChange={(e) => setBpt({ ...bpt, [k]: e.target.checked })}
                    className="mt-0.5 w-4 h-4 rounded border-slate-300"
                    data-testid={`bpt-${k}`}
                  />
                  <span className="text-[12.5px] text-slate-700 leading-snug">{lbl}</span>
                </label>
              ))}
            </div>

            <div className="border-t border-slate-200 pt-4">
              <div className="text-[11px] uppercase tracking-[0.14em] font-semibold text-slate-500 mb-2">Double consentement patient (HAS)</div>
              <label className="flex items-start gap-2.5 cursor-pointer p-2 rounded hover:bg-slate-50">
                <input
                  type="checkbox"
                  checked={consentReceived}
                  onChange={(e) => setConsentReceived(e.target.checked)}
                  className="mt-0.5 w-4 h-4 rounded border-slate-300"
                  data-testid="bpt-consent-received"
                />
                <span className="text-[12.5px] text-slate-700 leading-snug">Consentements reçus et horodatés : <strong>acte de soin</strong> + <strong>modalités à distance (TIC)</strong></span>
              </label>
            </div>

            <div className="mt-4 rounded-lg bg-slate-50 border border-slate-200 p-3 text-[11.5px] text-slate-600 leading-relaxed">
              <div className="flex items-start gap-2"><ShieldCheck className="w-3.5 h-3.5 text-emerald-500 shrink-0 mt-0.5" />
                <div>
                  Chiffrement WebRTC E2E (LiveKit) · Enregistrement désactivé par défaut (REA.1.2 ANS) · Flux audio/vidéo <strong>non stocké</strong>.
                  <br />Documents et CR : HDS · CDA R2 CI-SIS · PGSSI-S · RGPD.
                </div>
              </div>
            </div>
          </Card>
        </div>
      ) : (
        <div className="rounded-2xl overflow-hidden border border-slate-200 bg-slate-900 aspect-video relative">
          <div className="absolute inset-0 grid place-items-center text-center text-white">
            <div>
              <div className="mx-auto w-20 h-20 rounded-full bg-white/10 grid place-items-center mb-4">
                <Video className="w-9 h-9 text-white" />
              </div>
              <div className="text-[18px] font-semibold">Téléconsultation en cours</div>
              <div className="mt-1 text-[13px] text-white/70">Marie Lefèvre · 64 ans · INS qualifiée v2.1</div>
              <div className="mt-1 text-[11px] text-white/50">00:42 · WebRTC E2E · LiveKit</div>
              <Button onClick={() => setInCall(false)} className="mt-5 bg-red-500 hover:bg-red-600" data-testid="end-call-btn">Raccrocher & rédiger le CR</Button>
              <button
                onClick={() => { setInCall(false); toast.success('Fin de téléconsultation — ouverture du transfert PMS (sandbox).'); }}
                data-testid="end-call-pms-btn"
                className="mt-3 mx-auto flex items-center gap-1.5 text-[12px] font-semibold px-3 py-1.5 rounded-md shadow-sm hover:shadow-md transition-all"
                style={{ backgroundColor: '#4FC3F7', color: '#ffffff' }}
              >
                <ArrowUpRight className="w-3.5 h-3.5" /> Envoyer au PMS après CR
              </button>
            </div>
          </div>
          <div className="absolute top-3 left-3 flex items-center gap-2 text-[10px] text-white/85">
            <span className="px-2 py-0.5 rounded bg-emerald-500/20 border border-emerald-300/30 flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Pertinence HAS validée</span>
            <span className="px-2 py-0.5 rounded bg-emerald-500/20 border border-emerald-300/30 flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Double consentement OK</span>
            <span className="px-2 py-0.5 rounded bg-white/10 border border-white/15">REF TLS REA.1.2 ANS</span>
          </div>
          <div className="absolute bottom-4 right-4 w-32 h-24 rounded-lg bg-slate-700 border-2 border-white/20" />
        </div>
      )}
    </div>
  );
}

function PageTeleexp({ brand }) {
  const items = [
    { id: 1, patient: "Marie Lefèvre", topic: "Avis cardiologique — ECG anormal", status: "En attente", from: "Dr Bernard" },
    { id: 2, patient: "Paul Garcia", topic: "Lecture dermatologique — naevus", status: "Répondu", from: "Dr Martin" },
    { id: 3, patient: "Aïcha Benali", topic: "Avis endocrinologique — bilan thyroïdien", status: "Brouillon", from: "Dr Dubois" },
  ];
  return (
    <div>
      <PageHeader title="Téléexpertise" subtitle="Demandes d'avis spécialisés asynchrones — facturable RPPS demandeur ↔ RPPS requis" action={
        <Button className="text-white" style={{ backgroundColor: "var(--bp)" }}><Plus className="w-4 h-4 mr-1" />Nouvelle demande</Button>
      } />
      <div className="mb-4 rounded-lg border border-blue-200 bg-blue-50 px-4 py-2.5 flex flex-wrap items-center gap-x-5 gap-y-1.5 text-[11.5px] text-blue-900">
        <span className="flex items-center gap-1.5 font-semibold"><Stethoscope className="w-3.5 h-3.5" /> Cadre Téléexpertise</span>
        <span><strong>CCL.1.7</strong> · Lettre-clé téléexpertise (NGAP)</span>
        <span>Avenants 9/10 convention médicale</span>
        <span>Plafond 4 actes / patient / an</span>
        <span>Compte-rendu CDA R2 envoyé au demandeur via MSSanté</span>
      </div>
      <Card className="overflow-hidden">
        <table className="w-full text-[13px]">
          <thead className="bg-slate-50 text-slate-500 text-[11px] uppercase tracking-wider">
            <tr><th className="text-left px-4 py-3">Patient</th><th className="text-left px-4 py-3">Demande</th><th className="text-left px-4 py-3">Demandeur</th><th className="text-left px-4 py-3">Statut</th></tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {items.map((i) => (
              <tr key={i.id} className="hover:bg-slate-50">
                <td className="px-4 py-3 font-medium text-slate-900">{i.patient}</td>
                <td className="px-4 py-3 text-slate-700">{i.topic}</td>
                <td className="px-4 py-3 text-slate-500">{i.from}</td>
                <td className="px-4 py-3">
                  <span className={`text-[11.5px] px-2 py-0.5 rounded ${
                    i.status === "Répondu" ? "bg-emerald-100 text-emerald-700" :
                    i.status === "En attente" ? "bg-amber-100 text-amber-700" :
                    "bg-slate-100 text-slate-600"
                  }`}>{i.status}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}

function PageTelesurv({ brand }) {
  const items = [
    { p: "Marie Lefèvre", dev: "Tensiomètre BLE", last: "Il y a 12 min", alert: "OK" },
    { p: "Paul Garcia", dev: "SpO₂ + Glycémie", last: "Il y a 1h", alert: "Glycémie élevée" },
    { p: "Aïcha Benali", dev: "Tensiomètre BLE", last: "Il y a 3h", alert: "OK" },
  ];
  return (
    <div>
      <PageHeader title="Télésurveillance" subtitle="Suivi continu des patients chroniques avec dispositifs connectés" />
      <div className="grid lg:grid-cols-3 gap-5">
        {items.map((it, i) => (
          <Card key={i} className="p-5">
            <div className="flex items-start justify-between">
              <div>
                <div className="font-semibold text-slate-900">{it.p}</div>
                <div className="text-[12px] text-slate-500">{it.dev}</div>
              </div>
              <Activity className="w-5 h-5" style={{ color: "var(--bp)" }} />
            </div>
            <div className="mt-4 text-[12px] text-slate-500">Dernière mesure</div>
            <div className="text-[14px] font-semibold text-slate-900">{it.last}</div>
            <div className={`mt-3 inline-block text-[11.5px] px-2 py-0.5 rounded ${it.alert === "OK" ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}`}>{it.alert}</div>
          </Card>
        ))}
      </div>
    </div>
  );
}

function PageDMP({ brand }) {
  const [result, setResult] = useState(null);
  const history = [
    { p: SEED_PATIENTS[0], when: "Aujourd'hui · 09:12", docs: 12 },
    { p: SEED_PATIENTS[1], when: "Hier · 16:42", docs: 28 },
    { p: SEED_PATIENTS[2], when: "26/03/2026 · 11:05", docs: 4 },
  ];
  const [form, setForm] = useState({ last: "", first: "", dob: "", sex: "F", zip: "", city: "" });
  void brand;
  return (
    <div>
      <PageHeader title="Consulter un DMP" subtitle="Identification patient INSi qualifiée · Lecture carte Vitale · Recherche par traits" />
      {/* Bandeau matrice droits DMP */}
      <div className="mb-4 rounded-lg border border-blue-200 bg-blue-50 px-4 py-2.5 flex flex-wrap items-center gap-x-5 gap-y-1.5 text-[11.5px] text-blue-900">
        <span className="flex items-center gap-1.5 font-semibold"><ShieldCheck className="w-3.5 h-3.5" /> Matrice droits DMP — auth. indirecte (certificat ES)</span>
        <span className="flex items-center gap-1"><CheckCircle2 className="w-3 h-3 text-emerald-600" /> Alimentation TD2.1</span>
        <span className="flex items-center gap-1"><CheckCircle2 className="w-3 h-3 text-emerald-600" /> Consultation docs non masqués TD3.2</span>
        <span className="flex items-center gap-1 text-slate-500"><X className="w-3 h-3" /> Gestion administrative avancée (Phase 2)</span>
      </div>

      <div className="grid lg:grid-cols-[1fr,1fr] gap-5">
        <div className="space-y-5">
          <Card className="p-6">
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="text-[15px] font-semibold text-slate-900">Lecture via carte Vitale</div>
                <p className="text-[12.5px] text-slate-500 mt-1">Placez la carte dans le lecteur PC/SC puis lancez la lecture.</p>
              </div>
              <Badge className="bg-slate-100 text-slate-600 border-0 font-normal">INSi · INSI-MANU</Badge>
            </div>
            <Button onClick={() => { setResult(SEED_PATIENTS[0]); toast.success("Carte Vitale lue · INS v2.1 qualifié (OID 1.2.250.1.213.1.4.8)."); }} className="w-full text-white text-[15px] py-6 bg-blue-600 hover:bg-blue-700" data-testid="vitale-read-btn">
              Lire la carte Vitale
            </Button>
            <p className="mt-3 text-[11.5px] text-slate-500 flex items-center gap-1.5"><ShieldCheck className="w-3 h-3" /> Vérifiez que votre authentification forte (e-CPS via Pro Santé Connect) est valide avant la lecture.</p>
          </Card>
          <Card className="p-6">
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="text-[15px] font-semibold text-slate-900">Recherche par traits d&apos;identité</div>
                <p className="text-[12.5px] text-slate-500 mt-1">Interrogation INSi à partir des traits d&apos;état civil.</p>
              </div>
              <Badge className="bg-slate-100 text-slate-600 border-0 font-normal">RNIV · Identitovigilance</Badge>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {[["last", "Nom de naissance", "text"], ["first", "Prénom(s)", "text"], ["dob", "Date de naissance", "date"]].map(([k, lbl, t]) => (
                <div key={k}><label className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold block mb-1">{lbl}</label>
                  <input type={t} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="w-full border border-slate-300 rounded-md px-3 py-2 text-[13px] text-slate-900" /></div>
              ))}
              <div><label className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold block mb-1">Sexe</label>
                <div className="flex gap-1.5">{["H", "F", "NS"].map((s) => (
                  <button key={s} onClick={() => setForm({ ...form, sex: s })} className={`flex-1 py-2 rounded border text-[13px] font-semibold ${form.sex === s ? "border-blue-500 text-blue-700 bg-blue-50" : "border-slate-300 text-slate-600"}`}>{s}</button>
                ))}</div></div>
              {[["zip", "Code postal de naissance"], ["city", "Commune de naissance"]].map(([k, lbl]) => (
                <div key={k}><label className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold block mb-1">{lbl}</label>
                  <input value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="w-full border border-slate-300 rounded-md px-3 py-2 text-[13px] text-slate-900" /></div>
              ))}
            </div>
            <Button onClick={() => { setResult(SEED_PATIENTS[1]); toast.success("INSi qualifié · DMP trouvé · doublons RNIV vérifiés."); }} className="mt-4 text-white w-full" style={{ backgroundColor: "var(--bp)" }}>
              <Search className="w-4 h-4 mr-2" /> Qualifier l&apos;INS via INSi
            </Button>
            <p className="mt-3 text-[11px] text-slate-500">Référentiel INS v2.1 (déc. 2024) · art. R.1111-8-1 à -7 CSP · contrôle homonymies/doublons RNIV.</p>
          </Card>
        </div>
        <div className="space-y-5">
          <Card className="p-6">
            <div className="flex items-center justify-between mb-3">
              <div className="text-[15px] font-semibold text-slate-900">DMP récemment consultés</div>
              <span className="text-[11px] px-2 py-0.5 rounded bg-slate-100 text-slate-600">{history.length}</span>
            </div>
            <div className="space-y-2">
              {history.map((h, i) => (
                <button key={i} onClick={() => setResult(h.p)} className="w-full flex items-center gap-3 p-3 rounded-lg border border-slate-200 hover:border-slate-400 text-left transition-colors">
                  <div className="w-9 h-9 rounded-full grid place-items-center text-white font-semibold text-[12px]" style={{ backgroundColor: "var(--bp)" }}>{h.p.first[0]}{h.p.last[0]}</div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[13.5px] font-semibold text-slate-900 truncate">{h.p.first} {h.p.last}</div>
                    <div className="text-[11px] text-slate-500">{h.when} · {h.docs} documents</div>
                  </div>
                  <ChevronRight className="w-4 h-4 text-slate-300" />
                </button>
              ))}
            </div>
          </Card>
          {result && (
            <Card className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-11 h-11 rounded-full grid place-items-center text-white font-semibold" style={{ backgroundColor: "var(--bp)" }}>{result.first[0]}{result.last[0]}</div>
                <div className="flex-1">
                  <div className="text-[15px] font-semibold text-slate-900 flex items-center gap-2">{result.first} {result.last}
                    <span className="inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700"><CheckCircle2 className="w-3 h-3" /> INS qualifiée v2.1</span>
                  </div>
                  <div className="text-[11.5px] text-slate-500">INS : <span className="font-mono">{result.ins}</span> · OID <span className="font-mono">1.2.250.1.213.1.4.8</span> · {result.age} ans</div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                {[{ t: "Comptes-rendus", n: 12, f: "CDA R2 / CI-SIS" }, { t: "Biologie", n: 28, f: "LOINC" }, { t: "Imagerie", n: 4, f: "DICOM / PDF" }, { t: "Ordonnances", n: 19, f: "PECAN / CDA R2" }].map((c, i) => (
                  <div key={i} className="rounded-lg border border-slate-200 p-3 hover:bg-slate-50 cursor-pointer">
                    <FileText className="w-4 h-4 text-slate-400" />
                    <div className="mt-1 text-[13px] font-semibold text-slate-900">{c.t}</div>
                    <div className="text-[11px] text-slate-500">{c.n} documents · <span className="font-mono text-[10px]">{c.f}</span></div>
                  </div>
                ))}
              </div>
              <p className="mt-4 text-[11px] text-slate-500 leading-relaxed">Accès tracé conformément à la PGSSI-S (audit logs). Seuls les documents <strong>non masqués par le patient</strong> sont consultables en authentification indirecte (TD3.2).</p>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}

function PageAnnuaire({ brand }) {
  return (
    <div>
      <PageHeader title="Annuaire santé" subtitle="Recherche dans l'annuaire ANS · MSSanté · RPPS / FINESS" />
      <Card className="p-6">
        <div className="flex gap-2 mb-5">
          <input placeholder="Nom du PS, structure, RPPS, BAL MSS…" className="flex-1 border border-slate-200 rounded-md px-3 py-2.5 text-[13.5px]" />
          <Button className="text-white" style={{ backgroundColor: "var(--bp)" }}><Search className="w-4 h-4 mr-1.5" />Rechercher</Button>
        </div>
        <div className="space-y-2">
          {[
            { n: "Dr Bernard Camille", r: "Médecin généraliste", m: "c.bernard.medecin@medecin.mssante.fr", l: "Orléans" },
            { n: "Dr Martin Julien", r: "Cardiologue", m: "j.martin.cardio@medecin.mssante.fr", l: "Tours" },
            { n: "Sage-femme Dubois Sophie", r: "Sage-femme", m: "s.dubois.sf@sage-femme.mssante.fr", l: "Blois" },
          ].map((x, i) => (
            <div key={i} className="flex items-center gap-4 p-3 rounded-lg border border-slate-200 hover:bg-slate-50">
              <div className="w-9 h-9 rounded-full grid place-items-center text-white font-semibold text-[12px]" style={{ backgroundColor: "var(--bp)" }}>{x.n.split(" ").slice(-2)[0][0]}{x.n.split(" ").slice(-1)[0][0]}</div>
              <div className="flex-1">
                <div className="text-[13.5px] font-semibold text-slate-900">{x.n}</div>
                <div className="text-[11.5px] text-slate-500">{x.r} · {x.l} · <span className="font-mono">{x.m}</span></div>
              </div>
              <button className="text-[12px] px-3 py-1.5 rounded border border-slate-200 hover:bg-white">Envoyer MSS</button>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}


/* ============================ Patient booking view ============================ */
function PagePatientBooking({ brand, platformHost, services }) {
  const [step, setStep] = useState(0); // 0 motif, 1 praticien, 2 créneau, 3 confirm
  const [motif, setMotif] = useState(null);
  const [praticien, setPraticien] = useState(null);
  const [slot, setSlot] = useState(null);
  const motifs = [
    { id: "tc", label: "Téléconsultation", icon: Video, when: hasS("telecons", services) },
    { id: "pres", label: "Consultation présentielle", icon: Stethoscope, always: true },
    { id: "renouv", label: "Renouvellement d'ordonnance", icon: FileText, always: true },
    { id: "bilan", label: "Bilan annuel", icon: CheckCircle2, always: true },
  ].filter((x) => x.always || x.when);
  const praticiens = SEED_USERS;
  const slots = ["Lun 30 · 09:00", "Lun 30 · 10:30", "Mar 31 · 14:00", "Mer 01 · 11:15", "Jeu 02 · 16:30"];

  return (
    <div className="max-w-3xl mx-auto">
      <div className="text-center mb-8">
        <div className="text-[11px] uppercase tracking-[0.18em] font-semibold mb-2" style={{ color: "var(--bp)" }}>Vue patient · Prise de rendez-vous</div>
        <h1 className="text-3xl font-bold text-slate-900">Prenez rendez-vous chez {brand.name || "votre praticien"}</h1>
        <p className="mt-2 text-[13.5px] text-slate-600">{platformHost}</p>
      </div>
      <div className="flex items-center justify-center gap-2 mb-8">
        {["Motif", "Praticien", "Créneau", "Confirmation"].map((l, i) => (
          <React.Fragment key={l}>
            <div className={`flex items-center gap-2 ${i <= step ? "" : "opacity-40"}`}>
              <div className="w-7 h-7 rounded-full grid place-items-center text-white text-[12px] font-semibold" style={{ backgroundColor: i <= step ? "var(--bp)" : "#cbd5e1" }}>{i + 1}</div>
              <div className="text-[12.5px] font-medium text-slate-700 hidden sm:block">{l}</div>
            </div>
            {i < 3 && <div className="w-8 sm:w-12 h-px bg-slate-200" />}
          </React.Fragment>
        ))}
      </div>

      <Card className="p-6 lg:p-8">
        {step === 0 && (
          <>
            <h2 className="text-[16px] font-semibold mb-4">Quel est le motif de votre rendez-vous ?</h2>
            <div className="grid sm:grid-cols-2 gap-3">
              {motifs.map((m) => {
                const I = m.icon;
                return (
                  <button key={m.id} onClick={() => { setMotif(m); setStep(1); }} className="flex items-center gap-3 p-4 rounded-lg border-2 border-slate-200 hover:border-slate-400 text-left transition-colors">
                    <div className="w-10 h-10 rounded-lg grid place-items-center" style={{ backgroundColor: "rgba(15,23,42,0.06)" }}>
                      <I className="w-5 h-5" style={{ color: "var(--bp)" }} />
                    </div>
                    <div className="text-[14px] font-semibold text-slate-900">{m.label}</div>
                  </button>
                );
              })}
            </div>
          </>
        )}
        {step === 1 && (
          <>
            <h2 className="text-[16px] font-semibold mb-4">Avec quel praticien ?</h2>
            <div className="space-y-2">
              {praticiens.map((p) => (
                <button key={p.id} onClick={() => { setPraticien(p); setStep(2); }} className="w-full flex items-center gap-4 p-3 rounded-lg border border-slate-200 hover:border-slate-400 text-left transition-colors">
                  <div className="w-11 h-11 rounded-full grid place-items-center text-white font-semibold" style={{ backgroundColor: "var(--bp)" }}>{p.first[0]}{p.last[0]}</div>
                  <div className="flex-1">
                    <div className="text-[14px] font-semibold text-slate-900">{p.first} {p.last}</div>
                    <div className="text-[12px] text-slate-500">{p.role} · RPPS <span className="font-mono">{p.rpps}</span></div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-slate-300" />
                </button>
              ))}
            </div>
          </>
        )}
        {step === 2 && (
          <>
            <h2 className="text-[16px] font-semibold mb-1">Choisissez un créneau</h2>
            <p className="text-[12.5px] text-slate-500 mb-4">Avec {praticien?.first} {praticien?.last} · {motif?.label}</p>
            <div className="grid sm:grid-cols-2 gap-2">
              {slots.map((s) => (
                <button key={s} onClick={() => { setSlot(s); setStep(3); }} className="px-4 py-3 rounded-lg border border-slate-200 hover:border-slate-400 text-[13.5px] font-medium text-slate-800 transition-colors">{s}</button>
              ))}
            </div>
          </>
        )}
        {step === 3 && (
          <div className="text-center py-4">
            <div className="w-14 h-14 rounded-full grid place-items-center mx-auto mb-3" style={{ backgroundColor: "var(--bp)" }}>
              <CheckCircle2 className="w-7 h-7 text-white" />
            </div>
            <h2 className="text-[20px] font-bold text-slate-900">Rendez-vous confirmé !</h2>
            <p className="mt-2 text-[13.5px] text-slate-600">
              <b>{slot}</b> avec <b>{praticien?.first} {praticien?.last}</b> · {motif?.label}<br />
              Vous recevrez un email de confirmation et un rappel 24h avant.
            </p>
            <Button className="mt-5 text-white" style={{ backgroundColor: "var(--bp)" }} onClick={() => { setStep(0); setMotif(null); setPraticien(null); setSlot(null); }}>Nouveau rendez-vous</Button>
          </div>
        )}
      </Card>

      <div className="mt-6 flex items-center justify-center gap-4 opacity-70">
        <img src="/logos/insi.png" alt="INSi" className="h-6 object-contain" />
        <img src="/logos/dmp.png" alt="DMP" className="h-6 object-contain" />
        <img src="/logos/mon-espace-sante.png" alt="Mon espace santé" className="h-6 object-contain" />
        <img src="/logos/hds.png" alt="HDS" className="h-6 object-contain" />
      </div>
    </div>
  );
}

/* ============================ Settings (profile + RPPS/CPx) ============================ */
function PageSettings({ brand }) {
  const [tab, setTab] = useState("profile");
  const [me, setMe] = useState({ last: "DUPONT", first: "Marie", email: `marie.dupont@${(brand.name || "metaconnect").toLowerCase().replace(/\s+/g, "")}.fr` });
  return (
    <div>
      <PageHeader title="Paramètres" subtitle="Gérez votre profil et les paramètres de votre compte" />
      <div className="grid lg:grid-cols-[200px_1fr] gap-6">
        <aside className="space-y-1">
          {[
            { id: "profile", label: "Profil" },
            { id: "password", label: "Mot de passe" },
            { id: "appearance", label: "Apparence" },
            { id: "lps", label: "Configuration LPS" },
            { id: "reseau", label: "Réseau de proximité" },
          ].map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)} className={`w-full text-left px-3 py-2 rounded text-[13px] transition-colors ${tab === t.id ? "bg-slate-100 font-semibold text-slate-900" : "text-slate-600 hover:bg-slate-50"}`} data-testid={`settings-tab-${t.id}`}>
              {t.label}
            </button>
          ))}
        </aside>
        <div className="space-y-5">
          {tab === "profile" && (
            <>
              <Card className="p-6">
                <div className="text-[15px] font-semibold text-slate-900">Informations personnelles</div>
                <p className="text-[12.5px] text-slate-500 mb-4">Mettez à jour votre nom.</p>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold block mb-1">Nom</label>
                    <input value={me.last} onChange={(e) => setMe({ ...me, last: e.target.value })} className="w-full border border-slate-300 rounded-md px-3 py-2 text-[13.5px] text-slate-900" />
                  </div>
                  <div>
                    <label className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold block mb-1">Prénom</label>
                    <input value={me.first} onChange={(e) => setMe({ ...me, first: e.target.value })} className="w-full border border-slate-300 rounded-md px-3 py-2 text-[13.5px] text-slate-900" />
                  </div>
                </div>
                <Button className="mt-4 text-white" style={{ backgroundColor: "var(--bp)" }} onClick={() => toast.success("Enregistré.")}>Enregistrer</Button>
              </Card>
              <Card className="p-6">
                <div className="text-[15px] font-semibold text-slate-900">Email de notification</div>
                <p className="text-[12.5px] text-slate-500 mb-4">Adresse utilisée pour les notifications du compte professionnel.</p>
                <input value={me.email} onChange={(e) => setMe({ ...me, email: e.target.value })} className="w-full border border-slate-300 rounded-md px-3 py-2 text-[13.5px] text-slate-900 max-w-md" />
                <div className="mt-4"><Button className="text-white" style={{ backgroundColor: "var(--bp)" }} onClick={() => toast.success("Enregistré.")}>Enregistrer</Button></div>
              </Card>
              <Card className="p-6">
                <div className="text-[15px] font-semibold text-slate-900">Informations professionnelles</div>
                <p className="text-[12.5px] text-slate-500 mb-4">Informations en lecture seule sur votre identité professionnelle (issues de l&apos;Annuaire Santé ANS).</p>
                <div className="grid sm:grid-cols-2 gap-x-8 gap-y-4 text-[13px]">
                  <div>
                    <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Numéro RPPS</div>
                    <div className="text-slate-900 font-mono mt-0.5">10•••82</div>
                  </div>
                  <div>
                    <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Code de secours PS</div>
                    <div className="text-slate-900 font-mono mt-0.5">701098</div>
                  </div>
                  <div>
                    <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Code profession</div>
                    <div className="text-slate-900 font-mono mt-0.5">28</div>
                  </div>
                  <div>
                    <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">OID profession</div>
                    <div className="text-slate-700 font-mono text-[11px] mt-0.5 break-all">https://mos.esante.gouv.fr/NOS/TRE_G15-ProfessionSante/FHIR/TRE-G15-ProfessionSante</div>
                  </div>
                  <div>
                    <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">CPx — N° de série</div>
                    <div className="text-slate-900 font-mono mt-0.5">3200040307</div>
                  </div>
                </div>
              </Card>
            </>
          )}
          {tab === "password" && (
            <Card className="p-6">
              <div className="text-[15px] font-semibold text-slate-900 mb-4">Modifier mon mot de passe</div>
              <div className="space-y-3 max-w-md">
                <input type="password" placeholder="Mot de passe actuel" className="w-full border border-slate-300 rounded-md px-3 py-2 text-[13.5px] text-slate-900 placeholder:text-slate-400" />
                <input type="password" placeholder="Nouveau mot de passe" className="w-full border border-slate-300 rounded-md px-3 py-2 text-[13.5px] text-slate-900 placeholder:text-slate-400" />
                <input type="password" placeholder="Confirmer le nouveau" className="w-full border border-slate-300 rounded-md px-3 py-2 text-[13.5px] text-slate-900 placeholder:text-slate-400" />
                <Button className="text-white" style={{ backgroundColor: "var(--bp)" }}>Mettre à jour</Button>
              </div>
            </Card>
          )}
          {tab === "appearance" && (
            <SoonPanel title="Apparence" what="Thème clair / sombre, taille de police, densité d'affichage." />
          )}
          {tab === "lps" && (
            <SoonPanel title="Configuration LPS" what="Paramétrage du logiciel de poste pour la carte CPx (eCPS) et l'INSi." />
          )}
          {tab === "reseau" && (
            <ReseauPreferencesPanel />
          )}
        </div>
      </div>
    </div>
  );
}



/* ============================ Ordonnances (trace + DMP/MSS) ============================ */
function OrdoStatusBadge({ ok, neutral, text }) {
  return <span className={`inline-block text-[11px] px-2 py-0.5 rounded ${ok ? "bg-emerald-100 text-emerald-700" : neutral ? "bg-slate-100 text-slate-600" : "bg-red-100 text-red-700"}`}>{text}</span>;
}

// Small drug catalog used by the "Nouvelle ordonnance" autocomplete.

function PageOrdonnances() {
  const [tab, setTab] = useState("historique"); // "historique" | "nouvelle"

  // Historique (existant + statut "Envoyée pharmacie" / "Brouillon")
  const historyRows = [
    { id: "019ecaaa-8956-72c4-a0aa-38afb10b482f", patient: "Marie Lefèvre", prescripteur: "Dr Thierry LE VAN TUONG", rpps: "10003995312", meds: "Metformine, Amlodipine, Atorvastatine", statut: "Envoyée pharmacie", dmp: "Déposé", mss: "Envoyée", date: "12/03/2026 11:43" },
    { id: "019eb1a4-8467-73e8-a3ad-992e5e665e2f", patient: "Paul Garcia", prescripteur: "Dr Thierry LE VAN TUONG", rpps: "10003995312", meds: "Paracétamol, Kiné rééducation", statut: "Envoyée pharmacie", dmp: "Non déposé", mss: "Envoyée", date: "28/02/2026 10:06" },
    { id: "019e2ca2-a54b-70ea-a6e7-cb30db00da6b", patient: "Aïcha Benali", prescripteur: "Dr Thierry LE VAN TUONG", rpps: "10003995312", meds: "Budésonide/Formotérol, Salbutamol", statut: "Envoyée pharmacie", dmp: "Déposé", mss: "Envoyée", date: "05/03/2026 09:15" },
    { id: "019e2ca0-3ac0-7057-b20f-c3dd823848b8", patient: "Marie Lefèvre", prescripteur: "Dr Thierry LE VAN TUONG", rpps: "10003995312", meds: "Bandelettes glycémie, Lancettes", statut: "Brouillon", dmp: "—", mss: "—", date: "15/02/2026 19:12" },
    { id: "019e2c9d-ee08-72b6-a594-1716c0dd3fd8", patient: "Aïcha Benali", prescripteur: "Dr Thierry LE VAN TUONG", rpps: "10003995312", meds: "Adrénaline auto-injectable, Cétirizine", statut: "Envoyée pharmacie", dmp: "Déposé", mss: "Envoyée", date: "20/12/2025 18:20" },
  ];

  return (
    <div>
      <div className="flex items-start justify-between gap-4 mb-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Ordonnances</h1>
          <p className="mt-1 text-[12.5px] text-slate-500">
            Édition assistée · Vérification interactions & allergies · e-Prescription unifiée <strong>PECAN</strong> · Dépôt DMP + MSSanté
          </p>
        </div>
        <div className="inline-flex rounded-lg border border-slate-200 bg-white p-0.5 shadow-sm">
          <button
            onClick={() => setTab("historique")}
            data-testid="ordo-tab-historique"
            className={`px-4 py-1.5 text-[12.5px] font-medium rounded-md transition-colors ${tab === "historique" ? "text-white" : "text-slate-500 hover:bg-slate-50"}`}
            style={tab === "historique" ? { backgroundColor: "var(--bp)" } : {}}
          >
            <FileText className="w-3.5 h-3.5 inline mr-1" />Historique
          </button>
          <button
            onClick={() => setTab("nouvelle")}
            data-testid="ordo-tab-nouvelle"
            className={`px-4 py-1.5 text-[12.5px] font-medium rounded-md transition-colors ${tab === "nouvelle" ? "text-white" : "text-slate-500 hover:bg-slate-50"}`}
            style={tab === "nouvelle" ? { backgroundColor: "var(--bp)" } : {}}
          >
            <Plus className="w-3.5 h-3.5 inline mr-1" />Nouvelle ordonnance
          </button>
        </div>
      </div>

      {/* Bandeau référentiels PECAN / CDA R2 / e-prescription */}
      <div className="mb-4 rounded-lg border border-blue-200 bg-blue-50 px-4 py-2.5 flex flex-wrap items-center gap-x-5 gap-y-1.5 text-[11.5px] text-blue-900">
        <span className="flex items-center gap-1.5 font-semibold"><FileText className="w-3.5 h-3.5" /> e-Prescription</span>
        <span><strong>PECAN</strong> · Programme e-prescription unifiée ANS</span>
        <span>Format <strong>CDA R2 / CI-SIS</strong></span>
        <span>Dépôt DMP TD2.1</span>
        <span>Transmission MSSanté (LRO)</span>
      </div>

      {tab === "nouvelle" ? <OrdoNouvelleForm /> : <OrdoHistorique rows={historyRows} />}
    </div>
  );
}

function OrdoHistorique({ rows }) {
  const StatusBadge = OrdoStatusBadge;
  return (
    <Card className="overflow-hidden" data-testid="ordo-historique-card">
      <div className="bg-slate-50 px-4 py-3 border-b border-slate-200 grid grid-cols-[1.4fr,1.4fr,1.6fr,1fr,0.9fr,1fr,0.7fr] gap-3 text-[10.5px] uppercase tracking-wider text-slate-500 font-semibold">
        <div>N° traçabilité</div><div>Patient</div><div>Médicaments</div><div>Statut</div><div>DMP</div><div>Date</div><div className="text-right">Actions</div>
      </div>
      <div className="divide-y divide-slate-100">
        {rows.map((r) => (
          <div key={r.id} className="grid grid-cols-[1.4fr,1.4fr,1.6fr,1fr,0.9fr,1fr,0.7fr] gap-3 px-4 py-3 items-center text-[12.5px] hover:bg-slate-50">
            <div className="font-mono text-[10.5px] text-slate-600 break-all leading-tight">{r.id}</div>
            <div className="font-semibold text-slate-900">{r.patient}<div className="text-[10.5px] text-slate-400 font-normal">{r.prescripteur}</div></div>
            <div className="text-slate-700 text-[12px] leading-snug">{r.meds}</div>
            <div>
              <span className={`inline-block text-[11px] px-2 py-0.5 rounded font-medium ${r.statut === "Envoyée pharmacie" ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-600"}`}>{r.statut}</span>
            </div>
            <div><StatusBadge ok={r.dmp === "Déposé"} neutral={r.dmp === "—"} text={r.dmp} /></div>
            <div className="text-slate-500 text-[11.5px]">{r.date}</div>
            <div className="text-right"><Button size="sm" className="text-white" style={{ backgroundColor: "var(--bp)" }}>Voir</Button></div>
          </div>
        ))}
      </div>
    </Card>
  );
}

function OrdoNouvelleForm() {
  const [patientId, setPatientId] = useState(SEED_PATIENTS[0].id);
  const patient = SEED_PATIENTS.find((p) => p.id === patientId) || SEED_PATIENTS[0];
  const [lines, setLines] = useState([{ drug: "", posology: "1 cp matin", duration: "7 jours" }]);
  const [search, setSearch] = useState("");
  const [openIdx, setOpenIdx] = useState(null);

  const filtered = search.trim()
    ? DRUG_CATALOG.filter((d) => d.name.toLowerCase().includes(search.toLowerCase())).slice(0, 6)
    : DRUG_CATALOG.slice(0, 6);

  // Detect allergy conflicts vs current patient
  const conflicts = lines.flatMap((ln, i) => {
    if (!ln.drug) return [];
    const drug = DRUG_CATALOG.find((d) => d.name === ln.drug);
    if (!drug) return [];
    const hit = (patient.allergies || []).find((a) => drug.allergyKeys.some((k) => a.toLowerCase().includes(k)));
    return hit ? [{ lineIdx: i, drug: drug.name, allergy: hit }] : [];
  });

  // Also flag interactions with active prescriptions (same molecule already prescribed → doublon)
  const activeMeds = (patient.prescriptions?.[0]?.items || []).join(" | ").toLowerCase();
  const duplicates = lines.flatMap((ln, i) => {
    if (!ln.drug) return [];
    const key = ln.drug.split(" ")[0].toLowerCase();
    return activeMeds.includes(key) ? [{ lineIdx: i, drug: ln.drug }] : [];
  });

  const addLine = () => setLines([...lines, { drug: "", posology: "", duration: "" }]);
  const removeLine = (i) => setLines(lines.filter((_, idx) => idx !== i));
  const updateLine = (i, field, val) => setLines(lines.map((ln, idx) => idx === i ? { ...ln, [field]: val } : ln));
  const chooseDrug = (i, drug) => { updateLine(i, "drug", drug.name); setOpenIdx(null); setSearch(""); };

  const canSubmit = lines.every((ln) => ln.drug && ln.posology && ln.duration);

  return (
    <div className="grid lg:grid-cols-[1.4fr,1fr] gap-5" data-testid="ordo-nouvelle-form">
      {/* Left: form */}
      <Card className="p-5">
        <div className="text-[11px] uppercase tracking-[0.14em] font-semibold text-slate-500 mb-3">Nouvelle ordonnance</div>
        <div className="mb-4">
          <label className="text-[11px] uppercase tracking-[0.14em] text-slate-500 font-semibold block mb-1">Patient</label>
          <select
            value={patientId}
            onChange={(e) => setPatientId(e.target.value)}
            data-testid="ordo-patient-select"
            className="w-full border border-slate-300 rounded-md px-3 py-2 text-[13.5px] text-slate-900 bg-white"
          >
            {SEED_PATIENTS.map((p) => (
              <option key={p.id} value={p.id}>{p.first} {p.last} — INS {p.ins}</option>
            ))}
          </select>
          <div className="mt-1.5 text-[11.5px] text-slate-500 flex flex-wrap gap-3">
            <span><strong>Allergies :</strong> {patient.allergies?.length ? patient.allergies.join(", ") : "aucune connue"}</span>
            <span><strong>ALD :</strong> {patient.ald?.length ? patient.ald.join(", ") : "—"}</span>
          </div>
        </div>

        {/* Alert banner — allergy conflicts */}
        {(conflicts.length > 0 || duplicates.length > 0) && (
          <div className="mb-4 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3" data-testid="ordo-conflict-banner">
            <div className="flex items-start gap-2">
              <ShieldAlert className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
              <div className="flex-1">
                <div className="text-[12.5px] font-bold text-amber-900">
                  {conflicts.length + duplicates.length} conflit{conflicts.length + duplicates.length > 1 ? "s" : ""} détecté{conflicts.length + duplicates.length > 1 ? "s" : ""}
                </div>
                <ul className="mt-1 space-y-0.5 text-[12px] text-amber-900">
                  {conflicts.map((c, i) => (
                    <li key={`c-${i}`}>
                      <strong>Allergie</strong> — <span className="font-semibold">{c.drug}</span> incompatible avec <em>{c.allergy}</em> (patient).
                    </li>
                  ))}
                  {duplicates.map((d, i) => (
                    <li key={`d-${i}`}>
                      <strong>Doublon</strong> — <span className="font-semibold">{d.drug}</span> déjà présent dans une prescription active.
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        )}

        {/* Prescription lines */}
        <div className="space-y-3">
          {lines.map((ln, i) => (
            <div key={i} className="rounded-lg border border-slate-200 bg-slate-50 p-3" data-testid={`ordo-line-${i}`}>
              <div className="grid grid-cols-[2fr,1.2fr,1fr,auto] gap-2 items-start">
                {/* Drug autocomplete */}
                <div className="relative">
                  <label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold block mb-1">Médicament</label>
                  <div className="relative">
                    <input
                      value={openIdx === i ? search : ln.drug}
                      onFocus={() => { setOpenIdx(i); setSearch(ln.drug); }}
                      onChange={(e) => setSearch(e.target.value)}
                      placeholder="Rechercher (Amoxicilline, Metformine…)"
                      data-testid={`ordo-line-drug-${i}`}
                      className="w-full border border-slate-300 rounded-md px-3 py-2 text-[13px] text-slate-900 bg-white pr-8"
                    />
                    <Pill className="w-4 h-4 text-slate-400 absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none" />
                  </div>
                  {openIdx === i && filtered.length > 0 && (
                    <ul className="absolute z-20 mt-1 left-0 right-0 max-h-56 overflow-y-auto rounded-md border border-slate-200 bg-white shadow-lg">
                      {filtered.map((d) => (
                        <li key={d.name}>
                          <button
                            type="button"
                            onClick={() => chooseDrug(i, d)}
                            className="w-full text-left px-3 py-2 text-[13px] text-slate-800 hover:bg-blue-50"
                          >
                            <div className="font-semibold">{d.name}</div>
                            <div className="text-[11px] text-slate-500">{d.class}</div>
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold block mb-1">Posologie</label>
                  <input value={ln.posology} onChange={(e) => updateLine(i, "posology", e.target.value)}
                    placeholder="1 cp matin et soir"
                    data-testid={`ordo-line-posology-${i}`}
                    className="w-full border border-slate-300 rounded-md px-3 py-2 text-[13px] text-slate-900 bg-white" />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold block mb-1">Durée</label>
                  <input value={ln.duration} onChange={(e) => updateLine(i, "duration", e.target.value)}
                    placeholder="7 jours"
                    data-testid={`ordo-line-duration-${i}`}
                    className="w-full border border-slate-300 rounded-md px-3 py-2 text-[13px] text-slate-900 bg-white" />
                </div>
                <button
                  type="button"
                  onClick={() => removeLine(i)}
                  disabled={lines.length === 1}
                  data-testid={`ordo-line-remove-${i}`}
                  className="mt-6 p-2 rounded-md text-slate-400 hover:text-red-600 hover:bg-red-50 disabled:opacity-30 disabled:cursor-not-allowed"
                  title="Supprimer cette ligne"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>

        <button
          type="button"
          onClick={addLine}
          data-testid="ordo-add-line"
          className="mt-3 text-[12.5px] font-medium text-blue-700 hover:text-blue-900 inline-flex items-center gap-1.5"
        >
          <Plus className="w-4 h-4" /> Ajouter un médicament
        </button>

        <div className="mt-5 pt-4 border-t border-slate-200 flex items-center justify-between gap-3">
          <button
            onClick={() => toast.success("Ordonnance enregistrée en brouillon (sandbox).")}
            data-testid="ordo-save-draft"
            className="text-[12.5px] text-slate-600 hover:text-slate-900 font-medium"
          >
            Enregistrer comme brouillon
          </button>
          <Button
            disabled={!canSubmit}
            onClick={() => toast.success(`Ordonnance envoyée à la pharmacie · ${patient.first} ${patient.last} · CDA R2 signé.`)}
            data-testid="ordo-send-pharmacy"
            className="text-white disabled:opacity-50"
            style={{ backgroundColor: "var(--bp)" }}
          >
            <Send className="w-4 h-4 mr-1.5" /> Envoyer à la pharmacie
          </Button>
        </div>
      </Card>

      {/* Right: preview */}
      <Card className="p-5 bg-slate-50">
        <div className="text-[11px] uppercase tracking-[0.14em] font-semibold text-slate-500 mb-3">Aperçu · document signé</div>
        <div className="rounded-lg bg-white border border-slate-200 p-4 shadow-sm">
          <div className="text-[11px] text-slate-500">Dr Thierry LE VAN TUONG — RPPS 10003995312</div>
          <div className="text-[10.5px] text-slate-400">Cabinet d'Orléans · MSSanté cabinet@dr-tuong.mssante.fr</div>
          <div className="mt-3 border-t border-slate-100 pt-3">
            <div className="text-[13px] font-semibold text-slate-900">{patient.first} {patient.last}</div>
            <div className="text-[11px] text-slate-500 font-mono">INS {patient.ins} · {patient.sex === "F" ? "Née" : "Né"} le {patient.birth}</div>
          </div>
          <div className="mt-3 space-y-2">
            {lines.map((ln, i) => ln.drug ? (
              <div key={i} className="text-[12.5px] text-slate-900 leading-snug">
                <span className="font-semibold">{ln.drug}</span>
                <div className="text-[11.5px] text-slate-600">{ln.posology} — {ln.duration}</div>
              </div>
            ) : null)}
            {lines.every((ln) => !ln.drug) && <div className="text-[12px] text-slate-400 italic">Aucun médicament saisi.</div>}
          </div>
          <div className="mt-4 pt-3 border-t border-slate-100 flex items-center gap-2 text-[10.5px] text-slate-500">
            <ShieldCheck className="w-3.5 h-3.5 text-emerald-500" /> Signature eIDAS · Format CDA R2 · Dépôt DMP TD2.1
          </div>
        </div>
      </Card>
    </div>
  );
}

/* ============================ Documents (CR / Consentements) ============================ */
function PageDocs({ kind }) {
  const cfg = {
    cr: { title: "Comptes-rendus", subtitle: "Templates pré-configurés transmis automatiquement via MSSanté et au DMP.", items: [
      { t: "CR consultation généraliste", auto: "MSSanté + DMP" },
      { t: "CR téléconsultation HAS", auto: "MSSanté + DMP + patient" },
      { t: "CR bilan annuel", auto: "MSSanté + DMP" },
      { t: "CR avis spécialisé", auto: "MSSanté" },
    ]},
    ordo: { title: "Ordonnances", subtitle: "Édition assistée + transmission e-prescription unifiée + INSi.", items: [
      { t: "Renouvellement traitement chronique", auto: "e-Prescription + pharmacie" },
      { t: "Prescription biologique", auto: "Labo MSSanté + AmeliPro" },
      { t: "Prescription imagerie", auto: "Cabinet MSSanté" },
      { t: "Bon de transport sanitaire", auto: "PDF + email patient" },
    ]},
    cons: { title: "Recueils de consentement", subtitle: "Consentements RGPD automatisés, horodatés et opposables.", items: [
      { t: "Consentement téléconsultation HAS", auto: "Signature électronique simple" },
      { t: "Consentement transmission DMP", auto: "INSi + horodatage" },
      { t: "Consentement collecte des données", auto: "RGPD · base légale article 9" },
      { t: "Consentement enregistrement (cas particulier)", auto: "HDS + suppression à J+30" },
    ]},
  }[kind];

  return (
    <div>
      <PageHeader title={cfg.title} subtitle={cfg.subtitle} action={
        <Button className="text-white" style={{ backgroundColor: "var(--bp)" }}><Plus className="w-4 h-4 mr-1" />Nouveau modèle</Button>
      } />
      <div className="mb-4 rounded-lg border border-blue-200 bg-blue-50 px-4 py-2.5 flex flex-wrap items-center gap-x-5 gap-y-1.5 text-[11.5px] text-blue-900">
        <span className="flex items-center gap-1.5 font-semibold"><ShieldCheck className="w-3.5 h-3.5" /> {kind === "cons" ? "Force probante" : "Référentiels"}</span>
        <span>Format <strong>CDA R2 / CI-SIS</strong></span>
        <span>Signature électronique <strong>eIDAS</strong> (référentiel d&apos;authentification ANS · OID praticien)</span>
        <span>Horodatage qualifié RFC 3161</span>
        <span>Archivage HDS · 20 ans dossier médical</span>
        {kind === "cons" && <span>Base légale RGPD art. 9.2.h (santé)</span>}
      </div>
      <Card className="overflow-hidden">
        <div className="divide-y divide-slate-100">
          {cfg.items.map((it, i) => (
            <div key={i} className="flex items-center gap-4 p-4 hover:bg-slate-50">
              <div className="w-10 h-10 rounded-lg grid place-items-center" style={{ backgroundColor: "rgba(15,23,42,0.06)" }}>
                <FileText className="w-5 h-5" style={{ color: "var(--bp)" }} />
              </div>
              <div className="flex-1">
                <div className="text-[14px] font-semibold text-slate-900">{it.t}</div>
                <div className="text-[12px] text-slate-500 mt-0.5">Transmission auto : <span className="text-slate-700">{it.auto}</span></div>
              </div>
              <button className="text-[12px] px-3 py-1.5 rounded border border-slate-200 hover:bg-white">Modifier</button>
              <button className="text-[12px] px-3 py-1.5 rounded text-white" style={{ backgroundColor: "var(--bp)" }}>Utiliser</button>
            </div>
          ))}
        </div>
      </Card>
      <div className="mt-4 inline-flex items-center gap-2 text-[12px] text-slate-500 bg-white border border-slate-200 rounded-lg px-3 py-2">
        <Sparkles className="w-3.5 h-3.5" style={{ color: "var(--bp)" }} />
        Les modèles sont paramétrables par profession et par motif. Modifications sauvegardées automatiquement.
      </div>
    </div>
  );
}

/* ============================ Mon matériel (conditional) ============================ */
/* Surfaced only when the pro answered "Oui" to the assisted-teleconsultation banner.
   Lists the connected devices they ticked in the banner so they can visualise the
   inventory the platform will manage on day one. */
function PageMateriel({ sandboxEffects }) {
  const devices = sandboxEffects?.connectedDevices || [];
  return (
    <div data-testid="page-materiel">
      <PageHeader
        title="Mon matériel connecté"
        subtitle="Dispositifs déployés sur le terrain pour la téléconsultation assistée"
      />
      <Card className="p-5 mb-5 border-l-4" style={{ borderLeftColor: "var(--bp)" }}>
        <div className="text-[11px] uppercase tracking-[0.16em] text-slate-500 font-semibold">Récapitulatif</div>
        <div className="text-[18px] font-semibold text-slate-900 mt-1">
          {devices.length} dispositif{devices.length > 1 ? "s" : ""} configuré{devices.length > 1 ? "s" : ""}
        </div>
        <p className="text-[12.5px] text-slate-600 mt-1 max-w-2xl leading-relaxed">
          Ces dispositifs apparaissent automatiquement dans l&apos;outil de téléconsultation lorsque le PS délégué se connecte à la session.
          Chaque mesure est horodatée, signée et versée au compte rendu du patient.
        </p>
      </Card>
      {devices.length === 0 ? (
        <Card className="p-8 text-center text-slate-500 text-[13px]">
          Aucun dispositif n&apos;a encore été sélectionné. Ajoutez-en depuis le bandeau « Dispositifs connectés souhaités ? » sur la page Téléconsultation.
        </Card>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {devices.map((d, i) => (
            <Card key={d} className="p-4" data-testid={`materiel-device-${i}`}>
              <div className="flex items-start gap-3">
                <div className="grid place-items-center h-10 w-10 rounded-lg bg-slate-100 text-slate-700 shrink-0">
                  <Wifi className="w-4 h-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[13.5px] font-semibold text-slate-900 leading-tight">{d}</div>
                  <div className="text-[11.5px] text-slate-500 mt-0.5">Statut : disponible</div>
                  <div className="mt-2 inline-flex items-center gap-1 text-[10.5px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800 font-semibold">
                    <CheckCircle2 className="w-2.5 h-2.5" /> Compatible BLE
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

/* ============================ Réseau de proximité (conditional) ============================ */
/* Surfaced only when assistedEnabled = true. Stubs a local PS network the
   pro can call upon for in-person assistance during a teleconsult. */
/* ========================================================================
   RÉSEAU DE PROXIMITÉ — Carte France + Recherche + Préférences PS
   Conformité : REF ISE TLC V1.5.1 (ANS), Référentiel socle MSSanté #2 V1.0.1,
   CDA-R2 N1, Volet TLM ANS (TypeCode 75496-0).
   Sandbox mock — données fictives, aucune connexion FHIR réelle.
======================================================================== */
const RESEAU_PROFESSIONS = [
  "Tous", "Médecin généraliste", "Cardiologue", "Dermatologue", "Endocrinologue",
  "Neurologue", "Ophtalmologiste", "Pneumologue", "Pédiatre", "Psychiatre",
  "Gynécologue", "Infirmier libéral", "Sage-femme", "Kinésithérapeute", "ORL",
];

function ReseauPreferencesPanel() {
  const [pref, setPref] = useState({
    visible: true,
    assisted: true,
    expertise: true,
    dicom: false,
    radius: 50,
    address: "12 rue de l'Hôpital, 45000 Orléans",
    specialties: ["Dermatologue", "Cardiologue"],
    contactIM: true,
    contactMss: true,
    contactLk: true,
    mssAddress: "marie.dupont@cabinet.mssante.fr",
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Map backend payload <-> local state.
  const toLocal = (b) => ({
    visible: !!b.visible,
    assisted: !!b.accept_teleconsult_assistee,
    expertise: !!b.accept_teleexpertise,
    dicom: !!b.accept_expertise_image,
    radius: b.rayon_km ?? 50,
    address: b.adresse_ref || "",
    specialties: Array.isArray(b.specialites_rpps) ? b.specialites_rpps : [],
    contactIM: (b.moyens_contact || []).includes("instant_messaging"),
    contactMss: (b.moyens_contact || []).includes("mssante"),
    contactLk: (b.moyens_contact || []).includes("livekit"),
    mssAddress: b.mss_address || "",
  });

  const toApi = (l) => ({
    visible: l.visible,
    accept_teleconsult_assistee: l.assisted,
    accept_teleexpertise: l.expertise,
    accept_expertise_image: l.dicom,
    rayon_km: l.radius,
    adresse_ref: l.address,
    specialites_rpps: l.specialties,
    moyens_contact: [
      l.contactIM && "instant_messaging",
      l.contactMss && "mssante",
      l.contactLk && "livekit",
    ].filter(Boolean),
    mss_address: l.mssAddress,
  });

  // Initial load — pre-fill from backend.
  useEffect(() => {
    const token = localStorage.getItem("mc_pro_token");
    if (!token) {
      setLoading(false);
      return;
    }
    fetch(`${API_BASE}/pro/reseau-preferences`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.ok ? r.json() : Promise.reject())
      .then((data) => setPref(toLocal(data)))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    const token = localStorage.getItem("mc_pro_token");
    if (!token) {
      toast.info("Sandbox sans session pro — préférences sauvées localement uniquement.");
      setSaving(false);
      return;
    }
    try {
      const r = await fetch(`${API_BASE}/pro/reseau-preferences`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(toApi(pref)),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${r.status}`);
      }
      const saved = await r.json();
      setPref(toLocal(saved));
      toast.success("Préférences réseau enregistrées.");
    } catch (e) {
      toast.error(`Échec de l'enregistrement : ${e.message}`);
    } finally {
      setSaving(false);
    }
  };

  const toggleSpecialty = (s) => {
    setPref((p) => ({
      ...p,
      specialties: p.specialties.includes(s) ? p.specialties.filter((x) => x !== s) : [...p.specialties, s],
    }));
  };

  const SPEC_OPTIONS = ["Dermatologue", "Ophtalmologiste", "Cardiologue", "Pneumologue", "Neurologue", "Généraliste", "Endocrinologue", "Sage-femme"];

  if (loading) {
    return (
      <div className="p-6 text-[12.5px] text-slate-500 inline-flex items-center gap-2" data-testid="reseau-preferences-loading">
        <RefreshCw className="w-3.5 h-3.5 animate-spin" /> Chargement de vos préférences…
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="reseau-preferences-panel">
      {/* Sub 1 — Visibilité */}
      <Card className="p-6">
        <div className="flex items-center gap-2 mb-1">
          <Compass className="w-4 h-4" style={{ color: "var(--bp)" }} />
          <div className="text-[15px] font-semibold text-slate-900">Ma disponibilité réseau</div>
        </div>
        <p className="text-[12.5px] text-slate-500 mb-4">Ce que j&apos;accepte de recevoir des autres PS du réseau MetaConnect.</p>
        <div className="space-y-2.5">
          <CheckRow id="visible" label="Je suis visible dans le réseau de proximité MetaConnect" checked={pref.visible} onChange={(v) => setPref({ ...pref, visible: v })} />
          <CheckRow id="assisted" label="J'accepte les demandes de téléconsultation assistée" checked={pref.assisted} onChange={(v) => setPref({ ...pref, assisted: v })} />
          <CheckRow id="expertise" label="J'accepte les demandes de téléexpertise / avis spécialisé" sublabel="Demande d'acte de télémédecine — TypeCode CDA 75496-0, conforme Volet TLM ANS" checked={pref.expertise} onChange={(v) => setPref({ ...pref, expertise: v })} />
          <CheckRow id="dicom" label="J'accepte les demandes d'expertise image (DICOM / photo)" checked={pref.dicom} onChange={(v) => setPref({ ...pref, dicom: v })} />
        </div>
        <RegulatoryNote>
          La téléconsultation assistée implique la présence d&apos;un professionnel de santé délégué auprès du patient.
          Conformément au référentiel ANS <strong>REF ISE TLC V1.5.1</strong>, tout échange de document médical produit lors d&apos;une
          téléconsultation doit être transmis au <strong>DMP</strong> du patient et/ou via <strong>MSSanté</strong> au format <strong>CDA-R2 N1</strong>.
        </RegulatoryNote>
      </Card>

      {/* Sub 2 — Zone géographique */}
      <Card className="p-6">
        <div className="flex items-center gap-2 mb-1">
          <MapPin className="w-4 h-4" style={{ color: "var(--bp)" }} />
          <div className="text-[15px] font-semibold text-slate-900">Ma zone géographique</div>
        </div>
        <p className="text-[12.5px] text-slate-500 mb-4">Rayon dans lequel vos confrères peuvent vous solliciter (et où vous apparaissez en priorité).</p>
        <div>
          <label className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold block mb-2">Rayon de couverture</label>
          <div className="flex flex-wrap gap-2">
            {[10, 25, 50, 100, 0].map((r) => (
              <button
                key={r}
                onClick={() => setPref({ ...pref, radius: r })}
                data-testid={`radius-${r}`}
                className={`px-3.5 py-1.5 rounded-full text-[12.5px] border transition-colors ${
                  pref.radius === r ? "text-white border-transparent" : "bg-white text-slate-700 border-slate-300 hover:border-slate-400"
                }`}
                style={pref.radius === r ? { backgroundColor: "var(--bp)" } : {}}
              >
                {r === 0 ? "National" : `${r} km`}
              </button>
            ))}
          </div>
        </div>
        <div className="mt-4">
          <label className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold block mb-1">Adresse de référence</label>
          <input
            value={pref.address}
            onChange={(e) => setPref({ ...pref, address: e.target.value })}
            data-testid="reseau-pref-address"
            className="w-full border border-slate-300 rounded-md px-3 py-2 text-[13.5px] text-slate-900"
          />
          <p className="text-[11px] text-slate-400 mt-1">Auto-remplie depuis votre profil PSC / RPPS — modifiable si vous exercez sur plusieurs sites.</p>
        </div>
        <RegulatoryNote>
          Votre identité professionnelle est issue de l&apos;<strong>Annuaire Santé ANS</strong> (RPPS / ADELI). Votre numéro RPPS et votre adresse MSSanté sont
          utilisés pour votre référencement dans le réseau.
        </RegulatoryNote>
      </Card>

      {/* Sub 3 — Spécialités */}
      <Card className="p-6">
        <div className="flex items-center gap-2 mb-1">
          <Stethoscope className="w-4 h-4" style={{ color: "var(--bp)" }} />
          <div className="text-[15px] font-semibold text-slate-900">Spécialités que je souhaite solliciter</div>
        </div>
        <p className="text-[12.5px] text-slate-500 mb-4">Multi-sélection — issue de la nomenclature RPPS.</p>
        <div className="flex flex-wrap gap-2">
          {SPEC_OPTIONS.map((s) => {
            const active = pref.specialties.includes(s);
            return (
              <button
                key={s}
                onClick={() => toggleSpecialty(s)}
                data-testid={`reseau-spec-${s.toLowerCase().replace(/\s+/g, "-")}`}
                className={`px-3 py-1.5 rounded-full text-[12.5px] border transition-colors ${
                  active ? "text-white border-transparent" : "bg-white text-slate-700 border-slate-300 hover:border-slate-400"
                }`}
                style={active ? { backgroundColor: "var(--bp)" } : {}}
              >
                {s}
              </button>
            );
          })}
        </div>
      </Card>

      {/* Sub 4 — Moyens de contact */}
      <Card className="p-6">
        <div className="flex items-center gap-2 mb-1">
          <Send className="w-4 h-4" style={{ color: "var(--bp)" }} />
          <div className="text-[15px] font-semibold text-slate-900">Mes moyens de contact acceptés</div>
        </div>
        <p className="text-[12.5px] text-slate-500 mb-4">Comment vos confrères peuvent vous joindre depuis le réseau.</p>
        <div className="space-y-2.5">
          <CheckRow id="contactIM" label="Messagerie instantanée MetaConnect" sublabel="Coordination uniquement — pas de documents médicaux." checked={pref.contactIM} onChange={(v) => setPref({ ...pref, contactIM: v })} />
          <CheckRow id="contactMss" label="Messagerie MSSanté" sublabel="Pour les documents et échanges cliniques (CDA-R2 N1)." checked={pref.contactMss} onChange={(v) => setPref({ ...pref, contactMss: v })} />
          <CheckRow id="contactLk" label="Téléconsultation via LiveKit MetaConnect" checked={pref.contactLk} onChange={(v) => setPref({ ...pref, contactLk: v })} />
        </div>
        <div className="mt-4">
          <label className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold block mb-1">Adresse MSSanté</label>
          <input
            value={pref.mssAddress}
            onChange={(e) => setPref({ ...pref, mssAddress: e.target.value })}
            data-testid="reseau-pref-mss"
            className="w-full border border-slate-300 rounded-md px-3 py-2 text-[13.5px] text-slate-900"
          />
          <p className="text-[11px] text-slate-400 mt-1">Auto-remplie depuis votre profil ou à renseigner manuellement (format <code>@*.mssante.fr</code>).</p>
        </div>
        <RegulatoryNote>
          Les échanges de documents médicaux entre PS doivent transiter par <strong>MSSanté</strong> (Référentiel socle MSSanté <strong>#2 V1.0.1</strong>).
          La messagerie instantanée MetaConnect est réservée à la coordination — elle ne peut pas transporter de documents de santé.
        </RegulatoryNote>
      </Card>

      <div className="flex items-center gap-3 sticky bottom-0 bg-white py-3 -mx-6 px-6 border-t border-slate-200">
        <Button
          className="text-white"
          style={{ backgroundColor: "var(--bp)" }}
          data-testid="reseau-pref-save"
          disabled={saving}
          onClick={handleSave}
        >
          {saving ? "Enregistrement…" : "Enregistrer mes préférences"}
        </Button>
        <span className="text-[11.5px] text-slate-400">PATCH <code>/api/pro/reseau-preferences</code></span>
      </div>
    </div>
  );
}

function CheckRow({ id, label, sublabel, checked, onChange }) {
  return (
    <label className="flex items-start gap-2.5 cursor-pointer group" data-testid={`check-${id}`}>
      <input
        type="checkbox"
        checked={!!checked}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-0.5 h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-2 focus:ring-emerald-200"
      />
      <div className="flex-1">
        <div className="text-[13.5px] text-slate-900 leading-snug">{label}</div>
        {sublabel && <div className="text-[11.5px] text-slate-500 mt-0.5">{sublabel}</div>}
      </div>
    </label>
  );
}

function RegulatoryNote({ children }) {
  return (
    <div className="mt-4 rounded-md border border-blue-200 bg-blue-50/70 p-3 flex items-start gap-2 text-[11.5px] text-blue-900 leading-relaxed" data-testid="regulatory-note">
      <Info className="w-3.5 h-3.5 mt-0.5 shrink-0 text-blue-600" />
      <div>{children}</div>
    </div>
  );
}

/* ----------- Leaflet helpers ----------- */
const SPECIALTY_COLORS = {
  "Médecin généraliste": "#0F2B5B",
  "Cardiologue":         "#DC2626",
  "Dermatologue":        "#EC4899",
  "Endocrinologue":      "#F59E0B",
  "Neurologue":          "#7C3AED",
  "Ophtalmologiste":     "#0EA5E9",
  "Pneumologue":         "#06B6D4",
  "Pédiatre":            "#10B981",
  "Psychiatre":          "#8B5CF6",
  "Gynécologue":         "#F472B6",
  "Infirmier libéral":   "#A855F7",
  "Sage-femme":          "#14B8A6",
  "Kinésithérapeute":    "#84CC16",
  "ORL":                 "#22C55E",
};
const getSpecColor = (s) => SPECIALTY_COLORS[s] || "#64748B";

// 30+ practitioners spread across major French cities — used as fallback if backend returns < 20
const FALLBACK_PRACTICIANS = [
  // Paris (5)
  { id: "fp1",  first_name: "Sophie",  last_name: "Martin",   specialty: "Médecin généraliste", profession: "MG", city: "Paris",   cp: "75011", rpps: "10003456712", lat: 48.8589, lng: 2.3471, accept_teleconsult_assistee: true,  accept_teleexpertise: true,  accept_expertise_image: false, mss: "sophie.martin@paris.mssante.fr" },
  { id: "fp2",  first_name: "Pierre",  last_name: "Durand",   specialty: "Cardiologue",         profession: "Cardio", city: "Paris", cp: "75016", rpps: "10003456713", lat: 48.8534, lng: 2.2649, accept_teleconsult_assistee: false, accept_teleexpertise: true,  accept_expertise_image: true,  mss: "p.durand@paris.mssante.fr" },
  { id: "fp3",  first_name: "Léa",     last_name: "Bernard",  specialty: "Dermatologue",        profession: "Derm", city: "Paris", cp: "75008", rpps: "10003456714", lat: 48.8736, lng: 2.3060, accept_teleconsult_assistee: true,  accept_teleexpertise: true,  accept_expertise_image: true,  mss: "l.bernard@paris.mssante.fr" },
  { id: "fp4",  first_name: "Karim",   last_name: "Boudjema", specialty: "Pédiatre",            profession: "Péd", city: "Paris", cp: "75019", rpps: "10003456715", lat: 48.8870, lng: 2.3760, accept_teleconsult_assistee: true,  accept_teleexpertise: false, accept_expertise_image: false, mss: "k.boudjema@paris.mssante.fr" },
  { id: "fp5",  first_name: "Marie",   last_name: "Lambert",  specialty: "Psychiatre",          profession: "Psy", city: "Paris", cp: "75006", rpps: "10003456716", lat: 48.8500, lng: 2.3325, accept_teleconsult_assistee: true,  accept_teleexpertise: true,  accept_expertise_image: false, mss: "m.lambert@paris.mssante.fr" },
  // Lyon (4)
  { id: "fp6",  first_name: "Antoine", last_name: "Rousseau", specialty: "Médecin généraliste", profession: "MG", city: "Lyon",   cp: "69002", rpps: "10003456717", lat: 45.7560, lng: 4.8290, accept_teleconsult_assistee: true,  accept_teleexpertise: true,  accept_expertise_image: false, mss: "a.rousseau@lyon.mssante.fr" },
  { id: "fp7",  first_name: "Camille", last_name: "Dubois",   specialty: "Neurologue",          profession: "Neuro", city: "Lyon", cp: "69003", rpps: "10003456718", lat: 45.7610, lng: 4.8517, accept_teleconsult_assistee: false, accept_teleexpertise: true,  accept_expertise_image: true,  mss: "c.dubois@lyon.mssante.fr" },
  { id: "fp8",  first_name: "Julien",  last_name: "Petit",    specialty: "Gynécologue",         profession: "Gyn", city: "Lyon", cp: "69006", rpps: "10003456719", lat: 45.7700, lng: 4.8500, accept_teleconsult_assistee: true,  accept_teleexpertise: false, accept_expertise_image: false, mss: "j.petit@lyon.mssante.fr" },
  { id: "fp9",  first_name: "Sarah",   last_name: "Khelil",   specialty: "Kinésithérapeute",    profession: "Kiné", city: "Lyon", cp: "69001", rpps: "10003456720", lat: 45.7670, lng: 4.8336, accept_teleconsult_assistee: true,  accept_teleexpertise: false, accept_expertise_image: false, mss: "s.khelil@lyon.mssante.fr" },
  // Marseille (4)
  { id: "fp10", first_name: "Marc",    last_name: "Garcia",   specialty: "Médecin généraliste", profession: "MG", city: "Marseille", cp: "13008", rpps: "10003456721", lat: 43.2670, lng: 5.3960, accept_teleconsult_assistee: true,  accept_teleexpertise: true,  accept_expertise_image: false, mss: "m.garcia@mars.mssante.fr" },
  { id: "fp11", first_name: "Inès",    last_name: "Romano",   specialty: "Cardiologue",         profession: "Cardio", city: "Marseille", cp: "13006", rpps: "10003456722", lat: 43.2890, lng: 5.3850, accept_teleconsult_assistee: false, accept_teleexpertise: true,  accept_expertise_image: true,  mss: "i.romano@mars.mssante.fr" },
  { id: "fp12", first_name: "Lucas",   last_name: "Moreau",   specialty: "Ophtalmologiste",     profession: "Ophta", city: "Marseille", cp: "13001", rpps: "10003456723", lat: 43.2965, lng: 5.3698, accept_teleconsult_assistee: true,  accept_teleexpertise: true,  accept_expertise_image: true,  mss: "l.moreau@mars.mssante.fr" },
  { id: "fp13", first_name: "Aïcha",   last_name: "Benali",   specialty: "Infirmier libéral",   profession: "IDEL", city: "Marseille", cp: "13013", rpps: "10003456724", lat: 43.3380, lng: 5.4150, accept_teleconsult_assistee: true,  accept_teleexpertise: false, accept_expertise_image: false, mss: "a.benali@mars.mssante.fr" },
  // Bordeaux (3)
  { id: "fp14", first_name: "Hugo",    last_name: "Lefevre",  specialty: "Médecin généraliste", profession: "MG", city: "Bordeaux", cp: "33000", rpps: "10003456725", lat: 44.8378, lng: -0.5792, accept_teleconsult_assistee: true,  accept_teleexpertise: true,  accept_expertise_image: false, mss: "h.lefevre@bx.mssante.fr" },
  { id: "fp15", first_name: "Emma",    last_name: "Girard",   specialty: "Endocrinologue",      profession: "Endo", city: "Bordeaux", cp: "33200", rpps: "10003456726", lat: 44.8550, lng: -0.5900, accept_teleconsult_assistee: false, accept_teleexpertise: true,  accept_expertise_image: false, mss: "e.girard@bx.mssante.fr" },
  { id: "fp16", first_name: "Théo",    last_name: "Bonnet",   specialty: "Pneumologue",         profession: "Pneumo", city: "Bordeaux", cp: "33700", rpps: "10003456727", lat: 44.8275, lng: -0.5650, accept_teleconsult_assistee: true,  accept_teleexpertise: true,  accept_expertise_image: true,  mss: "t.bonnet@bx.mssante.fr" },
  // Lille (3)
  { id: "fp17", first_name: "Chloé",   last_name: "Vincent",  specialty: "Médecin généraliste", profession: "MG", city: "Lille",  cp: "59000", rpps: "10003456728", lat: 50.6292, lng: 3.0573, accept_teleconsult_assistee: true,  accept_teleexpertise: true,  accept_expertise_image: false, mss: "c.vincent@lille.mssante.fr" },
  { id: "fp18", first_name: "Yacine",  last_name: "Mahdi",    specialty: "Sage-femme",          profession: "SF", city: "Lille", cp: "59800", rpps: "10003456729", lat: 50.6420, lng: 3.0710, accept_teleconsult_assistee: true,  accept_teleexpertise: false, accept_expertise_image: false, mss: "y.mahdi@lille.mssante.fr" },
  { id: "fp19", first_name: "Manon",   last_name: "Robert",   specialty: "Dermatologue",        profession: "Derm", city: "Lille", cp: "59000", rpps: "10003456730", lat: 50.6300, lng: 3.0670, accept_teleconsult_assistee: false, accept_teleexpertise: true,  accept_expertise_image: true,  mss: "m.robert@lille.mssante.fr" },
  // Toulouse (3)
  { id: "fp20", first_name: "Nicolas", last_name: "Faure",    specialty: "Médecin généraliste", profession: "MG", city: "Toulouse", cp: "31000", rpps: "10003456731", lat: 43.6047, lng: 1.4442, accept_teleconsult_assistee: true,  accept_teleexpertise: true,  accept_expertise_image: false, mss: "n.faure@tls.mssante.fr" },
  { id: "fp21", first_name: "Élodie",  last_name: "Aubry",    specialty: "Cardiologue",         profession: "Cardio", city: "Toulouse", cp: "31400", rpps: "10003456732", lat: 43.5800, lng: 1.4700, accept_teleconsult_assistee: false, accept_teleexpertise: true,  accept_expertise_image: true,  mss: "e.aubry@tls.mssante.fr" },
  { id: "fp22", first_name: "Mohamed", last_name: "Saidi",    specialty: "ORL",                 profession: "ORL", city: "Toulouse", cp: "31200", rpps: "10003456733", lat: 43.6240, lng: 1.4385, accept_teleconsult_assistee: true,  accept_teleexpertise: true,  accept_expertise_image: false, mss: "m.saidi@tls.mssante.fr" },
  // Nantes (3)
  { id: "fp23", first_name: "Pauline", last_name: "Renault",  specialty: "Médecin généraliste", profession: "MG", city: "Nantes", cp: "44000", rpps: "10003456734", lat: 47.2173, lng: -1.5534, accept_teleconsult_assistee: true,  accept_teleexpertise: false, accept_expertise_image: false, mss: "p.renault@nts.mssante.fr" },
  { id: "fp24", first_name: "Romain",  last_name: "Mercier",  specialty: "Kinésithérapeute",    profession: "Kiné", city: "Nantes", cp: "44100", rpps: "10003456735", lat: 47.2050, lng: -1.5800, accept_teleconsult_assistee: true,  accept_teleexpertise: false, accept_expertise_image: false, mss: "r.mercier@nts.mssante.fr" },
  { id: "fp25", first_name: "Léna",    last_name: "Caron",    specialty: "Psychiatre",          profession: "Psy", city: "Nantes", cp: "44200", rpps: "10003456736", lat: 47.2200, lng: -1.5400, accept_teleconsult_assistee: true,  accept_teleexpertise: true,  accept_expertise_image: false, mss: "l.caron@nts.mssante.fr" },
  // Strasbourg (2)
  { id: "fp26", first_name: "Florent", last_name: "Weber",    specialty: "Médecin généraliste", profession: "MG", city: "Strasbourg", cp: "67000", rpps: "10003456737", lat: 48.5734, lng: 7.7521, accept_teleconsult_assistee: true,  accept_teleexpertise: true,  accept_expertise_image: false, mss: "f.weber@stg.mssante.fr" },
  { id: "fp27", first_name: "Anna",    last_name: "Schmitt",  specialty: "Gynécologue",         profession: "Gyn", city: "Strasbourg", cp: "67100", rpps: "10003456738", lat: 48.5500, lng: 7.7700, accept_teleconsult_assistee: false, accept_teleexpertise: true,  accept_expertise_image: false, mss: "a.schmitt@stg.mssante.fr" },
  // Montpellier (2)
  { id: "fp28", first_name: "David",   last_name: "Pasquier", specialty: "Médecin généraliste", profession: "MG", city: "Montpellier", cp: "34000", rpps: "10003456739", lat: 43.6109, lng: 3.8772, accept_teleconsult_assistee: true,  accept_teleexpertise: false, accept_expertise_image: false, mss: "d.pasquier@mtp.mssante.fr" },
  { id: "fp29", first_name: "Salomé",  last_name: "Olivier",  specialty: "Dermatologue",        profession: "Derm", city: "Montpellier", cp: "34000", rpps: "10003456740", lat: 43.6250, lng: 3.8700, accept_teleconsult_assistee: true,  accept_teleexpertise: true,  accept_expertise_image: true,  mss: "s.olivier@mtp.mssante.fr" },
  // Nice (2)
  { id: "fp30", first_name: "Vincent", last_name: "Costa",    specialty: "Médecin généraliste", profession: "MG", city: "Nice",   cp: "06000", rpps: "10003456741", lat: 43.7102, lng: 7.2620, accept_teleconsult_assistee: true,  accept_teleexpertise: true,  accept_expertise_image: false, mss: "v.costa@nice.mssante.fr" },
  { id: "fp31", first_name: "Clara",   last_name: "Léger",    specialty: "Ophtalmologiste",     profession: "Ophta", city: "Nice", cp: "06200", rpps: "10003456742", lat: 43.7050, lng: 7.2400, accept_teleconsult_assistee: false, accept_teleexpertise: true,  accept_expertise_image: true,  mss: "c.leger@nice.mssante.fr" },
];

function makeDivIcon(color, pulsing = false) {
  const html = `
    <div style="position: relative;">
      ${pulsing ? `<span style="position:absolute; inset:-6px; border-radius:50%; background:${color}; opacity:0.35; animation: mc-pulse 1.4s ease-out infinite;"></span>` : ""}
      <div style="width: 22px; height: 22px; border-radius: 50%; background: ${color}; border: 3px solid white; box-shadow: 0 1px 4px rgba(0,0,0,.25);"></div>
    </div>
    <style>@keyframes mc-pulse { 0%{transform:scale(0.95);opacity:.5} 70%{transform:scale(1.7);opacity:0} 100%{transform:scale(1.7);opacity:0} }</style>
  `;
  return L.divIcon({ html, className: "", iconSize: [22, 22], iconAnchor: [11, 11], popupAnchor: [0, -12] });
}

function FlyTo({ position, zoom }) {
  const map = useMap();
  useEffect(() => {
    if (position) map.flyTo(position, zoom || 11, { duration: 1.2 });
  }, [position, zoom, map]);
  return null;
}

function PageReseau({ brand }) {
  const [query, setQuery] = useState("");
  const [profession, setProfession] = useState("Tous");
  const [filterAssisted, setFilterAssisted] = useState(false);
  const [filterExpertise, setFilterExpertise] = useState(false);
  const [radius, setRadius] = useState(50); // km
  const [selectedId, setSelectedId] = useState(null);
  const [composer, setComposer] = useState(null);
  // Network state — fetched from backend
  const [praticiens, setPraticiens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null); // {total_france, dept_XX: n}
  const [searchResults, setSearchResults] = useState([]); // live ANS results
  const [ansSource, setAnsSource] = useState(""); // "ans-fhir" | "fallback-seed"
  const [userGeo, setUserGeo] = useState(null); // {lat, lng} from navigator (optional)

  // Browser geolocation — optional, silent if denied.
  useEffect(() => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => setUserGeo({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
      () => {},
      { timeout: 5000, maximumAge: 300000 }
    );
  }, []);

  // Fetch the practitioner list whenever filters change.
  useEffect(() => {
    const ctrl = new AbortController();
    const params = new URLSearchParams();
    if (profession !== "Tous") params.set("specialite", profession);
    if (filterAssisted) params.set("teleconsult", "true");
    if (filterExpertise) params.set("teleexpertise", "true");
    if (userGeo) {
      params.set("lat", userGeo.lat);
      params.set("lng", userGeo.lng);
      params.set("rayon_km", String(radius));
    }
    setLoading(true);
    fetch(`${API_BASE}/reseau/praticiens?${params.toString()}`, { signal: ctrl.signal })
      .then((r) => r.json())
      .then((data) => {
        const arr = Array.isArray(data) ? data : [];
        // If backend has too few practitioners, supplement with our 30+ fallback (filtered by current selection)
        if (arr.length < 20) {
          let extra = FALLBACK_PRACTICIANS;
          if (profession !== "Tous") extra = extra.filter((p) => p.specialty === profession);
          if (filterAssisted) extra = extra.filter((p) => p.accept_teleconsult_assistee);
          if (filterExpertise) extra = extra.filter((p) => p.accept_teleexpertise);
          // Merge, dedup by id, keep backend entries first
          const ids = new Set(arr.map((p) => p.id));
          setPraticiens([...arr, ...extra.filter((p) => !ids.has(p.id))]);
        } else {
          setPraticiens(arr);
        }
      })
      .catch(() => {
        // Backend offline → fallback to our local seed
        let extra = FALLBACK_PRACTICIANS;
        if (profession !== "Tous") extra = extra.filter((p) => p.specialty === profession);
        if (filterAssisted) extra = extra.filter((p) => p.accept_teleconsult_assistee);
        if (filterExpertise) extra = extra.filter((p) => p.accept_teleexpertise);
        setPraticiens(extra);
      })
      .finally(() => setLoading(false));
    return () => ctrl.abort();
  }, [profession, filterAssisted, filterExpertise, radius, userGeo]);

  // Fetch stats once + every 5 min.
  useEffect(() => {
    let alive = true;
    const load = () => fetch(`${API_BASE}/reseau/stats`).then((r) => r.json()).then((d) => { if (alive) setStats(d); }).catch(() => {});
    load();
    const id = setInterval(load, 5 * 60 * 1000);
    return () => { alive = false; clearInterval(id); };
  }, []);

  // Debounced live ANS search — RPPS digits only.
  useEffect(() => {
    const q = query.trim();
    if (q.length < 3 || !/^\d+$/.test(q)) {
      setSearchResults([]);
      setAnsSource("");
      return;
    }
    const ctrl = new AbortController();
    const id = setTimeout(() => {
      fetch(`${API_BASE}/reseau/ans-search?q=${encodeURIComponent(q)}&limit=8`, { signal: ctrl.signal })
        .then((r) => r.json())
        .then((d) => {
          setSearchResults(Array.isArray(d.results) ? d.results : []);
          setAnsSource(d.source || "");
        })
        .catch(() => {});
    }, 250);
    return () => { clearTimeout(id); ctrl.abort(); };
  }, [query]);

  const selected = useMemo(() => praticiens.find((p) => p.id === selectedId) || null, [praticiens, selectedId]);

  const handleSelect = (idOrRpps) => {
    // The ANS search returns FHIR-shaped objects (no full PS profile) — match by RPPS.
    const ps = praticiens.find((p) => p.id === idOrRpps) || praticiens.find((p) => p.rpps && p.rpps === idOrRpps);
    if (ps) {
      setSelectedId(ps.id);
    } else if (idOrRpps && idOrRpps.length >= 4) {
      // ANS result that we don't have in our seed yet — surface a toast.
      toast.info("Ce praticien n'a pas encore activé son profil MetaConnect.");
    }
    setQuery("");
    setSearchResults([]);
  };

  const openComposer = (ps) => {
    const subject = `Demande d'acte de télémédecine — Patient anonymisé — ${new Date().toLocaleDateString("fr-FR")}`;
    const body = `TypeCode : 75496-0 (Demande d'acte de télémédecine, Volet TLM ANS)
De : Dr ${"Marie DUPONT"} — Médecin généraliste — RPPS 10003995312
À : ${ps.mss}

Bonjour,
Je sollicite votre expertise dans le cadre d'une téléconsultation assistée.
Motif : [préciser]
Matériel disponible côté patient : Tensiomètre BLE, Saturomètre BLE
[champ libre]

Cordialement,`;
    setComposer({ ps, subject, body });
  };

  const sendComposer = () => {
    if (!composer) return;
    const href = `mailto:${composer.ps.mss}?subject=${encodeURIComponent(composer.subject)}&body=${encodeURIComponent(composer.body)}`;
    window.location.href = href;
    setTimeout(() => setComposer(null), 300);
  };

  // Derived: total + my dept count for the banner.
  const myDeptCount = (() => {
    if (!stats) return 0;
    // Pick the largest available dept around the user position; fallback to first dept entry.
    const userCp = selected?.cp || (praticiens[0] && praticiens[0].cp) || "";
    const userDept = userCp.slice(0, 2);
    if (userDept && stats[`dept_${userDept}`]) return stats[`dept_${userDept}`];
    return 0;
  })();

  return (
    <div data-testid="page-reseau" className="relative">
      <PageHeader
        title="Réseau de proximité"
        subtitle={`Carte des professionnels MetaConnect — ${brand?.name ? `pour ${brand.name}` : "réseau territorial"}.`}
      />

      {/* Search bar — RPPS digits only (ANS public dataset doesn't expose name search) */}
      <div className="mb-3 max-w-xl">
        <div className="relative">
          <div className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">
            <Search className="w-4 h-4" />
          </div>
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            inputMode="numeric"
            placeholder="Rechercher par numéro RPPS (11 chiffres)…"
            data-testid="reseau-search"
            className="w-full pl-10 pr-3 py-2.5 rounded-md bg-white border border-slate-300 text-slate-900 text-[13.5px] placeholder:text-slate-400 outline-none focus:border-slate-400"
          />
        </div>
        {/* Inline validation — block any letter input */}
        {query.trim() && !/^\d*$/.test(query.trim()) ? (
          <div className="mt-1.5 text-[11.5px] text-amber-700 inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-amber-50 border border-amber-200" data-testid="rpps-invalid-warning">
            <AlertTriangle className="w-3 h-3" /> Veuillez saisir uniquement le numéro RPPS (11 chiffres)
          </div>
        ) : (
          <div className="mt-1.5 text-[11.5px] text-slate-500 inline-flex items-start gap-1 leading-snug" data-testid="rpps-help">
            <Info className="w-3 h-3 mt-0.5 shrink-0 text-slate-400" />
            <span>
              La recherche par nom n&apos;est pas disponible sur l&apos;annuaire public ANS.
              Saisissez le numéro <strong>RPPS à 11 chiffres</strong> pour trouver un professionnel.
            </span>
          </div>
        )}
        {ansSource === "fallback-seed" && query.trim().length >= 3 && (
          <div className="mt-1 inline-flex items-center gap-1 text-[10.5px] text-amber-700 px-1.5 py-0.5 rounded bg-amber-50 border border-amber-200" data-testid="ans-fallback-badge">
            <Info className="w-3 h-3" /> Annuaire local — ANS Annuaire Santé non connecté
          </div>
        )}
        {searchResults.length > 0 && (
          <div className="relative">
            <div className="absolute top-1 left-0 right-0 bg-white border border-slate-200 rounded-md shadow-lg z-[1100] overflow-hidden" data-testid="reseau-search-dropdown">
              {searchResults.map((p) => {
                const displayName = [p.first_name, p.last_name].filter(Boolean).join(" ") || p.prefix || "Praticien ANS";
                return (
                  <button
                    key={p.fhir_id || p.rpps}
                    onClick={() => handleSelect(p.rpps || p.fhir_id)}
                    data-testid={`reseau-search-result-${p.fhir_id || p.rpps}`}
                    className="w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-slate-50 border-b border-slate-100 last:border-b-0"
                  >
                    <div className="h-8 w-8 rounded-full grid place-items-center text-white text-[10.5px] font-semibold shrink-0" style={{ backgroundColor: "var(--bp)" }}>
                      {(p.first_name || p.prefix || "?")[0]}{(p.last_name || "P")[0]}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-[13px] font-semibold text-slate-900 truncate">Dr {displayName}</div>
                      <div className="text-[11px] text-slate-500 truncate">{p.profession || "Professionnel de santé"}{p.city ? ` · ${p.city}` : ""} · RPPS {p.rpps || "—"}</div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Map + Filters */}
      <Card className="overflow-hidden">
        <div className="relative h-[540px]">
          {loading && praticiens.length === 0 && (
            <div className="absolute inset-0 z-[800] bg-slate-50/80 grid place-items-center" data-testid="reseau-loading">
              <div className="text-[12.5px] text-slate-500 inline-flex items-center gap-2">
                <RefreshCw className="w-3.5 h-3.5 animate-spin" /> Chargement du réseau…
              </div>
            </div>
          )}
          <MapContainer
            center={[46.5, 2.3]}
            zoom={6}
            style={{ height: "100%", width: "100%" }}
            scrollWheelZoom
            data-testid="reseau-map"
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            <FlyTo position={selected ? [selected.lat, selected.lng] : null} zoom={11} />
            {praticiens.filter((p) => p.lat != null && p.lng != null).map((p) => {
              const isSel = selectedId === p.id;
              const specColor = getSpecColor(p.specialty || p.profession);
              return (
                <Marker
                  key={p.id}
                  position={[p.lat, p.lng]}
                  icon={makeDivIcon(isSel ? "#F97316" : specColor, isSel)}
                  eventHandlers={{ click: () => handleSelect(p.id) }}
                >
                  <Popup>
                    <div className="text-[12px]" data-testid={`marker-popup-${p.id}`}>
                      <div className="font-semibold text-slate-900 flex items-center gap-1.5">
                        <span className="w-2 h-2 rounded-full" style={{ background: specColor }} />
                        Dr {p.first_name} {p.last_name}
                      </div>
                      <div className="text-[11px] mt-0.5" style={{ color: specColor, fontWeight: 600 }}>{p.specialty || p.profession}</div>
                      <div className="text-slate-500 mt-0.5">📍 {p.city}{p.cp ? `, ${p.cp}` : ""} · RPPS {p.rpps || "—"}</div>
                      <div className="mt-1 space-y-0.5">
                        {p.accept_teleconsult_assistee && <div>✅ Téléconsultation assistée</div>}
                        {p.accept_teleexpertise && <div>✅ Téléexpertise (Volet TLM ANS)</div>}
                        {p.accept_expertise_image && <div>✅ Expertise image DICOM</div>}
                      </div>
                      <div className="mt-1.5 flex gap-1.5">
                        <button onClick={() => handleSelect(p.id)} className="text-[11px] text-white rounded px-2 py-0.5" style={{ backgroundColor: "var(--bp)" }}>Voir fiche</button>
                        {p.mss && <button onClick={(e) => { e.stopPropagation(); openComposer(p); }} className="text-[11px] rounded px-2 py-0.5 bg-emerald-600 text-white">Mail MSSanté</button>}
                      </div>
                    </div>
                  </Popup>
                </Marker>
              );
            })}
          </MapContainer>

          {/* Legend — color code by specialty */}
          <div
            className="absolute bottom-3 right-3 z-[1000] bg-white/95 backdrop-blur rounded-lg shadow-lg border border-slate-200 p-3 text-[11px] max-h-[280px] overflow-y-auto"
            data-testid="reseau-legend"
            style={{ maxWidth: 200 }}
          >
            <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-2 flex items-center justify-between">
              <span>Spécialités</span>
              <span className="text-slate-400 font-normal normal-case tracking-normal">{praticiens.length} PS</span>
            </div>
            <div className="space-y-1.5">
              {Object.entries(SPECIALTY_COLORS)
                .filter(([spec]) => praticiens.some((p) => (p.specialty || p.profession) === spec))
                .map(([spec, color]) => (
                  <button
                    key={spec}
                    onClick={() => setProfession(profession === spec ? "Tous" : spec)}
                    className={`w-full flex items-center gap-2 text-left hover:bg-slate-50 rounded px-1 py-0.5 transition-colors ${profession === spec ? "bg-slate-100 font-semibold" : ""}`}
                  >
                    <span className="w-3 h-3 rounded-full shrink-0 border-2 border-white shadow-sm" style={{ background: color }} />
                    <span className="text-slate-700 truncate">{spec}</span>
                    <span className="text-slate-400 text-[10px] ml-auto">{praticiens.filter((p) => (p.specialty || p.profession) === spec).length}</span>
                  </button>
                ))}
            </div>
          </div>

          {/* Floating filters */}
          <div className="absolute top-3 left-3 z-[1000] bg-white rounded-lg shadow-lg border border-slate-200 p-3 flex flex-wrap items-center gap-2 max-w-[460px]" data-testid="reseau-filters">
            <select
              value={profession}
              onChange={(e) => setProfession(e.target.value)}
              data-testid="filter-profession"
              className="text-[11.5px] px-2 py-1 rounded border border-slate-300 text-slate-900 bg-white font-medium"
            >
              {RESEAU_PROFESSIONS.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
            <button
              onClick={() => setFilterAssisted((v) => !v)}
              data-testid="filter-assisted"
              className={`text-[11.5px] px-2.5 py-1 rounded-full border transition-colors ${
                filterAssisted ? "text-white border-transparent" : "bg-white text-slate-600 border-slate-300"
              }`}
              style={filterAssisted ? { backgroundColor: "var(--bp)" } : {}}
            >
              Téléconsultation assistée
            </button>
            <button
              onClick={() => setFilterExpertise((v) => !v)}
              data-testid="filter-expertise"
              className={`text-[11.5px] px-2.5 py-1 rounded-full border transition-colors ${
                filterExpertise ? "text-white border-transparent" : "bg-white text-slate-600 border-slate-300"
              }`}
              style={filterExpertise ? { backgroundColor: "var(--bp)" } : {}}
            >
              Téléexpertise
            </button>
            <label className="flex items-center gap-1.5 text-[11.5px] text-slate-600 ml-1">
              Rayon
              <input
                type="range" min={10} max={300} step={10}
                value={radius}
                onChange={(e) => setRadius(Number(e.target.value))}
                data-testid="filter-radius"
                className="w-24 accent-slate-700"
              />
              <span className="font-mono text-slate-700 w-12">{radius} km</span>
            </label>
          </div>

          {/* Legend */}
          <div className="absolute bottom-3 left-3 z-[1000] bg-white/95 rounded-md shadow-md border border-slate-200 px-3 py-2 text-[10.5px] text-slate-600 flex items-center gap-3">
            <span className="flex items-center gap-1"><span className="inline-block h-2.5 w-2.5 rounded-full" style={{ backgroundColor: "var(--bp)" }} /> PS réseau MetaConnect</span>
            <span className="flex items-center gap-1"><span className="inline-block h-2.5 w-2.5 rounded-full bg-orange-500" /> PS sélectionné</span>
            <span className="ml-2 text-slate-400">{praticiens.length} PS visible{praticiens.length > 1 ? "s" : ""}</span>
          </div>
        </div>
      </Card>

      {/* Public stats banner — only when reasonable volume to avoid the "desert effect" */}
      {stats && stats.total_france >= 5 && (
        <div
          data-testid="reseau-stats-banner"
          className="mt-3 rounded-md border border-slate-200 bg-slate-50/80 px-4 py-2 text-[12px] text-slate-700 flex items-center gap-2"
        >
          <Stethoscope className="w-3.5 h-3.5" style={{ color: "var(--bp)" }} />
          <span>
            <span className="font-semibold text-slate-900" data-testid="reseau-stats-total">{stats.total_france}</span> professionnels MetaConnect en France
            {myDeptCount > 0 && (
              <>
                {" · "}
                <span className="font-semibold text-slate-900" data-testid="reseau-stats-dept">{myDeptCount}</span> dans votre département
              </>
            )}
          </span>
        </div>
      )}

      {!loading && praticiens.length === 0 && (
        <div className="mt-3 rounded-md border border-slate-200 bg-slate-50 px-4 py-3 text-[12.5px] text-slate-600" data-testid="reseau-no-results">
          Aucun professionnel ne correspond à vos filtres. Élargissez le rayon ou changez la spécialité.
        </div>
      )}

      <p className="mt-3 text-[11.5px] text-slate-500 flex items-start gap-1.5">
        <Info className="w-3 h-3 mt-0.5 shrink-0" />
        Carte alimentée par OpenStreetMap. L&apos;identité des PS est issue de l&apos;Annuaire Santé ANS (RPPS/ADELI). Les disponibilités sont mises à jour toutes les 15 minutes.
      </p>

      {selected && (
        <ReseauPsDrawer
          ps={selected}
          onClose={() => setSelectedId(null)}
          onOpenMss={() => openComposer(selected)}
        />
      )}

      {composer && (
        <ReseauMssComposer
          composer={composer}
          onChange={setComposer}
          onCancel={() => setComposer(null)}
          onSend={sendComposer}
        />
      )}
    </div>
  );
}

function ReseauPsDrawer({ ps, onClose, onOpenMss }) {
  return (
    <div className="fixed inset-0 z-[1200] pointer-events-none" data-testid="reseau-drawer">
      <div className="absolute top-0 right-0 h-full w-full sm:w-[420px] bg-white shadow-2xl border-l border-slate-200 flex flex-col pointer-events-auto" style={{ animation: "slideInRight .22s ease" }}>
        <div className="px-4 py-3 border-b border-slate-200 flex items-center gap-2">
          <button onClick={onClose} data-testid="reseau-drawer-close" className="text-[12px] text-slate-500 hover:text-slate-700 inline-flex items-center gap-1"><ArrowLeft className="w-3.5 h-3.5" /> Retour</button>
        </div>
        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          <div className="flex items-center gap-3">
            <div className="h-12 w-12 rounded-full grid place-items-center text-white text-[14px] font-semibold shrink-0" style={{ backgroundColor: "var(--bp)" }}>
              {(ps.first_name || "?")[0]}{(ps.last_name || "?")[0]}
            </div>
            <div className="min-w-0">
              <div className="text-[15px] font-semibold text-slate-900">Dr {ps.first_name} {ps.last_name}</div>
              <div className="text-[12px] text-slate-500">{ps.profession}{ps.specialty ? ` — ${ps.specialty}` : ""}</div>
            </div>
          </div>

          <div className="text-[12.5px] text-slate-700 space-y-1">
            {ps.rpps && <div><span className="text-slate-500">RPPS</span> <span className="font-mono">{ps.rpps}</span></div>}
            {(ps.city || ps.cp) && <div className="flex items-center gap-1"><MapPin className="w-3 h-3 text-slate-400" /> {ps.city}{ps.cp ? `, ${ps.cp}` : ""}{ps.dept ? ` · ${ps.dept}` : ""}</div>}
            {ps.distance_km != null && <div className="text-[11.5px] text-slate-500">📏 {ps.distance_km} km de votre position</div>}
            {ps.phone && <div className="flex items-center gap-1"><Phone className="w-3 h-3 text-slate-400" /> {ps.phone}</div>}
            {ps.mss && <div className="flex items-center gap-1 break-all"><Mail className="w-3 h-3 text-slate-400" /> {ps.mss}</div>}
          </div>

          <div>
            <div className="text-[10.5px] uppercase tracking-wider text-slate-500 font-semibold mb-1.5">Disponibilités réseau</div>
            <ul className="text-[12.5px] space-y-0.5">
              <li className={ps.accept_teleconsult_assistee ? "text-emerald-700" : "text-slate-400"}>{ps.accept_teleconsult_assistee ? "✅" : "❌"} Téléconsultation assistée</li>
              <li className={ps.accept_teleexpertise ? "text-emerald-700" : "text-slate-400"}>{ps.accept_teleexpertise ? "✅" : "❌"} Téléexpertise / avis spécialisé</li>
              <li className={ps.accept_expertise_image ? "text-emerald-700" : "text-slate-400"}>{ps.accept_expertise_image ? "✅" : "❌"} Expertise image DICOM</li>
            </ul>
          </div>

          {ps.devices?.length > 0 && (
            <div>
              <div className="text-[10.5px] uppercase tracking-wider text-slate-500 font-semibold mb-1.5">Matériel déclaré</div>
              <ul className="text-[12.5px] text-slate-700 space-y-0.5">
                {ps.devices.map((d) => <li key={d}>• {d}</li>)}
              </ul>
            </div>
          )}

          <div className="rounded-md border border-blue-200 bg-blue-50/70 p-3 flex items-start gap-2 text-[11px] text-blue-900 leading-relaxed">
            <Info className="w-3.5 h-3.5 mt-0.5 shrink-0 text-blue-600" />
            <div>
              Tout document produit lors d&apos;une téléexpertise doit être transmis au <strong>DMP</strong> et/ou via <strong>MSSanté</strong> au format CDA-R2 N1
              (ANS <strong>REF ISE TLC V1.5.1</strong>).
            </div>
          </div>
        </div>
        <div className="p-4 border-t border-slate-200 space-y-2">
          <button
            onClick={() => toast.info("Conversation MetaConnect ouverte (mock).")}
            data-testid="reseau-drawer-contact-im"
            className="w-full inline-flex items-center justify-center gap-2 px-3 py-2 rounded-md text-white text-[13px] font-semibold"
            style={{ backgroundColor: "var(--bp)" }}
          >
            <MessageSquare className="w-3.5 h-3.5" /> Contacter via MetaConnect
          </button>
          {ps.mss && (
            <button
              onClick={onOpenMss}
              data-testid="reseau-drawer-mss"
              className="w-full inline-flex items-center justify-center gap-2 px-3 py-2 rounded-md border border-slate-300 text-slate-800 text-[13px] font-semibold hover:bg-slate-50"
            >
              <MailCheck className="w-3.5 h-3.5" /> Envoyer un mail MSSanté
            </button>
          )}
        </div>
      </div>
      <style>{`@keyframes slideInRight { from { transform: translateX(100%); opacity: .9 } to { transform: translateX(0); opacity: 1 } }`}</style>
    </div>
  );
}

function ReseauMssComposer({ composer, onChange, onCancel, onSend }) {
  return (
    <div className="fixed inset-0 z-[1300] grid place-items-center p-4" data-testid="reseau-mss-composer">
      <div className="absolute inset-0 bg-black/40" onClick={onCancel} />
      <div className="relative bg-white rounded-xl shadow-2xl border border-slate-200 w-full max-w-2xl max-h-[88vh] flex flex-col">
        <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <MailCheck className="w-4 h-4 text-emerald-600" />
            <div className="text-[14px] font-semibold text-slate-900">Mail MSSanté</div>
            <span className="text-[10.5px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 font-semibold">CDA-R2 N1</span>
          </div>
          <button onClick={onCancel} data-testid="reseau-mss-cancel" className="text-slate-400 hover:text-slate-600"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-5 space-y-3 overflow-y-auto">
          <div>
            <label className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold block mb-1">À</label>
            <input value={composer.ps.mss} readOnly className="w-full border border-slate-300 rounded-md px-3 py-2 text-[13.5px] text-slate-700 bg-slate-50" />
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold block mb-1">Objet</label>
            <input
              value={composer.subject}
              onChange={(e) => onChange({ ...composer, subject: e.target.value })}
              data-testid="reseau-mss-subject"
              className="w-full border border-slate-300 rounded-md px-3 py-2 text-[13.5px] text-slate-900"
            />
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold block mb-1">Corps</label>
            <textarea
              value={composer.body}
              onChange={(e) => onChange({ ...composer, body: e.target.value })}
              data-testid="reseau-mss-body"
              rows={12}
              className="w-full border border-slate-300 rounded-md px-3 py-2 text-[12.5px] text-slate-900 font-mono leading-relaxed"
            />
          </div>
          <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-[11.5px] text-amber-800 flex items-start gap-2">
            <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
            <div>
              Cet envoi ouvre votre client mail (protocole <code>mailto:</code>). En production, le composer est intégré nativement
              dans le webmail MSSanté MetaConnect (signature électronique + chiffrement TLS + archivage HDS).
            </div>
          </div>
        </div>
        <div className="px-5 py-3 border-t border-slate-200 flex items-center justify-end gap-2">
          <Button variant="outline" onClick={onCancel}>Annuler</Button>
          <Button onClick={onSend} data-testid="reseau-mss-send" className="text-white" style={{ backgroundColor: "var(--bp)" }}>
            <Send className="w-3.5 h-3.5 mr-1.5" /> Envoyer via mailto
          </Button>
        </div>
      </div>
    </div>
  );
}


/* ============================ Patient files ============================ */
function PageDossiers({ sandboxEffects }) {
  const insWarn = !!sandboxEffects?.insWarn;
  const showImportCsv = !!sandboxEffects?.showImportCsv;

  const [filter, setFilter] = React.useState("all"); // all | alert | mt | norv
  const [query, setQuery] = React.useState("");
  const [sortKey, setSortKey] = React.useState(null);
  const [sortAsc, setSortAsc] = React.useState(true);
  const [openPatient, setOpenPatient] = React.useState(null);

  // Helpers
  const hasAlerts = (p) => (p.allergies?.length || 0) + (p.ald?.length || 0) > 0;
  const formatRdv = (iso) => {
    if (!iso) return null;
    const d = new Date(iso);
    return d.toLocaleString("fr-FR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
  };
  const isRdvSoon = (iso) => {
    if (!iso) return false;
    const diffHours = (new Date(iso).getTime() - Date.now()) / (1000 * 60 * 60);
    return diffHours > 0 && diffHours <= 48;
  };

  // Stats
  const stats = React.useMemo(() => {
    const now = new Date();
    const weekEnd = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
    return {
      active: SEED_PATIENTS.length,
      rdvWeek: SEED_PATIENTS.filter((p) => p.nextRdv && new Date(p.nextRdv) >= now && new Date(p.nextRdv) <= weekEnd).length,
      alerts: SEED_PATIENTS.filter(hasAlerts).length,
      dmpUnlinked: SEED_PATIENTS.filter((p) => !p.dmpLinked).length,
    };
  }, []);

  // Filter + search + sort
  const visible = React.useMemo(() => {
    let list = [...SEED_PATIENTS];
    if (filter === "alert") list = list.filter(hasAlerts);
    if (filter === "mt") list = list.filter((p) => p.isMT);
    if (filter === "norv") list = list.filter((p) => !p.nextRdv);
    if (query.trim()) {
      const q = query.trim().toLowerCase();
      list = list.filter((p) => `${p.first} ${p.last}`.toLowerCase().includes(q) || (p.ins || "").toLowerCase().includes(q));
    }
    if (sortKey) {
      list.sort((a, b) => {
        const va = (a[sortKey] ?? "").toString();
        const vb = (b[sortKey] ?? "").toString();
        return sortAsc ? va.localeCompare(vb) : vb.localeCompare(va);
      });
    }
    return list;
  }, [filter, query, sortKey, sortAsc]);

  const toggleSort = (key) => {
    if (sortKey === key) setSortAsc(!sortAsc);
    else { setSortKey(key); setSortAsc(true); }
  };

  return (
    <div>
      <PageHeader title="Dossiers patients" subtitle="Création et consultation des dossiers (INSi qualifié)" action={
        <div className="flex items-center gap-2">
          {showImportCsv && (
            <Button data-testid="dossiers-import-csv" variant="outline" className="border-slate-300" onClick={(e) => e.preventDefault()}>
              <UploadCloud className="w-4 h-4 mr-1.5" /> Importer mes patients
            </Button>
          )}
          <Button className="text-white" style={{ backgroundColor: "var(--bp)" }}><Plus className="w-4 h-4 mr-1" />Nouveau dossier</Button>
        </div>
      } />

      {/* Stats line */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <DossierStatCard label="Patients actifs" value={stats.active} />
        <DossierStatCard label="RDV cette semaine" value={stats.rdvWeek} />
        <DossierStatCard label="Alertes en cours" value={stats.alerts} />
        <DossierStatCard label="DMP non liés" value={stats.dmpUnlinked} alert={stats.dmpUnlinked > 0} />
      </div>

      {/* Filter bar */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3 mb-4">
        <div className="flex flex-wrap items-center gap-2">
          <DossierFilterPill active={filter === "all"} onClick={() => setFilter("all")} id="all" label="Tous" count={SEED_PATIENTS.length} />
          <DossierFilterPill active={filter === "alert"} onClick={() => setFilter("alert")} id="alert" label="Avec alerte" count={stats.alerts} />
          <DossierFilterPill active={filter === "mt"} onClick={() => setFilter("mt")} id="mt" label="Médecin traitant" count={SEED_PATIENTS.filter((p) => p.isMT).length} />
          <DossierFilterPill active={filter === "norv"} onClick={() => setFilter("norv")} id="norv" label="Sans prochain RDV" count={SEED_PATIENTS.filter((p) => !p.nextRdv).length} />
        </div>
        <div className="relative max-w-xs w-full lg:w-72">
          <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Rechercher nom, INS…"
            data-testid="dossiers-search"
            className="w-full pl-9 pr-3 py-1.5 text-[13px] rounded-md border border-slate-300 bg-white text-slate-900 placeholder:text-slate-400 outline-none focus:border-[#4FC3F7] focus:ring-2 focus:ring-[#4FC3F7]/20"
          />
        </div>
      </div>

      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-[13px]">
            <thead className="bg-slate-50 text-slate-500 text-[11px] uppercase tracking-wider">
              <tr>
                <DossierSortableTh k="last" sortKey={sortKey} sortAsc={sortAsc} onClick={toggleSort}>Patient</DossierSortableTh>
                <DossierSortableTh k="ins" sortKey={sortKey} sortAsc={sortAsc} onClick={toggleSort}>INS</DossierSortableTh>
                <DossierSortableTh k="lastVisit" sortKey={sortKey} sortAsc={sortAsc} onClick={toggleSort}>Dernière visite</DossierSortableTh>
                <th className="text-left px-4 py-3">DMP</th>
                <th className="text-left px-4 py-3" data-testid="dossiers-col-alertes">Alertes</th>
                <th className="text-left px-4 py-3" data-testid="dossiers-col-mt">MT</th>
                <DossierSortableTh k="nextRdv" sortKey={sortKey} sortAsc={sortAsc} onClick={toggleSort} className="whitespace-nowrap">Prochain RDV</DossierSortableTh>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {visible.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-8 text-center text-slate-400 text-[13px]" data-testid="dossiers-empty">Aucun dossier ne correspond à ces critères.</td></tr>
              )}
              {visible.map((p) => {
                const alerts = [...(p.allergies || []).map((a) => `Allergie ${a}`), ...(p.ald || []).map((a) => `ALD ${a}`)];
                const rdvSoon = isRdvSoon(p.nextRdv);
                return (
                  <tr key={p.id} onClick={() => setOpenPatient(p)} className="hover:bg-[#F0F9FF] cursor-pointer transition-colors" data-testid={`dossier-row-${p.id}`}>
                    <td className="px-4 py-3">
                      <div className="font-semibold text-slate-900 flex items-center gap-2">
                        {p.first} {p.last}
                        {insWarn && (
                          <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-300" title="INS non encore qualifié — à compléter avant la première téléconsultation" data-testid="dossier-ins-warn">
                            <AlertTriangle className="w-2.5 h-2.5" /> INS à qualifier
                          </span>
                        )}
                      </div>
                      <div className="text-[11.5px] text-slate-500">{p.age} ans</div>
                    </td>
                    <td className="px-4 py-3 font-mono text-[12px] text-slate-700">{p.ins}</td>
                    <td className="px-4 py-3 text-slate-600">{p.lastVisit}</td>
                    <td className="px-4 py-3">
                      {p.dmpLinked ? (
                        <span className="inline-flex items-center gap-1 text-[11.5px] px-2 py-0.5 rounded bg-emerald-100 text-emerald-700">
                          <CheckCircle2 className="w-3 h-3" /> Lié
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-[11.5px] px-2 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200">
                          Non lié
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3" data-testid={`dossier-alert-${p.id}`}>
                      {alerts.length > 0 ? (
                        <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-amber-100 border border-amber-300 cursor-help" title={alerts.join(" · ")}>
                          <AlertTriangle className="w-3.5 h-3.5 text-amber-600" />
                        </span>
                      ) : (
                        <span className="text-slate-300">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3" data-testid={`dossier-mt-${p.id}`}>
                      {p.isMT ? (
                        <span className="inline-flex items-center gap-1 text-[10.5px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 border border-emerald-300" title="Médecin traitant déclaré">
                          MT
                        </span>
                      ) : (
                        <span className="text-slate-300">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap" data-testid={`dossier-rdv-${p.id}`}>
                      {p.nextRdv ? (
                        <span className={`inline-flex items-center gap-1 text-[11.5px] px-2 py-0.5 rounded-full ${rdvSoon ? "bg-[#4FC3F7] text-white font-semibold" : "bg-slate-100 text-slate-700"}`}>
                          {formatRdv(p.nextRdv)}
                          {rdvSoon && <span className="text-[9px] uppercase ml-1 opacity-90">≤48h</span>}
                        </span>
                      ) : (
                        <span className="text-slate-300">—</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      {openPatient && <PatientDetailDrawer patient={openPatient} onClose={() => setOpenPatient(null)} />}
    </div>
  );
}

/* ============================ Assistance ============================ */
function PageAssistance({ brand }) {
  return (
    <div className="max-w-3xl">
      <PageHeader title="Assistance" subtitle="Notre équipe support est à votre disposition du lundi au vendredi, 9h-18h." />
      <div className="grid sm:grid-cols-3 gap-4 mb-6">
        <Card className="p-5 text-center">
          <Phone className="w-6 h-6 mx-auto mb-2" style={{ color: "var(--bp)" }} />
          <div className="text-[12px] uppercase text-slate-500 font-semibold">Téléphone</div>
          <div className="mt-1 text-[15px] font-bold text-slate-900">02 38 00 00 00</div>
        </Card>
        <Card className="p-5 text-center">
          <Mail className="w-6 h-6 mx-auto mb-2" style={{ color: "var(--bp)" }} />
          <div className="text-[12px] uppercase text-slate-500 font-semibold">Email</div>
          <div className="mt-1 text-[13px] font-semibold text-slate-900">support@metaconnect.fr</div>
        </Card>
        <Card className="p-5 text-center">
          <MailCheck className="w-6 h-6 mx-auto mb-2" style={{ color: "var(--bp)" }} />
          <div className="text-[12px] uppercase text-slate-500 font-semibold">Chat en ligne</div>
          <button onClick={() => toast.success("Chat démarré (sandbox)")} className="mt-1 text-[13px] font-semibold underline" style={{ color: "var(--bp)" }}>Démarrer une conversation</button>
        </Card>
      </div>
      <Card className="p-6">
        <h2 className="text-[15px] font-semibold mb-3">Ouvrir un ticket</h2>
        <div className="space-y-3">
          <select className="w-full border border-slate-200 rounded-md px-3 py-2 text-[13.5px] bg-white text-slate-900">
            <option>Problème technique</option><option>Question facturation</option><option>Demande de fonctionnalité</option><option>Autre</option>
          </select>
          <textarea rows={4} placeholder="Décrivez votre demande…" className="w-full border border-slate-200 rounded-md px-3 py-2 text-[13.5px] text-slate-900 placeholder:text-slate-400" />
          <Button className="text-white" style={{ backgroundColor: "var(--bp)" }} onClick={() => toast.success("Ticket ouvert — réponse sous 4h ouvrées.")}>Envoyer</Button>
        </div>
      </Card>
    </div>
  );
}

/* ============================ Services Panel ============================ */
const PRESET_MOTIFS = [
  { label: "Consultation de médecine générale", duration: 20, price: 26.5 },
  { label: "Téléconsultation de médecine générale", duration: 15, price: 25.0 },
  { label: "Consultation pédiatrique (0-6 ans)", duration: 25, price: 32.0 },
  { label: "Suivi diabète type 2", duration: 30, price: 31.5 },
  { label: "Renouvellement d'ordonnance", duration: 10, price: 25.0 },
  { label: "Téléconsultation de renouvellement", duration: 10, price: 25.0 },
  { label: "Bilan de santé annuel", duration: 45, price: 46.5 },
  { label: "Visite de pré-reprise du travail", duration: 30, price: 31.5 },
  { label: "Consultation gynécologique de suivi", duration: 30, price: 30.0 },
  { label: "Suivi de grossesse", duration: 30, price: 38.0 },
  { label: "Consultation post-natale", duration: 30, price: 38.0 },
  { label: "Frottis cervico-utérin", duration: 20, price: 30.0 },
  { label: "Pose / retrait de stérilet", duration: 30, price: 47.0 },
  { label: "Vaccination (adulte)", duration: 10, price: 26.5 },
  { label: "Vaccination pédiatrique", duration: 15, price: 32.0 },
  { label: "Consultation dermatologique", duration: 20, price: 30.0 },
  { label: "Cryothérapie (verrue, lésion)", duration: 15, price: 35.0 },
  { label: "ECG de repos", duration: 20, price: 41.0 },
  { label: "Suivi cardiovasculaire", duration: 30, price: 50.0 },
  { label: "Consultation de nutrition", duration: 45, price: 60.0 },
  { label: "Sevrage tabagique", duration: 30, price: 30.0 },
  { label: "Suivi psychologique", duration: 45, price: 60.0 },
  { label: "Téléconsultation psychologique", duration: 45, price: 60.0 },
  { label: "Sortie d'hospitalisation", duration: 25, price: 30.0 },
  { label: "Téléexpertise (avis spécialiste)", duration: 0, price: 20.0 },
  { label: "Certificat médical de sport", duration: 20, price: 30.0 },
  { label: "Test antigénique COVID", duration: 10, price: 16.5 },
  { label: "Bilan auditif simple", duration: 20, price: 35.0 },
];

function ServicesPanel() {
  const [services, setServices] = useState([
    { id: "s1", label: "Consultation de médecine générale", duration: 20, price: 26.5, active: true },
    { id: "s2", label: "Téléconsultation de médecine générale", duration: 15, price: 25.0, active: true },
    { id: "s3", label: "Renouvellement d'ordonnance", duration: 10, price: 25.0, active: true },
  ]);
  const [pick, setPick] = useState("");
  const [showCustom, setShowCustom] = useState(false);
  const [custom, setCustom] = useState({ label: "", duration: 20, price: 25 });

  const addPreset = () => {
    const m = PRESET_MOTIFS.find((p) => p.label === pick);
    if (!m) return;
    if (services.some((s) => s.label === m.label)) {
      toast.error("Ce motif est déjà ajouté.");
      return;
    }
    setServices([...services, { id: `s${Date.now()}`, ...m, active: true }]);
    setPick("");
    toast.success("Motif ajouté à votre catalogue.");
  };
  const addCustom = () => {
    if (!custom.label.trim()) return toast.error("Indiquez un libellé.");
    setServices([...services, { id: `s${Date.now()}`, ...custom, active: true }]);
    setCustom({ label: "", duration: 20, price: 25 });
    setShowCustom(false);
    toast.success("Motif personnalisé créé.");
  };
  const removeS = (id) => setServices(services.filter((s) => s.id !== id));
  const toggleS = (id) => setServices(services.map((s) => s.id === id ? { ...s, active: !s.active } : s));

  return (
    <Card className="p-6">
      <div className="flex items-start justify-between gap-4 mb-5">
        <div>
          <div className="text-[15px] font-semibold text-slate-900">Catalogue des services & motifs de consultation</div>
          <p className="text-[12.5px] text-slate-500 mt-0.5">Pioche parmi {PRESET_MOTIFS.length} motifs prédéfinis (nomenclature CCAM/CNAM) ou crée le tien.</p>
        </div>
        <Badge className="bg-emerald-100 text-emerald-700 border-0">{services.filter((s) => s.active).length} actifs</Badge>
      </div>

      <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 mb-5">
        <div className="text-[11px] uppercase tracking-[0.14em] font-semibold text-slate-500 mb-2">Ajouter un motif depuis la bibliothèque</div>
        <div className="flex flex-col sm:flex-row gap-2">
          <select
            value={pick}
            onChange={(e) => setPick(e.target.value)}
            className="flex-1 border border-slate-300 rounded-md px-3 py-2 text-[13px] bg-white text-slate-900 font-medium"
            data-testid="services-preset-select"
          >
            <option value="">— Sélectionner un motif —</option>
            {PRESET_MOTIFS.map((m) => (
              <option key={m.label} value={m.label}>{m.label} · {m.duration}min · {m.price.toFixed(2)} €</option>
            ))}
          </select>
          <Button onClick={addPreset} disabled={!pick} className="text-white" style={{ backgroundColor: "var(--bp)" }} data-testid="services-add-preset">
            <Plus className="w-4 h-4 mr-1" /> Ajouter
          </Button>
        </div>
        <button onClick={() => setShowCustom(!showCustom)} className="mt-3 text-[12px] underline text-slate-600 hover:text-slate-900">
          {showCustom ? "Masquer" : "+ Créer un motif personnalisé"}
        </button>
        {showCustom && (
          <div className="mt-3 grid sm:grid-cols-[1fr_120px_120px_auto] gap-2 items-end">
            <div>
              <label className="text-[11px] text-slate-500 font-medium">Libellé</label>
              <input value={custom.label} onChange={(e) => setCustom({ ...custom, label: e.target.value })} className="w-full border border-slate-300 rounded-md px-3 py-2 text-[13px] bg-white" placeholder="Ex : Consultation podologique" />
            </div>
            <div>
              <label className="text-[11px] text-slate-500 font-medium">Durée (min)</label>
              <input type="number" value={custom.duration} onChange={(e) => setCustom({ ...custom, duration: parseInt(e.target.value || 0, 10) })} className="w-full border border-slate-300 rounded-md px-3 py-2 text-[13px] bg-white" />
            </div>
            <div>
              <label className="text-[11px] text-slate-500 font-medium">Tarif (€)</label>
              <input type="number" step="0.01" value={custom.price} onChange={(e) => setCustom({ ...custom, price: parseFloat(e.target.value || 0) })} className="w-full border border-slate-300 rounded-md px-3 py-2 text-[13px] bg-white" />
            </div>
            <Button onClick={addCustom} className="text-white h-[38px]" style={{ backgroundColor: "var(--bp)" }}>Créer</Button>
          </div>
        )}
      </div>

      <div className="overflow-hidden border border-slate-200 rounded-lg">
        <table className="w-full text-[13px]">
          <thead className="bg-slate-50 text-slate-500 text-[11px] uppercase tracking-wider">
            <tr>
              <th className="text-left px-4 py-2.5">Motif</th>
              <th className="text-left px-4 py-2.5 w-[110px]">Durée</th>
              <th className="text-left px-4 py-2.5 w-[110px]">Tarif</th>
              <th className="text-left px-4 py-2.5 w-[120px]">Statut</th>
              <th className="px-4 py-2.5 w-[60px]"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 bg-white">
            {services.map((s) => (
              <tr key={s.id} className="hover:bg-slate-50">
                <td className="px-4 py-3 font-medium text-slate-900">{s.label}</td>
                <td className="px-4 py-3 text-slate-700">{s.duration} min</td>
                <td className="px-4 py-3 text-slate-700">{s.price.toFixed(2)} €</td>
                <td className="px-4 py-3">
                  <button onClick={() => toggleS(s.id)} className={`relative w-10 h-5 rounded-full transition-colors ${s.active ? "" : "bg-slate-300"}`} style={s.active ? { backgroundColor: "var(--bp)" } : {}}>
                    <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-all ${s.active ? "left-5" : "left-0.5"}`} />
                  </button>
                </td>
                <td className="px-4 py-3 text-right">
                  <button onClick={() => removeS(s.id)} className="text-slate-400 hover:text-red-500" data-testid={`services-remove-${s.id}`}><Trash2 className="w-4 h-4" /></button>
                </td>
              </tr>
            ))}
            {services.length === 0 && (
              <tr><td colSpan="5" className="px-4 py-10 text-center text-slate-400 text-[13px]">Aucun service. Ajoutez votre premier motif ↑</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

/* ============================ Reviews Panel ============================ */
const SEED_REVIEWS = [
  { id: "r1", author: "Camille L.", initials: "CL", rating: 5, date: "il y a 3 jours", text: "Praticien à l'écoute, prise de rendez-vous en ligne très simple. La téléconsultation fonctionne parfaitement, je recommande !", replied: true, reply: "Merci beaucoup pour votre retour, à très vite !" },
  { id: "r2", author: "Mohamed B.", initials: "MB", rating: 5, date: "il y a 1 semaine", text: "Excellent accueil, cabinet propre et moderne. Le secrétariat est très réactif.", replied: false },
  { id: "r3", author: "Sophie D.", initials: "SD", rating: 4, date: "il y a 2 semaines", text: "Très bon médecin, à l'écoute. Petite attente au cabinet mais rien d'anormal.", replied: true, reply: "Merci pour votre patience, nous travaillons à fluidifier les rendez-vous." },
  { id: "r4", author: "Thomas R.", initials: "TR", rating: 5, date: "il y a 3 semaines", text: "Ordonnance reçue en quelques minutes sur l'espace patient. Top !", replied: false },
  { id: "r5", author: "Élodie M.", initials: "EM", rating: 5, date: "il y a 1 mois", text: "Vraiment satisfaite. Le passage en téléconsultation pour le renouvellement est un gain de temps énorme.", replied: true, reply: "Ravi que la téléconsultation vous facilite la vie 🙏" },
  { id: "r6", author: "Lucas P.", initials: "LP", rating: 3, date: "il y a 1 mois", text: "Consultation correcte, j'ai eu un peu de mal à trouver un créneau rapidement.", replied: false },
];

function ReviewsPanel() {
  const [imported, setImported] = useState(false);
  const [loading, setLoading] = useState(false);
  const [reviews, setReviews] = useState([]);
  const [reply, setReply] = useState({});
  const placeId = "ChIJN1t_tDeuEmsRUsoyG83frY4";

  const handleImport = () => {
    setLoading(true);
    setTimeout(() => {
      setReviews(SEED_REVIEWS);
      setImported(true);
      setLoading(false);
      toast.success("6 avis Google importés ✓");
    }, 1400);
  };
  const handleReply = (id) => {
    if (!reply[id]?.trim()) return toast.error("Tapez une réponse avant d'envoyer.");
    setReviews(reviews.map((r) => r.id === id ? { ...r, replied: true, reply: reply[id] } : r));
    setReply({ ...reply, [id]: "" });
    toast.success("Réponse publiée sur Google.");
  };

  const avg = reviews.length ? (reviews.reduce((a, r) => a + r.rating, 0) / reviews.length).toFixed(1) : "—";

  return (
    <div className="space-y-5">
      {/* Header / Google integration */}
      <Card className="p-6">
        <div className="flex items-start gap-5">
          <div className="w-12 h-12 rounded-full bg-white border border-slate-200 grid place-items-center shadow-sm shrink-0">
            <svg viewBox="0 0 48 48" className="w-7 h-7"><path fill="#FFC107" d="M43.6 20.1H42V20H24v8h11.3c-1.6 4.7-6 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3 0 5.8 1.1 7.9 3l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.6-.4-3.9z"/><path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.6 16 18.9 13 24 13c3 0 5.8 1.1 7.9 3l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/><path fill="#4CAF50" d="M24 44c5.2 0 10-2 13.5-5.3l-6.2-5.2c-2 1.5-4.5 2.5-7.3 2.5-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.5 39.7 16.2 44 24 44z"/><path fill="#1976D2" d="M43.6 20.1H42V20H24v8h11.3c-.8 2.3-2.3 4.3-4.3 5.6l6.2 5.2C40.9 35.8 44 30.3 44 24c0-1.3-.1-2.6-.4-3.9z"/></svg>
          </div>
          <div className="flex-1">
            <div className="text-[15px] font-semibold text-slate-900">Avis clients · Google Business Profile</div>
            <p className="text-[12.5px] text-slate-500 mt-0.5">Les avis publiés sur votre fiche Google sont synchronisés automatiquement. Vous pouvez répondre directement depuis l&apos;interface.</p>
          </div>
          {imported && (
            <div className="text-right">
              <div className="flex items-center gap-1 justify-end">
                {[1,2,3,4,5].map((i) => (
                  <Star key={i} className={`w-4 h-4 ${i <= Math.round(avg) ? "fill-amber-400 text-amber-400" : "text-slate-300"}`} />
                ))}
              </div>
              <div className="text-[18px] font-bold text-slate-900">{avg} <span className="text-[12px] font-medium text-slate-500">/ 5</span></div>
              <div className="text-[11px] text-slate-500">{reviews.length} avis Google</div>
            </div>
          )}
        </div>

        {!imported && (
          <div className="mt-5 rounded-lg border border-slate-200 bg-slate-50 p-4 flex flex-col sm:flex-row sm:items-center gap-3">
            <div className="flex-1">
              <label className="text-[11px] uppercase tracking-[0.14em] text-slate-500 font-semibold block mb-1">Google Place ID</label>
              <input defaultValue={placeId} className="w-full border border-slate-300 rounded-md px-3 py-2 text-[12.5px] font-mono bg-white" />
              <p className="text-[11px] text-slate-500 mt-1.5">Récupérez votre Place ID sur <span className="underline">developers.google.com/maps/place-id</span></p>
            </div>
            <Button onClick={handleImport} disabled={loading} className="text-white sm:self-end" style={{ backgroundColor: "var(--bp)" }} data-testid="reviews-import-btn">
              {loading ? (<><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Import en cours…</>) : (<>Importer depuis Google</>)}
            </Button>
          </div>
        )}
        {imported && (
          <div className="mt-4 flex items-center justify-between gap-3 rounded-lg bg-emerald-50 border border-emerald-200 px-4 py-2.5">
            <div className="flex items-center gap-2 text-[12.5px] text-emerald-800">
              <CheckCircle2 className="w-4 h-4" /> Synchronisation active · dernière mise à jour : à l&apos;instant
            </div>
            <button onClick={handleImport} className="text-[11.5px] text-emerald-700 font-semibold underline">Resynchroniser</button>
          </div>
        )}
      </Card>

      {/* Reviews list */}
      {imported && (
        <div className="space-y-4">
          {reviews.map((r) => (
            <Card key={r.id} className="p-5">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-full grid place-items-center text-white font-semibold shrink-0" style={{ backgroundColor: "var(--bp)" }}>{r.initials}</div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div className="font-semibold text-slate-900 text-[14px]">{r.author}</div>
                    <div className="text-[11.5px] text-slate-400">{r.date}</div>
                  </div>
                  <div className="flex items-center gap-0.5 mt-0.5 mb-2">
                    {[1,2,3,4,5].map((i) => (
                      <Star key={i} className={`w-3.5 h-3.5 ${i <= r.rating ? "fill-amber-400 text-amber-400" : "text-slate-300"}`} />
                    ))}
                  </div>
                  <p className="text-[13px] text-slate-700 leading-relaxed">{r.text}</p>

                  {r.replied && (
                    <div className="mt-3 ml-1 pl-4 border-l-2 border-slate-200">
                      <div className="text-[11.5px] font-semibold text-slate-500 mb-1">Votre réponse</div>
                      <p className="text-[12.5px] text-slate-600 leading-relaxed">{r.reply}</p>
                    </div>
                  )}
                  {!r.replied && (
                    <div className="mt-3 flex gap-2">
                      <input
                        value={reply[r.id] || ""}
                        onChange={(e) => setReply({ ...reply, [r.id]: e.target.value })}
                        placeholder="Répondre publiquement…"
                        className="flex-1 border border-slate-300 rounded-md px-3 py-2 text-[12.5px] bg-white"
                        data-testid={`review-reply-input-${r.id}`}
                      />
                      <Button onClick={() => handleReply(r.id)} className="text-white" style={{ backgroundColor: "var(--bp)" }} data-testid={`review-reply-send-${r.id}`}>
                        <Send className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

/* ============================ Patient Teleconsultation Flow ============================ */
const TELECONS_STEPS = [
  { id: "warn", label: "Avertissements" },
  { id: "info", label: "Informations" },
  { id: "doctor", label: "Médecin traitant" },
  { id: "loc", label: "Localisation" },
  { id: "docs", label: "Ajout de documents" },
  { id: "tech", label: "Tests de connexion" },
  { id: "call", label: "Consultation" },
];

export function PatientTeleconsFlow({ brand, doctorName = "Dr ARACELI MUELA-GARCIA", estimatedMin = 7, onExit }) {
  const [step, setStep] = useState(0);
  const [data, setData] = useState({
    longTermTreatment: null,
    recentMeds: null,
    recentMedsDetail: "",
    allergies: null,
    consent: false,
    doctorFirst: "",
    doctorLast: "",
    manualDoctor: false,
    address: "79 route de La Ferté Saint Aubin",
    address2: "",
    zip: "45000",
    city: "Orléans",
    country: "France",
    docs: [],
    techChecks: { cam: false, mic: false, net: false, browser: true },
  });
  const next = () => setStep((s) => Math.min(s + 1, TELECONS_STEPS.length - 1));
  const back = () => setStep((s) => Math.max(s - 1, 0));
  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));

  return (
    <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden shadow-sm">
      {/* Header */}
      <div className="px-6 py-4 border-b border-slate-200 bg-gradient-to-r from-slate-50 to-white flex items-center justify-between gap-4">
        <div>
          <div className="text-[11px] uppercase tracking-[0.14em] font-semibold text-slate-500">Parcours patient · téléconsultation</div>
          <div className="text-[15px] font-bold text-slate-900 mt-0.5">Professionnel : <span style={{ color: "var(--bp)" }}>{doctorName}</span></div>
          <div className="text-[12px] text-slate-500 mt-0.5 flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" /> Temps estimé avant consultation : <span className="font-semibold text-slate-700">{estimatedMin} minute(s)</span></div>
        </div>
        {onExit && (
          <button onClick={onExit} className="text-[12px] text-slate-500 hover:text-slate-900 flex items-center gap-1"><X className="w-4 h-4" /> Quitter</button>
        )}
      </div>

      {/* Stepper */}
      <div className="px-6 py-4 border-b border-slate-200 bg-white">
        <div className="flex items-center gap-1 sm:gap-2 overflow-x-auto">
          {TELECONS_STEPS.map((s, i) => {
            const done = i < step;
            const active = i === step;
            return (
              <React.Fragment key={s.id}>
                <div className={`flex items-center gap-2 shrink-0 ${active ? "" : done ? "" : "opacity-50"}`}>
                  <div className={`w-6 h-6 rounded-full grid place-items-center text-[11px] font-bold ${done ? "bg-emerald-500 text-white" : active ? "text-white" : "bg-slate-200 text-slate-500"}`} style={active ? { backgroundColor: "var(--bp)" } : {}}>
                    {done ? <CheckCircle2 className="w-3.5 h-3.5" /> : i + 1}
                  </div>
                  <div className={`text-[11.5px] font-medium hidden md:block ${active ? "text-slate-900" : "text-slate-500"}`}>{s.label}</div>
                </div>
                {i < TELECONS_STEPS.length - 1 && <div className="h-px flex-1 bg-slate-200 min-w-[8px]" />}
              </React.Fragment>
            );
          })}
        </div>
      </div>

      {/* Step body */}
      <div className="px-6 py-7 min-h-[420px] bg-white">
        {step === 0 && <PT_Warnings />}
        {step === 1 && <PT_Info data={data} set={set} />}
        {step === 2 && <PT_Doctor data={data} set={set} />}
        {step === 3 && <PT_Location data={data} set={set} />}
        {step === 4 && <PT_Docs data={data} set={set} />}
        {step === 5 && <PT_Tech data={data} set={set} />}
        {step === 6 && <PT_Call doctorName={doctorName} brand={brand} />}
      </div>

      {/* Nav */}
      {step < 6 && (
        <div className="px-6 py-4 border-t border-slate-200 bg-slate-50 flex items-center justify-between gap-3">
          <Button variant="outline" onClick={step === 0 ? (onExit || back) : back} className="border-slate-300" data-testid="patient-tele-back">
            <ArrowLeft className="w-4 h-4 mr-1" /> {step === 0 ? "Annuler" : "Retour"}
          </Button>
          <div className="text-[11.5px] text-slate-500">Étape {step + 1} / {TELECONS_STEPS.length}</div>
          <Button
            onClick={next}
            disabled={step === 1 && !(data.consent && data.consentSoin)}
            className="text-white"
            style={{ backgroundColor: "var(--bp)" }}
            data-testid="patient-tele-next"
          >
            Suivant <ArrowRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      )}
    </div>
  );
}

function PT_Warnings() {
  return (
    <div className="max-w-2xl">
      <div className="text-[11px] uppercase tracking-[0.14em] font-semibold text-slate-500 mb-2">Avertissements</div>
      <h2 className="text-[20px] font-bold text-slate-900 mb-3">Avant de commencer votre téléconsultation</h2>
      <p className="text-[12.5px] text-slate-500 mb-4">Conformément à la <strong>fiche HAS Bonnes Pratiques Télesoin (fév. 2021)</strong> et au <strong>référentiel fonctionnel télesoin de l&apos;ANS</strong>.</p>
      <div className="space-y-3 text-[13.5px] text-slate-700 leading-relaxed">
        <div className="flex gap-3 rounded-lg border border-amber-200 bg-amber-50 p-4">
          <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
          <div>
            <div className="font-semibold text-amber-900 mb-0.5">Urgence vitale</div>
            <p>La téléconsultation ne se substitue pas à une consultation présentielle ni à un appel d&apos;urgence. En cas d&apos;urgence vitale, composez le <strong>15</strong> ou le <strong>112</strong>.</p>
          </div>
        </div>
        <div className="flex gap-3 rounded-lg border border-blue-200 bg-blue-50 p-4">
          <Info className="w-5 h-5 text-blue-600 shrink-0 mt-0.5" />
          <div>
            <div className="font-semibold text-blue-900 mb-0.5">Préparation de la séance</div>
            <p>Vérifiez que vous êtes dans un endroit calme, éclairé et confidentiel. Munissez-vous de votre <strong>carte Vitale</strong> et de votre traitement en cours.</p>
          </div>
        </div>
        <div className="flex gap-3 rounded-lg border border-violet-200 bg-violet-50 p-4">
          <Stethoscope className="w-5 h-5 text-violet-600 shrink-0 mt-0.5" />
          <div>
            <div className="font-semibold text-violet-900 mb-0.5">Parcours de soins coordonné</div>
            <p>La téléconsultation s&apos;inscrit dans le parcours de soins. Un <strong>premier soin présentiel</strong> peut être requis selon votre situation clinique. Le remboursement par l&apos;Assurance Maladie suppose l&apos;adressage par votre médecin traitant (hors exceptions).</p>
          </div>
        </div>
        <div className="flex gap-3 rounded-lg border border-orange-200 bg-orange-50 p-4">
          <RefreshCw className="w-5 h-5 text-orange-600 shrink-0 mt-0.5" />
          <div>
            <div className="font-semibold text-orange-900 mb-0.5">Mode dégradé documenté</div>
            <p>En cas de coupure de connexion, de panne caméra/micro ou d&apos;urgence durant l&apos;acte, la séance pourra être <strong>basculée en appel téléphonique sécurisé</strong> ou reportée. Le praticien vous contactera dans les 5 minutes.</p>
          </div>
        </div>
        <div className="flex gap-3 rounded-lg border border-emerald-200 bg-emerald-50 p-4">
          <ShieldCheck className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" />
          <div>
            <div className="font-semibold text-emerald-900 mb-0.5">Sécurité et confidentialité</div>
            <p>Le flux audio/vidéo est <strong>chiffré de bout-en-bout (WebRTC)</strong> et n&apos;est pas enregistré (référentiel ANS REA.1.2). Vos documents et compte-rendu sont stockés chez un <strong>Hébergeur de Données de Santé (HDS)</strong> agréé, conformément à la PGSSI-S et au RGPD.</p>
          </div>
        </div>
      </div>
    </div>
  );
}

function PT_Info({ data, set }) {
  return (
    <div className="max-w-2xl">
      <div className="text-[11px] uppercase tracking-[0.14em] font-semibold text-slate-500 mb-2">Informations & consentement</div>
      <h2 className="text-[20px] font-bold text-slate-900 mb-2">Notice d&apos;information préalable</h2>
      <p className="text-[13px] text-slate-500 mb-5">Conformément au <strong>référentiel HAS BPT 2021</strong>, le praticien doit s&apos;assurer de votre consentement éclairé après vous avoir informé(e) des modalités, du coût et des alternatives.</p>

      <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 mb-6 text-[12.5px] text-slate-700 space-y-1.5">
        <div className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" /><div><strong>Modalités :</strong> téléconsultation en vidéo chiffrée, durée moyenne 15-30 min, échange interactif PS ↔ patient.</div></div>
        <div className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" /><div><strong>Coût & remboursement :</strong> tarif conventionné équivalent au présentiel (NGAP), pris en charge à 70 % par l&apos;Assurance Maladie + complémentaire santé.</div></div>
        <div className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" /><div><strong>Alternatives :</strong> consultation présentielle en cabinet, visite à domicile (si applicable), réorientation vers un autre confrère.</div></div>
        <div className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" /><div><strong>Données :</strong> identité qualifiée via INS v2.1 (téléservice INSi), traitement HDS, conservation 20 ans (dossier médical), droits d&apos;accès/rectification/effacement (RGPD).</div></div>
      </div>

      <h3 className="text-[15px] font-semibold text-slate-900 mb-3">Quelques questions médicales</h3>
      <div className="space-y-5">
        <PT_YesNo
          label="Suivez-vous un traitement de longue durée (ALD) ?"
          value={data.longTermTreatment}
          onChange={(v) => set("longTermTreatment", v)}
          name="ltt"
        />
        <PT_YesNo
          label="Avez-vous pris un ou des médicaments dans les 72 dernières heures ?"
          value={data.recentMeds}
          onChange={(v) => set("recentMeds", v)}
          name="rm"
        />
        {data.recentMeds === true && (
          <div>
            <label className="block text-[12.5px] font-semibold text-slate-700 mb-1.5">Merci de préciser quels médicaments et leurs dosages.</label>
            <textarea
              rows={3}
              value={data.recentMedsDetail}
              onChange={(e) => set("recentMedsDetail", e.target.value)}
              placeholder="Ex : Doliprane 1000 mg matin et soir, Spasfon 80 mg si besoin…"
              className="w-full border border-slate-300 rounded-md px-3 py-2 text-[13.5px] focus:outline-none focus:border-slate-500"
              data-testid="pt-info-meds-detail"
            />
          </div>
        )}
        <PT_YesNo
          label="Présentez-vous des allergies médicamenteuses ?"
          value={data.allergies}
          onChange={(v) => set("allergies", v)}
          name="al"
        />
      </div>

      {/* Double consentement HAS BPT 2021 */}
      <div className="mt-7">
        <div className="text-[11px] uppercase tracking-[0.14em] font-semibold text-slate-500 mb-2">Double consentement requis</div>
        <h3 className="text-[15px] font-semibold text-slate-900 mb-3">Consentement éclairé (HAS BPT 2021)</h3>

        <label className="flex items-start gap-3 cursor-pointer p-4 rounded-lg border border-slate-200 hover:border-slate-400 transition-colors mb-2">
          <input
            type="checkbox"
            checked={data.consentSoin || false}
            onChange={(e) => set("consentSoin", e.target.checked)}
            className="mt-0.5 w-4 h-4 rounded border-slate-300"
            data-testid="pt-info-consent-soin"
          />
          <span className="text-[13px] text-slate-700">
            <span className="font-semibold">1. Acte de soin —</span> Je consens à la réalisation de l&apos;acte médical proposé par le praticien <span className="text-red-500">*</span>
          </span>
        </label>

        <label className="flex items-start gap-3 cursor-pointer p-4 rounded-lg border border-slate-200 hover:border-slate-400 transition-colors mb-2">
          <input
            type="checkbox"
            checked={data.consent}
            onChange={(e) => set("consent", e.target.checked)}
            className="mt-0.5 w-4 h-4 rounded border-slate-300"
            data-testid="pt-info-consent"
          />
          <span className="text-[13px] text-slate-700">
            <span className="font-semibold">2. Modalités à distance —</span> Je confirme vouloir recourir librement à la téléconsultation par technologies de l&apos;information et de la communication (TIC) <span className="text-red-500">*</span>
          </span>
        </label>

        <label className="flex items-start gap-3 cursor-pointer p-4 rounded-lg border border-slate-200 hover:border-slate-400 transition-colors">
          <input
            type="checkbox"
            checked={data.consentData || false}
            onChange={(e) => set("consentData", e.target.checked)}
            className="mt-0.5 w-4 h-4 rounded border-slate-300"
            data-testid="pt-info-consent-data"
          />
          <span className="text-[13px] text-slate-700">
            <span className="font-semibold">3. Traitement des données —</span> J&apos;accepte le dépôt du compte-rendu au <strong>DMP</strong> et son envoi à mon médecin traitant via <strong>MSSanté</strong> (RGPD · HDS)
          </span>
        </label>

        <p className="mt-3 text-[11.5px] text-slate-500">Vos consentements sont horodatés et tracés dans votre dossier patient (référentiel ANS REA.1.1).</p>
      </div>
    </div>
  );
}

function PT_YesNo({ label, value, onChange, name }) {
  return (
    <div>
      <div className="text-[13.5px] font-medium text-slate-800 mb-2">{label}</div>
      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => onChange(true)}
          className={`flex-1 px-4 py-2.5 rounded-md text-[13px] font-medium border transition-all ${value === true ? "text-white border-transparent" : "border-slate-300 text-slate-700 hover:bg-slate-50"}`}
          style={value === true ? { backgroundColor: "var(--bp)" } : {}}
          data-testid={`pt-yn-${name}-yes`}
        >
          Oui
        </button>
        <button
          type="button"
          onClick={() => onChange(false)}
          className={`flex-1 px-4 py-2.5 rounded-md text-[13px] font-medium border transition-all ${value === false ? "text-white border-transparent" : "border-slate-300 text-slate-700 hover:bg-slate-50"}`}
          style={value === false ? { backgroundColor: "var(--bp)" } : {}}
          data-testid={`pt-yn-${name}-no`}
        >
          Non
        </button>
      </div>
    </div>
  );
}

function PT_Doctor({ data, set }) {
  return (
    <div className="max-w-2xl">
      <div className="text-[11px] uppercase tracking-[0.14em] font-semibold text-slate-500 mb-2">Médecin traitant</div>
      <h2 className="text-[20px] font-bold text-slate-900 mb-2">Votre médecin traitant</h2>
      <p className="text-[13px] text-slate-500 mb-6">Dans le cadre du parcours de soins coordonnés, indiquez-nous votre médecin traitant.</p>

      <div className="grid sm:grid-cols-2 gap-3 mb-3">
        <div>
          <label className="block text-[11.5px] font-semibold text-slate-600 mb-1">Prénom <span className="text-red-500">*</span></label>
          <input
            value={data.doctorFirst}
            onChange={(e) => set("doctorFirst", e.target.value)}
            disabled={data.manualDoctor}
            className="w-full border border-slate-300 rounded-md px-3 py-2 text-[13.5px] disabled:bg-slate-50 disabled:text-slate-400"
            placeholder="Prénom du médecin"
            data-testid="pt-doctor-first"
          />
        </div>
        <div>
          <label className="block text-[11.5px] font-semibold text-slate-600 mb-1">Nom <span className="text-red-500">*</span></label>
          <input
            value={data.doctorLast}
            onChange={(e) => set("doctorLast", e.target.value)}
            disabled={data.manualDoctor}
            className="w-full border border-slate-300 rounded-md px-3 py-2 text-[13.5px] disabled:bg-slate-50 disabled:text-slate-400"
            placeholder="Nom du médecin"
            data-testid="pt-doctor-last"
          />
        </div>
      </div>

      <Button
        disabled={data.manualDoctor || (!data.doctorFirst && !data.doctorLast)}
        onClick={() => toast.success("Recherche dans l'annuaire RPPS… 3 résultats trouvés (sandbox)")}
        className="text-white"
        style={{ backgroundColor: "var(--bp)" }}
        data-testid="pt-doctor-search"
      >
        <Search className="w-4 h-4 mr-1" /> Rechercher
      </Button>

      <label className="mt-5 flex items-center gap-2 cursor-pointer text-[13px] text-slate-700">
        <input
          type="checkbox"
          checked={data.manualDoctor}
          onChange={(e) => set("manualDoctor", e.target.checked)}
          className="w-4 h-4 rounded border-slate-300"
          data-testid="pt-doctor-manual"
        />
        Ajouter un médecin traitant manuellement
      </label>

      {data.manualDoctor && (
        <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50 p-4 text-[12.5px] text-slate-600">
          Saisissez les coordonnées de votre médecin traitant (non référencé dans l&apos;annuaire RPPS). Ces informations seront transmises au praticien pour le compte-rendu de consultation.
        </div>
      )}
    </div>
  );
}

function PT_Location({ data, set }) {
  return (
    <div className="max-w-2xl">
      <div className="text-[11px] uppercase tracking-[0.14em] font-semibold text-slate-500 mb-2">Localisation</div>
      <h2 className="text-[20px] font-bold text-slate-900 mb-2">Où vous trouvez-vous actuellement ?</h2>
      <p className="text-[13px] text-slate-500 mb-4">Afin de pouvoir vous localiser en cas d&apos;urgence, le praticien a besoin de connaître le lieu précis où vous vous trouvez actuellement pour réaliser votre téléconsultation.</p>

      <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 mb-5 flex gap-2.5 text-[12.5px] text-amber-900">
        <Info className="w-4 h-4 shrink-0 mt-0.5" />
        <div>Le remboursement par l&apos;Assurance Maladie suppose que vous soyez en <strong>France métropolitaine, DOM-COM ou un État membre de l&apos;UE/EEE</strong> au moment de la téléconsultation.</div>
      </div>

      <div className="space-y-3">
        <div>
          <label className="block text-[11.5px] font-semibold text-slate-600 mb-1">Adresse <span className="text-red-500">*</span></label>
          <input value={data.address} onChange={(e) => set("address", e.target.value)} className="w-full border border-slate-300 rounded-md px-3 py-2 text-[13.5px]" data-testid="pt-loc-address" />
        </div>
        <div>
          <label className="block text-[11.5px] font-semibold text-slate-600 mb-1">Complément d&apos;adresse</label>
          <input value={data.address2} onChange={(e) => set("address2", e.target.value)} className="w-full border border-slate-300 rounded-md px-3 py-2 text-[13.5px]" placeholder="Bâtiment, étage, numéro de porte…" data-testid="pt-loc-address2" />
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <label className="block text-[11.5px] font-semibold text-slate-600 mb-1">Code postal <span className="text-red-500">*</span></label>
            <input value={data.zip} onChange={(e) => set("zip", e.target.value)} className="w-full border border-slate-300 rounded-md px-3 py-2 text-[13.5px]" data-testid="pt-loc-zip" />
          </div>
          <div>
            <label className="block text-[11.5px] font-semibold text-slate-600 mb-1">Ville <span className="text-red-500">*</span></label>
            <input value={data.city} onChange={(e) => set("city", e.target.value)} className="w-full border border-slate-300 rounded-md px-3 py-2 text-[13.5px]" data-testid="pt-loc-city" />
          </div>
        </div>
        <div>
          <label className="block text-[11.5px] font-semibold text-slate-600 mb-1">Pays <span className="text-red-500">*</span></label>
          <input value={data.country} onChange={(e) => set("country", e.target.value)} className="w-full border border-slate-300 rounded-md px-3 py-2 text-[13.5px]" placeholder="France" data-testid="pt-loc-country" />
        </div>
      </div>
    </div>
  );
}

function PT_Docs({ data, set }) {
  const add = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    set("docs", [...data.docs, ...files.map((f) => ({ name: f.name, size: f.size }))]);
    toast.success(`${files.length} document(s) ajouté(s).`);
  };
  return (
    <div className="max-w-2xl">
      <div className="text-[11px] uppercase tracking-[0.14em] font-semibold text-slate-500 mb-2">Ajout de documents</div>
      <h2 className="text-[20px] font-bold text-slate-900 mb-2">Documents utiles à la consultation</h2>
      <p className="text-[13px] text-slate-500 mb-2">Vous pouvez transmettre des documents médicaux (ordonnance, résultats d&apos;analyse, photos d&apos;une lésion). Formats acceptés : PDF, JPG, PNG · 10 Mo max par fichier.</p>
      <p className="text-[11.5px] text-slate-500 mb-5">Documents stockés en <strong>HDS certifié</strong>, chiffrés au repos, transmissibles au DMP via le cadre d&apos;interopérabilité <strong>CI-SIS / CDA R2</strong>.</p>

      <label className="block rounded-lg border-2 border-dashed border-slate-300 bg-slate-50 hover:bg-slate-100 transition-colors p-8 text-center cursor-pointer" data-testid="pt-docs-upload">
        <UploadCloud className="w-8 h-8 mx-auto mb-2 text-slate-400" />
        <div className="text-[13.5px] font-semibold text-slate-700">Cliquez pour téléverser</div>
        <div className="text-[12px] text-slate-500 mt-0.5">ou glissez-déposez vos fichiers ici</div>
        <input type="file" multiple onChange={add} className="hidden" />
      </label>

      {data.docs.length > 0 && (
        <div className="mt-4 space-y-2">
          {data.docs.map((d, i) => (
            <div key={i} className="flex items-center gap-3 px-3 py-2 rounded-md border border-slate-200 bg-white">
              <Paperclip className="w-4 h-4 text-slate-400" />
              <div className="flex-1 text-[13px] text-slate-700">{d.name}</div>
              <div className="text-[11px] text-slate-400">{(d.size / 1024).toFixed(1)} Ko</div>
              <button onClick={() => set("docs", data.docs.filter((_, j) => j !== i))} className="text-slate-400 hover:text-red-500"><X className="w-4 h-4" /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function PT_Tech({ data, set }) {
  const items = [
    { k: "cam", label: "Caméra", icon: Camera, hint: "Autorisez l'accès à votre caméra dans le navigateur" },
    { k: "mic", label: "Microphone", icon: Mic, hint: "Vérifiez que votre micro n'est pas coupé matériellement" },
    { k: "net", label: "Connexion internet", icon: Wifi, hint: "Débit minimum recommandé : 5 Mbps descendant / 1 Mbps montant" },
    { k: "browser", label: "Navigateur compatible", icon: ShieldCheck, hint: "Chrome, Firefox, Safari ou Edge à jour" },
  ];
  const runCheck = (k) => {
    set("techChecks", { ...data.techChecks, [k]: true });
    toast.success(`${items.find((i) => i.k === k).label} OK ✓`);
  };
  const allOk = items.every((i) => data.techChecks[i.k]);
  return (
    <div className="max-w-2xl">
      <div className="text-[11px] uppercase tracking-[0.14em] font-semibold text-slate-500 mb-2">Tests de connexion</div>
      <h2 className="text-[20px] font-bold text-slate-900 mb-2">Vérifions votre matériel</h2>
      <p className="text-[13px] text-slate-500 mb-6">Ces tests prennent quelques secondes et garantissent une bonne qualité de téléconsultation (référentiel ANS PRE.2.2).</p>

      <div className="space-y-3">
        {items.map(({ k, label, icon: Icon, hint }) => {
          const ok = data.techChecks[k];
          return (
            <div key={k} className="flex items-center gap-4 p-4 rounded-lg border border-slate-200 bg-white">
              <div className={`w-10 h-10 rounded-full grid place-items-center ${ok ? "bg-emerald-100" : "bg-slate-100"}`}>
                <Icon className={`w-5 h-5 ${ok ? "text-emerald-600" : "text-slate-500"}`} />
              </div>
              <div className="flex-1">
                <div className="text-[13.5px] font-semibold text-slate-900">{label}</div>
                <div className="text-[11.5px] text-slate-500">{ok ? "Test réussi" : hint}</div>
              </div>
              {ok ? (
                <CheckCircle2 className="w-5 h-5 text-emerald-500" />
              ) : (
                <Button variant="outline" size="sm" onClick={() => runCheck(k)} className="border-slate-300" data-testid={`pt-tech-${k}`}>Tester</Button>
              )}
            </div>
          );
        })}
      </div>

      {/* Mode dégradé documenté — HAS BPT 2021 */}
      <div className="mt-6 rounded-lg border border-orange-200 bg-orange-50 p-4">
        <div className="flex items-start gap-2.5">
          <RefreshCw className="w-4.5 h-4.5 text-orange-600 shrink-0 mt-0.5" />
          <div>
            <div className="text-[13px] font-semibold text-orange-900">Mode dégradé documenté</div>
            <p className="text-[12px] text-orange-800 mt-0.5">Si un test échoue ou en cas de coupure pendant l&apos;acte, la séance pourra être <strong>basculée en appel téléphonique sécurisé</strong> avec rappel automatique du praticien dans les 5 minutes (HAS BPT 2021).</p>
          </div>
        </div>
      </div>

      {allOk && (
        <div className="mt-4 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 flex items-center gap-2 text-[13px] text-emerald-800">
          <CheckCircle2 className="w-5 h-5" /> Tout est prêt. Vous pouvez passer à l&apos;étape suivante.
        </div>
      )}
    </div>
  );
}

function PT_Call({ doctorName, brand }) {
  return (
    <div className="max-w-3xl mx-auto text-center">
      <div className="mx-auto w-16 h-16 rounded-full grid place-items-center mb-4" style={{ backgroundColor: "var(--bp)" }}>
        <Video className="w-8 h-8 text-white" />
      </div>
      <div className="text-[11px] uppercase tracking-[0.14em] font-semibold text-slate-500 mb-2">Salle d&apos;attente virtuelle</div>
      <h2 className="text-[22px] font-bold text-slate-900">Vous êtes dans la salle d&apos;attente</h2>
      <p className="text-[13.5px] text-slate-500 mt-2 mb-6">Votre praticien <span className="font-semibold text-slate-800">{doctorName}</span> sera bientôt avec vous.<br />Merci de rester sur cette page.</p>

      <div className="rounded-2xl bg-slate-900 aspect-video relative overflow-hidden">
        <div className="absolute inset-0 grid place-items-center text-white">
          <div className="text-center">
            <div className="mx-auto w-14 h-14 rounded-full bg-white/10 grid place-items-center mb-3 animate-pulse">
              <Clock className="w-7 h-7" />
            </div>
            <div className="text-[15px] font-semibold">En attente du praticien…</div>
            <div className="mt-1 text-[12px] text-white/60">Plateforme {brand?.name || "MetaConnect"} · WebRTC chiffré E2E (LiveKit)</div>
          </div>
        </div>
        <div className="absolute bottom-3 right-3 w-24 h-16 rounded-lg bg-slate-700 border-2 border-white/20" />
        {/* Badges ANS / HDS */}
        <div className="absolute top-3 left-3 flex items-center gap-2 text-[10px] text-white/85">
          <span className="px-2 py-0.5 rounded bg-white/10 border border-white/15">REF TLS REA.1.2 · ANS</span>
          <span className="px-2 py-0.5 rounded bg-white/10 border border-white/15">Flux non stocké</span>
        </div>
      </div>

      <div className="mt-5 flex flex-wrap items-center justify-center gap-4 text-[11.5px] text-slate-500">
        <span className="flex items-center gap-1"><ShieldCheck className="w-3.5 h-3.5 text-emerald-500" /> HDS · agréé ANS</span>
        <span className="flex items-center gap-1"><Lock className="w-3.5 h-3.5 text-emerald-500" /> Chiffrement bout-en-bout (WebRTC)</span>
        <span className="flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" /> PGSSI-S · RGPD · INS v2.1</span>
      </div>
    </div>
  );
}

/* ============================ Antécédents (CIM-10 tag manager) ============================ */


/* ============================ PMS Send Modal ============================ */


/* ============================ Suivi ROSP ============================ */


/* ============================ Paramétrage ============================ */


/* ============================ PMS Connection Panel (Établissement > PMS connecté) ============================ */


